import { createClient } from '@supabase/supabase-js';
import { supabase, supabaseUrl, supabaseAnonKey } from "./supabase.js";
import { resetClient } from "./resetClient.js";
import { prepareImageForUpload } from "./imageUpload.js";
import { DEFAULT_ITEM_IMAGE } from "./defaultImage.js";
import { normalizePhone } from "./loyalty.js";
import { taxLabel } from "./inclusiveTax.js";
import { getOrCreateVisitorId, getStoredSource, clearStoredSource } from "./sourceTracking.js";
import { cleanHidden, TABLE_SERVICE } from "./homeLayout.js";
import { loadKitchenQueue, setQueueItemState, ticketToQueueItem } from "./kitchenQueue.js";
import { parseBookingRules, parseWaitlistQuotes, DEFAULT_BOOKING_RULES, DEFAULT_WAITLIST_QUOTES } from "./bookingRules.js";
import { todayIn, addDays, zonedToDate } from "./scheduleTime.js";
import { timezoneForAddress, isUtcZone, zoneFitsAddress } from "./usTimezones.js";

// Legacy "no image" rows stored a path that never resolves in production; treat
// them (and NULL) as "no image" so the view applies DEFAULT_ITEM_IMAGE instead.
const isMissingImage = (url) => !url || url.includes("default image.png");

let adjCounter = 0;

const safeParse = (str, fallback) => { if (str == null) return fallback; try { return JSON.parse(str); } catch { return fallback; } };

const generateAdjustmentId = function () {
  adjCounter += 1;
  return `adj_${Date.now()}-${adjCounter}`;
};

export const DEFAULT_TIP_PRESETS = [15, 18, 20];

// Tip presets come back from the localStorage cache as "15,18,20" (String of
// an array) and from the server as an array. Either way: 1-3 whole
// percentages from 1 to 100, else the default.
export function parseTipPresets(v) {
  const list = (Array.isArray(v) ? v : String(v ?? '').split(','))
    .map((n) => Math.round(Number(n)))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= 100)
    .slice(0, 3);
  return list.length ? list : [...DEFAULT_TIP_PRESETS];
}

// Home screen settings (homeLayout.js). The hidden list comes back from the
// cache as "a,b" and from the server as an array; table service is one of
// off / numbers / full.
export function parseHomeHidden(v) {
  return cleanHidden(Array.isArray(v) ? v : String(v ?? '').split(','));
}
export function parseTableService(v) {
  return TABLE_SERVICE.includes(v) ? v : 'off';
}

export const state = {
  userId: null,         // logged-in user's own auth ID
  businessId: null,     // business this session belongs to (owner's user ID)
  businessName: null,
  businessEmail: null,
  businessPhone: null,
  businessTimezone: null,  // IANA timezone string, e.g. "Asia/Manila"
  username: "Wowa",
  role: null,           // role name of the logged-in user
  permissions: null,    // Set<capabilityKey> the logged-in user's role grants
  subscriptionStatus: 'free', // 'free' | 'paid' - businesses.subscription_status, fail-safe defaults to 'free'
  subscriptionPortalUrl: null, // Lemon Squeezy hosted customer-portal link (businesses.ls_customer_portal_url), null until subscribed
  subscriptionRenewsAt: null, // businesses.ls_renews_at - next billing date while actively (really) subscribed, null otherwise
  trialEndsAt: null,    // businesses.trial_ends_at - temporary full-access grant expiration (signup trial or admin grant), null = none active
  trialGrantedBy: null, // businesses.trial_granted_by - null = automatic signup trial; an admin's email = manually granted via admin panel
  currentStaff: null,   // logged-in user's staff record
  currentCashier: null, // staff member actively on the POS (switchable)
  staff: [],
  roles: [],
  menuItems: [],
  deletedItems: [], // soft-deleted menu items: [{ id, name }] - kept so history (Reports) can show "(deleted)"
  menuCategories: [],
  scheduleStations: [],     // shift-schedule stations incl. archived: [{ id, name, color, sortOrder, archived }]
  scheduledShifts: [],      // the week on screen in the Schedule panel: [{ id, staffId, stationId, startsAt, endsAt, note, publishedAt, seriesId, seriesDate, isException }]
  scheduleSeries: [],       // repeat rules: [{ id, staffId, stationId, weekdays, startTime, endTime, startDate, untilDate, note, autoPublish }]
  staffAvailability: [],    // days someone isn't free all day: [{ staffId, weekday (0 = Mon), status 'unavailable'|'partial', startTime, endTime, note }]
  scheduleLeave: [],        // approved + pending leave around the week on screen: [{ id, staffId, type, startDate, endDate, status }]
  shiftRequests: [],        // swaps + open-shift pickups: open ones + the last 30 closed (see fetchShiftRequests)
  inventoryItems: [],       // stock ledger: [{ id, name, unit, quantity, lowStockThreshold, linkedMenuItemId, sku, category, unitCost, supplierId }]
  inventoryLinks: [],       // product -> stock consumption: [{ id, menuItemId, inventoryItemId, qty, variantLabel, variantOption }]
  inventoryAdjustments: [], // history log for the Inventory panel (latest page only)
  suppliers: [],            // vendor cards (STRICTLY optional): [{ id, name, category, contactName, phone, email, ... }]
  itemSuppliers: [],        // item <-> supplier price list: [{ id, inventoryItemId, supplierId, unitCost, preferred }]
  purchaseOrders: [],       // [{ id, poNumber, supplierId, supplierName, status, lines: [...], ... }]
  employees: [],
  cart: [],
  orderQueue: [],
  salesBasket: [],
  cashflowSales: [],
  voidedSales: [],
  tickets: [],
  reportsSales: [],
  expenses: [],
  discountCodes: [],
  loyaltyRewards: [],             // reward catalog: [{ id, itemId, pointsCost, active }]
  currentPromoCode: null,
  currentLoyaltyCustomer: null,   // { id, phone, name, pointsBalance, visitCount, saleDates } linked at checkout
  currentLoyaltyRedemption: null, // { adjId, points, value } when a points redemption is applied
  // Tables (2026-10-22): floor plan, open tabs, today's bookings, waitlist.
  diningTables: [],
  openTabs: [],
  reservations: [],
  waitlist: [],
  shifts: [],
  shiftEditRequests: [],  // pending correction requests (reviewer inbox)
  currentShift: null,
  settings: {
    adjustments: [],
    showRemovedAdjustments: true,
    printingEnabled: localStorage.getItem('pointbunny_printing_enabled') !== 'false',
    confirmPrint: localStorage.getItem('pointbunny_confirm_print') !== 'false',
    kdsYellowThreshold: parseInt(localStorage.getItem('pointbunny_kds_yellow') || '600'),
    kdsRedThreshold: parseInt(localStorage.getItem('pointbunny_kds_red') || '900'),
    kdsAutoCompleteThreshold: parseInt(localStorage.getItem('pointbunny_kds_auto') || '3600'),
    kdsWindowSize: safeParse(localStorage.getItem('pointbunny_kds_window_size'), { width: 1920, height: 1080 }),
    cfdWindowSize: safeParse(localStorage.getItem('pointbunny_cfd_window_size'), { width: 1920, height: 1080 }),
    orderTypeEnabled: localStorage.getItem('pointbunny_order_type_enabled') !== 'false',
    showCashierName: localStorage.getItem('pointbunny_show_cashier') !== 'false',
    showInvoiceNumber: localStorage.getItem('pointbunny_show_invoice_number') !== 'false',
    showTicketNumber: localStorage.getItem('pointbunny_show_ticket_number') !== 'false',
    showBusinessPhone: localStorage.getItem('pointbunny_show_business_phone') !== 'false',
    receiptFooter: localStorage.getItem('pointbunny_receipt_footer') || '',
    receiptQrUrl: localStorage.getItem('pointbunny_receipt_qr_url') || '',
    // taxMode is the source of truth ('none'|'exclusive'|'inclusive'); taxInclusive
    // is kept in sync (== taxMode === 'inclusive') purely so the many existing
    // consumers that already check the boolean don't need to change. Default is
    // 'none' - safest assumption for a business that hasn't configured tax at all.
    taxMode: localStorage.getItem('pointbunny_tax_mode') || 'none',
    taxInclusive: localStorage.getItem('pointbunny_tax_inclusive') === 'true',
    taxInclusiveRate: parseFloat(localStorage.getItem('pointbunny_tax_inclusive_rate')) || 12,
    taxInclusiveLabel: localStorage.getItem('pointbunny_tax_label') || null,
    taxExemptLabel: localStorage.getItem('pointbunny_tax_exempt_label') || null,
    cfdAdUrl: localStorage.getItem('pointbunny_cfd_ad') || null,
    cfdQrUrl: localStorage.getItem('pointbunny_cfd_qr') || null,
    pickupAdUrl: localStorage.getItem('pointbunny_pickup_ad') || null,
    tipsEnabled: localStorage.getItem('pointbunny_tips_enabled') === 'true',
    tipPresets: parseTipPresets(localStorage.getItem('pointbunny_tip_presets')),
    tipQrUrl: localStorage.getItem('pointbunny_tip_qr') || null,
    tipPoolRoleIds: (() => { const v = localStorage.getItem('pointbunny_tip_pool_roles'); return v == null ? null : v === '' ? [] : v.split(','); })(),
    hideStatsFromStaff: localStorage.getItem('pointbunny_hide_stats_staff') === 'true',
    gettingStartedDismissed: localStorage.getItem('pointbunny_checklist_dismissed') === 'true',
    gettingStartedCompleted: localStorage.getItem('pointbunny_checklist_completed') === 'true',
    receiptSetupSeen: localStorage.getItem('pointbunny_receipt_setup_seen') === 'true',
    taxSetupSeen: localStorage.getItem('pointbunny_tax_setup_seen') === 'true',
    teamSetupSeen: localStorage.getItem('pointbunny_team_setup_seen') === 'true',
    inventorySetupSeen: localStorage.getItem('pointbunny_inventory_setup_seen') === 'true',
    loyaltyEnabled: localStorage.getItem('pointbunny_loyalty_enabled') === 'true',
    loyaltyEarnRate: parseFloat(localStorage.getItem('pointbunny_loyalty_earn_rate')) || 0.1,
    loyaltyEarnAmount: parseFloat(localStorage.getItem('pointbunny_loyalty_earn_amount')) || 1,
    loyaltyRedeemPoints: parseInt(localStorage.getItem('pointbunny_loyalty_redeem_points')) || 100,
    loyaltyRedeemValue: parseFloat(localStorage.getItem('pointbunny_loyalty_redeem_value')) || 5,
    inventoryEnabled: localStorage.getItem('pointbunny_inventory_enabled') !== 'false',
    placeType: localStorage.getItem('pointbunny_place_type') || null,
    homeHidden: parseHomeHidden(localStorage.getItem('pointbunny_home_hidden')),
    tableService: parseTableService(localStorage.getItem('pointbunny_table_service')),
    waitlistEnabled: localStorage.getItem('pointbunny_waitlist_enabled') === 'true',
    reservationsEnabled: localStorage.getItem('pointbunny_reservations_enabled') === 'true',
    bookingRules: parseBookingRules(localStorage.getItem('pointbunny_booking_rules')),
    waitlistQuotes: parseWaitlistQuotes(localStorage.getItem('pointbunny_waitlist_quotes')),
  },
  currentReceiptAdjustments: [],
  needsOnboarding: false,
  businessType:            null,
  businessIndustry:        null,
  businessAddressStreet:   null,
  businessAddressCity:     null,
  businessAddressProvince: null,
  businessAddressZip:      null,
  businessAddressCountry:  null,
};

// ── Role permissions ──────────────────────────────────────────────────────────
// Capability keys are the single vocabulary for access control. Each role stores
// the keys it grants in roles.permissions (JSONB, shape { key: true }). The maps
// below are the DEFAULTS used to seed the four built-in roles and to fall back on
// when a role's stored permissions are empty or in the old coarse shape. Gating is
// driven off hasPermission(key); UI hiding is cosmetic, RLS is the real guard.
// `manage_roles` is itself a capability (owner-assignable) - see role editor.
export const ALL_PERMISSIONS = [
  'use_timeclock',           // clock in / out (time clock)
  'take_orders',             // create a new order / checkout
  'view_catalogue',          // browse the menu
  'apply_receipt_adjustment', // add/override one-off adjustments on a receipt at checkout
  'use_loyalty',             // enroll customers + redeem points at checkout
  'manage_menu',        // add/edit menu items & categories
  'manage_inventory',   // stock levels, restocking, product-inventory links
  'manage_adjustments', // fees / promos
  'manage_discounts',   // discount codes
  'manage_loyalty',     // loyalty program settings + customer list
  'void_sale',          // void / refund a sale
  'manage_cashflow',    // cashflow & expenses
  'view_reports',       // reports / analytics
  'view_activity_log',  // see the home page activity feed
  'manage_pay',         // edit shifts / pay rates
  'manage_schedule',    // build + publish the shift schedule
  'manage_staff',       // staff members
  'reset_staff_mfa',    // remove a staff member's 2FA (lost authenticator + codes)
  'manage_roles',       // create/edit roles & their access
  'manage_business',    // business settings
];

// Display labels for the role editor checklist.
export const PERMISSION_LABELS = {
  use_timeclock:      'Clock in & out',
  take_orders:        'Take orders & checkout',
  view_catalogue:     'Browse menu',
  apply_receipt_adjustment: 'Apply one-off receipt adjustments',
  use_loyalty:        'Use loyalty at checkout',
  manage_menu:        'Manage menu & categories',
  manage_inventory:   'Manage inventory & stock',
  manage_adjustments: 'Manage fees & adjustments',
  manage_discounts:   'Manage discount codes',
  manage_loyalty:     'Manage loyalty program',
  void_sale:          'Void & refund sales',
  manage_cashflow:    'View cashflow & expenses',
  view_reports:       'View reports',
  view_activity_log:  'View activity log',
  manage_pay:         'Manage shifts & pay rates',
  manage_schedule:    'Build & publish the schedule',
  manage_staff:       'Manage staff',
  reset_staff_mfa:    'Remove staff 2FA',
  manage_roles:       'Manage roles & access',
  manage_business:    'Manage business settings',
};

// Short tooltip descriptions for the role editor - what each capability covers.
export const PERMISSION_DESCRIPTIONS = {
  use_timeclock:      'Clock in and out for shifts on the time clock.',
  take_orders:        'Create new orders and take payment at checkout.',
  view_catalogue:     'Browse the menu while taking orders.',
  apply_receipt_adjustment: 'Add or remove a one-off fee or discount on a single receipt at checkout.',
  use_loyalty:        'Enroll customers in the loyalty program and redeem their points at checkout.',
  manage_menu:        'Add, edit, and remove menu items and categories.',
  manage_inventory:   'Track stock levels, restock items, and link products to inventory.',
  manage_adjustments: 'Set up taxes, service fees, and auto-applied receipt adjustments.',
  manage_discounts:   'Create, pause, edit, and delete promo / discount codes.',
  manage_loyalty:     'Configure the loyalty program and view the customer list.',
  void_sale:          'Void or refund completed sales.',
  manage_cashflow:    'Open the activity panel and record expenses.',
  view_reports:       'View the reports and analytics dashboard.',
  view_activity_log:  'See the home page activity feed of recent sales, shifts, and stock events.',
  manage_pay:         'Edit staff shifts and hourly pay rates.',
  manage_schedule:    'Plan the week, add and edit scheduled shifts, manage stations, and publish the schedule to staff.',
  manage_staff:       'Invite, edit, and remove staff members.',
  reset_staff_mfa:    "Turn off a staff member's two-factor authentication when they've lost their authenticator and recovery codes. Done from the Staff panel, so the role also needs Manage staff.",
  manage_roles:       'Create roles and choose what each one can access.',
  manage_business:    'Change business details and settings.',
};

// Capabilities grouped by function for the role editor. Each group gets a header
// with a "check all" toggle. Order here is the order shown in the editor.
export const PERMISSION_GROUPS = [
  { label: 'Time Clock',        keys: ['use_timeclock'] },
  { label: 'Sales & Checkout',  keys: ['take_orders', 'view_catalogue', 'apply_receipt_adjustment', 'use_loyalty', 'void_sale'] },
  { label: 'Menu & Promotions', keys: ['manage_menu', 'manage_inventory', 'manage_adjustments', 'manage_discounts', 'manage_loyalty'] },
  { label: 'Reports & Money',   keys: ['view_reports', 'manage_cashflow', 'view_activity_log'] },
  { label: 'Team & Business',   keys: ['manage_pay', 'manage_schedule', 'manage_staff', 'reset_staff_mfa', 'manage_roles', 'manage_business'] },
];

export const ROLE_PERMISSIONS = {
  Staff:   ['use_timeclock'],
  Cashier: ['use_timeclock', 'take_orders', 'view_catalogue', 'apply_receipt_adjustment', 'use_loyalty'],
  Manager: [
    'use_timeclock', 'take_orders', 'view_catalogue', 'apply_receipt_adjustment', 'use_loyalty',
    'manage_menu', 'manage_inventory', 'manage_adjustments', 'manage_discounts', 'manage_loyalty', 'void_sale',
    'manage_cashflow', 'view_reports', 'manage_schedule',
  ],
  Admin: [...ALL_PERMISSIONS],
};

// The Admin role is locked: it always grants every capability and can't be edited
// or deleted, guaranteeing a full-access role always exists (lockout safety).
export const LOCKED_ROLE = 'Admin';

// Converts a list of capability keys to the stored JSONB shape { key: true }.
export const capsToPermissions = (caps) =>
  Object.fromEntries((caps ?? []).map((k) => [k, true]));

// Build the granted-capability Set for a role from its stored permissions JSONB.
// New shape: { capabilityKey: true }. If no known capability keys are present
// (empty, or the legacy { menu, cashflow, ... } shape), fall back to the built-in
// defaults by role name so existing/un-migrated roles keep working.
export const capabilitySetFromRole = function (roleName, permissionsJson) {
  const obj = permissionsJson && typeof permissionsJson === 'object' ? permissionsJson : {};
  if (roleName === LOCKED_ROLE) return new Set(ALL_PERMISSIONS);
  const stored = ALL_PERMISSIONS.filter((k) => obj[k] === true);
  if (stored.length > 0) return new Set(stored);
  return new Set(ROLE_PERMISSIONS[roleName] ?? []);
};

// True if the logged-in user can do `key`. The business owner (their auth id == the
// business id) is always a super-admin. Everyone else is checked against the
// capability set their role granted, loaded into state.permissions at login.
export const hasPermission = function (key) {
  if (state.userId && state.userId === state.businessId) return true;
  return state.permissions instanceof Set ? state.permissions.has(key) : false;
};

// ── Free vs. paid plan gating ───────────────────────────────────────────────────

// Free tier: 4 locked feature panels. Staff are unlimited on every plan since
// 2026-10-15 (Free was capped at 1, the owner, before that - Kelly's call,
// growth over upsell and so free shift scheduling is useful). See CLAUDE.md
// "Free vs. Paid Plan Gating" for the full pattern - any new premium feature
// should add itself to PLAN_GATED_FEATURES and check isFeatureLocked().

export const PLAN_GATED_FEATURES = {
  inventory: {
    title: 'Inventory',
    blurb: 'Track stock levels, get low-stock alerts, and manage suppliers & purchase orders.',
    bullets: ['Real-time stock tracking', 'Low & out-of-stock alerts', 'Suppliers & purchase orders', 'Stock counts'],
  },
  timesheets: {
    title: 'Timesheets',
    blurb: 'Review staff shifts, hours, and time-off requests in one place.',
    bullets: ['Shift & hours history', 'Time-clock device management', 'Shift correction inbox', 'Leave requests'],
  },
  loyalty: {
    title: 'Loyalty',
    blurb: 'Reward repeat customers with a points program and free-item rewards.',
    bullets: ['Points on every sale', 'Free-item reward catalog', 'Customer purchase streaks', 'Member lookup at checkout'],
  },
  reports: {
    title: 'Reports',
    blurb: 'Full sales, traffic, and kitchen analytics - beyond today\'s at-a-glance numbers.',
    bullets: ['Multi-day & custom date ranges', 'Compare period vs. period', 'Top items, categories & staff', 'Peak hours & serving-time trends'],
  },
};

// True while a temporary full-access grant is unexpired - either the automatic
// signup trial or an admin-granted extension (both use trial_ends_at; see the
// "no-card trial" note on _initBusiness and migration-2026-09-03-trial-system.sql).
export const isTrialActive = function () {
  return Boolean(state.trialEndsAt) && new Date(state.trialEndsAt) > new Date();
};

export const isPaidPlan = function () {
  return state.subscriptionStatus === 'paid' || isTrialActive();
};

export const isFeatureLocked = function (feature) {
  return !isPaidPlan();
};

let _planChangeChannel = null;

// Live-updates state.subscriptionStatus when the admin panel overrides a business's
// plan, so gating flips without the owner needing to log out/in (closes the
// long-standing "Subscription Status - Realtime Sync Needed" todo). onChange is
// called with `becamePaid: true` exactly once, the moment subscription_status
// transitions INTO 'paid' from anything else (a real upgrade) - so the caller can
// fire a one-time celebration rather than reacting to every row update.
export const subscribeToPlanChanges = function (businessId, onChange) {
  if (_planChangeChannel) return;
  _planChangeChannel = supabase
    .channel('business-subscription-' + businessId)
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'businesses',
      filter: `id=eq.${businessId}`,
    }, ({ new: updated }) => {
      const wasPaid = state.subscriptionStatus === 'paid';
      state.subscriptionStatus    = updated.subscription_status === 'paid' ? 'paid' : 'free';
      state.subscriptionPortalUrl = updated.ls_customer_portal_url ?? null;
      state.subscriptionRenewsAt  = updated.ls_renews_at     ?? null;
      state.trialEndsAt           = updated.trial_ends_at    ?? null;
      state.trialGrantedBy        = updated.trial_granted_by ?? null;
      onChange?.({ becamePaid: !wasPaid && state.subscriptionStatus === 'paid' });
    })
    .subscribe();
};

export const unsubscribeFromPlanChanges = function () {
  if (!_planChangeChannel) return;
  supabase.removeChannel(_planChangeChannel);
  _planChangeChannel = null;
};

// ── Concurrent session tracking (no cap on any plan) ───────────────────────────
// 2026-10-15 (Kelly): every plan, Free included, gets unlimited concurrent
// sessions - growth over upsell for now (migration-2026-10-15-free-unlimited-
// sessions.sql). Before that, Free was capped at 1 and Pro/trial was unlimited
// (2026-09-19). A "session" is one browser tab holding a real auth session -
// the main POS app and the time-clock kiosk; KDS/CFD/Pickup popups never
// authenticate. Sessions are still claimed + heartbeated so active_sessions
// stays a live "who's online" list, and the block path below is kept in case a
// cap ever comes back. claim_session() is the source of truth and today never
// returns { ok:false }.

// One id per browser TAB (sessionStorage, not localStorage) so two tabs on the
// same device count as two sessions, matching "concurrent session" intent. Reused
// across a reload of the same tab so re-claiming updates the existing row instead
// of counting twice.
const _sessionDeviceId = function () {
  let id = sessionStorage.getItem('pointbunny_session_device_id');
  if (!id) {
    id = typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem('pointbunny_session_device_id', id);
  }
  return id;
};

let _heartbeatHandle = null;

// Cached opportunistically (after claim + every heartbeat) so a pagehide
// listener can fire a release without awaiting an async getSession() call
// during unload, when a promise isn't reliably given time to resolve.
let _cachedAccessToken = null;
const _refreshCachedToken = async function () {
  try {
    const { data } = await supabase.auth.getSession();
    _cachedAccessToken = data?.session?.access_token ?? null;
  } catch (_) { /* best-effort */ }
};

// Builds the blocked-sign-in message from the RPC's { blocked_by, cap }.
// Unreachable today (no plan has a cap) - defensive fallback only.
const _sessionLimitMessage = function (data) {
  const who = data?.blocked_by || 'Someone';
  const cap = data?.cap ?? 1;
  return `You've reached the limit of ${cap} signed-in session${cap === 1 ? '' : 's'} (${who} is one of them). Sign out on another device and try again.`;
};

// Claims this tab a session slot for the business, or throws a
// SESSION_LIMIT_REACHED error naming who already holds the seat. `source` is
// 'pos' or 'timeclock'. Fails OPEN on any unexpected error (network hiccup, the
// RPC itself erroring) so a bug here can never lock out a real sign-in - only an
// explicit { ok:false } from the RPC blocks.
export const claimSession = async function (businessId, displayName, source) {
  try {
    const { data, error } = await supabase.rpc('claim_session', {
      p_business_id: businessId,
      p_device_id: _sessionDeviceId(),
      p_display_name: displayName,
      p_source: source,
    });
    if (error) { console.warn('claimSession RPC error (allowing sign-in):', error); return; }
    if (data && data.ok === false) {
      const err = new Error(_sessionLimitMessage(data));
      err.code = 'SESSION_LIMIT_REACHED';
      throw err;
    }
    _refreshCachedToken();
  } catch (err) {
    if (err.code === 'SESSION_LIMIT_REACHED') throw err;
    console.warn('claimSession failed (allowing sign-in):', err);
  }
};

// Keeps this tab's session row alive so it isn't swept as stale (3-minute TTL
// server-side). Call once right after a successful claimSession().
export const startSessionHeartbeat = function (businessId) {
  stopSessionHeartbeat();
  _heartbeatHandle = setInterval(async () => {
    // supabase.rpc(...) returns a PostgrestFilterBuilder, not a plain Promise -
    // it has no .catch() method, so chaining .catch() directly threw a
    // TypeError on every tick (silently killing the heartbeat entirely - found
    // 2026-09-19 when sessions kept expiring exactly 3 minutes after claiming
    // regardless of activity, since last_seen_at was never actually refreshed).
    try {
      await supabase.rpc('touch_session', { p_business_id: businessId, p_device_id: _sessionDeviceId() });
    } catch (_) { /* best-effort */ }
    _refreshCachedToken();
  }, 90 * 1000);
};

export const stopSessionHeartbeat = function () {
  if (_heartbeatHandle) { clearInterval(_heartbeatHandle); _heartbeatHandle = null; }
};

// Frees this tab's session slot immediately on sign-out, rather than waiting for
// the 3-minute stale sweep, so the seat is available again right away.
export const releaseSession = async function (businessId) {
  stopSessionHeartbeat();
  _cachedAccessToken = null; // the auth token is about to be revoked by sign-out
  try {
    await supabase.rpc('release_session', { p_business_id: businessId, p_device_id: _sessionDeviceId() });
  } catch (_) { /* best-effort */ }
};

// Best-effort release fired when the tab is actually closing (pagehide) rather
// than an explicit Sign Out - covers the common "just closed the browser"
// case, which otherwise leaves the slot occupied (and can block a teammate
// elsewhere) until the 3-minute stale sweep catches it on someone's next
// sign-in attempt. Can't await a promise reliably during unload, so this goes
// around the supabase-js client (which doesn't expose fetch's `keepalive`
// option) with a raw fetch using a token cached ahead of time by claimSession/
// the heartbeat. Not guaranteed - a crash or power loss still won't fire it -
// just meaningfully better than relying on the TTL alone. Call once, right
// after startSessionHeartbeat().
export const armSessionReleaseOnUnload = function (businessId) {
  window.addEventListener('pagehide', () => {
    if (!_cachedAccessToken) return;
    try {
      fetch(`${supabaseUrl}/rest/v1/rpc/release_session`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${_cachedAccessToken}`,
        },
        body: JSON.stringify({ p_business_id: businessId, p_device_id: _sessionDeviceId() }),
      });
    } catch (_) { /* best-effort */ }
  });
};

// ── Acquisition-channel attribution ────────────────────────────────────────────

// Best-effort funnel-event insert for the source-tracking feature. Must never surface
// errors or block signup - tracking can never break the app.
// A missing sourceId/sessionId is a no-op (registration_events.source_id is NOT NULL,
// and there's nothing useful to log for an organic signup with no tracked source).
export const logRegistrationEvent = async function (eventType, { sourceId, sessionId, businessId = null } = {}) {
  if (!sourceId || !sessionId) return;
  try {
    await supabase.from('registration_events').insert({
      source_id: sourceId,
      event_type: eventType,
      session_id: sessionId,
      business_id: businessId,
    });
  } catch (_) {
    // best-effort only
  }
};

// ── Business context (runs on every login) ────────────────────────────────────

const _initBusiness = async function (user) {
  const displayName  = user.user_metadata?.display_name ?? user.user_metadata?.full_name ?? user.user_metadata?.name ?? '';
  const firstName    = user.user_metadata?.first_name || displayName.split(' ')[0] || '';
  const lastName     = user.user_metadata?.last_name  || displayName.split(' ').slice(1).join(' ') || '';
  const businessName = user.user_metadata?.business_name
    || `${firstName} ${lastName}`.trim()
    || user.email
    || 'My Business';
  const phone        = user.user_metadata?.phone ?? null;
  // Acquisition source captured at sign-up (Javascript/sourceTracking.js), carried through
  // via auth user_metadata so it survives even if email confirmation happens on another
  // device (localStorage alone wouldn't). Google sign-up can't carry custom metadata, so it
  // falls back to this browser's stored source - safe because _initBusiness only runs for a
  // brand-new owner. Null for an organic signup with no tracking link.
  const sourceId      = user.user_metadata?.source ?? getStoredSource()?.source ?? null;

  // No-card-required trial: every brand-new business gets 7 days of full Pro
  // access with zero Lemon Squeezy involvement. trial_granted_by stays NULL here
  // (vs. an admin's email when granted manually from the admin panel) so the two
  // sources stay distinguishable later. See CLAUDE.md "Free vs. Paid Plan Gating".
  const trialEndsAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

  let { error: bizError } = await supabase
    .from('businesses')
    .upsert({ id: user.id, name: businessName, email: user.email, phone: null, source_id: sourceId, subscription_status: 'free', trial_ends_at: trialEndsAt }, { ignoreDuplicates: true });
  if (bizError) {
    // Retry without trial_ends_at - a schema hiccup on that one column must never
    // block brand-new signups from creating their business at all (see the login
    // incident on loadBusinessContext above; same defensive posture here). The
    // trial can be granted later once the column resolves, or manually from admin.
    console.warn('businesses upsert with trial_ends_at failed, retrying without it:', bizError.message);
    ({ error: bizError } = await supabase
      .from('businesses')
      .upsert({ id: user.id, name: businessName, email: user.email, phone: null, source_id: sourceId, subscription_status: 'free' }, { ignoreDuplicates: true }));
  }
  if (bizError) throw new Error(`businesses upsert failed: ${bizError.message}`);

  const { data: existingRole } = await supabase
    .from('roles')
    .select('id')
    .eq('business_id', user.id)
    .eq('name', 'Admin')
    .maybeSingle();

  let adminRoleId = existingRole?.id;
  if (!adminRoleId) {
    const { data: insertedRoles, error: roleError } = await supabase
      .from('roles')
      .insert([
        { business_id: user.id, name: 'Admin',   permissions: capsToPermissions(ROLE_PERMISSIONS.Admin)   },
        { business_id: user.id, name: 'Manager', permissions: capsToPermissions(ROLE_PERMISSIONS.Manager) },
        { business_id: user.id, name: 'Cashier', permissions: capsToPermissions(ROLE_PERMISSIONS.Cashier) },
        { business_id: user.id, name: 'Staff',   permissions: capsToPermissions(ROLE_PERMISSIONS.Staff)   },
      ])
      .select('id, name');
    if (roleError) throw new Error(`roles insert failed: ${roleError.message}`);
    adminRoleId = insertedRoles.find(r => r.name === 'Admin')?.id;
  }

  const { data: existingStaff } = await supabase
    .from('staff')
    .select('id, first_name, last_name, email')
    .eq('user_id', user.id)
    .maybeSingle();

  let staffRow = existingStaff;
  if (!staffRow) {
    const { data: newStaff, error: staffError } = await supabase
      .from('staff')
      .insert({
        business_id: user.id,
        user_id: user.id,
        role_id: adminRoleId,
        first_name: firstName,
        last_name: lastName,
        email: user.email,
        joined_at: new Date().toISOString(),
      })
      .select('id, first_name, last_name, email')
      .single();
    if (staffError) throw new Error(`staff insert failed: ${staffError.message}`);
    staffRow = newStaff;
  }

  state.currentStaff = {
    id: staffRow.id,
    firstName: staffRow.first_name,
    lastName: staffRow.last_name,
    email: staffRow.email,
    role: 'Admin',
  };

  if (sourceId) {
    await logRegistrationEvent('registered', { sourceId, sessionId: getOrCreateVisitorId(), businessId: user.id });
  }
  clearStoredSource();
};

export const loadBusinessContext = async function (user, { isInviteAcceptance = false } = {}) {
  state.userId = user.id;

  // A person's account can be tied to more than one staff row: at most one ACTIVE row
  // (their current business) plus any number of soft-deleted historical rows from
  // businesses they were removed from. We pick the active one. .maybeSingle() can't be
  // used here - it errors when an old deactivated row coexists with a new active row
  // (which happens after someone is removed from one business and joins another).
  const { data: ownRows } = await supabase
    .from('staff')
    .select('id, business_id, first_name, last_name, email, pin, is_active, role_id, roles(name, permissions)')
    .eq('user_id', user.id);

  let staffRow = (ownRows ?? []).find((r) => r.is_active) ?? null;
  // True when the account has only deactivated rows: removed from every business they
  // joined. This is a genuine lockout UNLESS a fresh pending invite is waiting (handled
  // in the else branch below, which takes precedence so a removed staffer can re-join).
  const removedFromAll = !staffRow && (ownRows ?? []).length > 0;

  // Only claim a pending invite row when the user explicitly came through the invite link.
  // Skipping this for normal sign-in/sign-up prevents a person who owns their own business
  // from being silently enrolled as staff at another business that invited their email.
  if (!staffRow && isInviteAcceptance) {
    // A person can hold pending invites from more than one business at once. Per the
    // single-membership rule they join the first business that invited them; any other
    // pending invites for their email are discarded so they cannot pile up (those
    // businesses can only re-invite after this person is removed from their team).
    // Note: we fetch all matches and pick one rather than using .maybeSingle(), which
    // errors out when more than one pending row matches and leaves the user unclaimed.
    const { data: pendingRows } = await supabase
      .from('staff')
      .select('id, business_id, first_name, last_name, email, pin, role_id, roles(name)')
      .eq('email', user.email)
      .is('user_id', null)
      .eq('is_active', true)
      .order('invited_at', { ascending: true });

    if (pendingRows && pendingRows.length) {
      const claim = pendingRows[0];
      await supabase
        .from('staff')
        .update({ user_id: user.id, joined_at: new Date().toISOString() })
        .eq('id', claim.id);

      const duplicates = pendingRows.slice(1).map((r) => r.id);
      if (duplicates.length) {
        await supabase.from('staff').delete().in('id', duplicates);
      }
      staffRow = claim;
    }
  }

  if (staffRow) {
    state.businessId   = staffRow.business_id;
    // Fail SAFE, not loud: if the role join can't be read (e.g. an RLS hiccup on the
    // roles table) we must not silently escalate a staff member to Admin. The owner
    // (their auth id == the business id) is the only account that defaults to Admin;
    // any other unreadable role drops to least-privilege so access is never granted
    // by accident. A null role name here is also a signal the roles RLS is misbehaving.
    const _isOwnerAccount = user.id === staffRow.business_id;
    if (!staffRow.roles?.name) {
      console.warn('[pointbunny] staff role name could not be read; defaulting role. Check roles RLS / get_my_business_id().');
    }
    state.role         = staffRow.roles?.name ?? (_isOwnerAccount ? 'Admin' : 'Cashier');
    state.permissions  = _isOwnerAccount
      ? new Set(ALL_PERMISSIONS)
      : capabilitySetFromRole(state.role, staffRow.roles?.permissions);
    state.currentStaff = {
      id:        staffRow.id,
      firstName: staffRow.first_name,
      lastName:  staffRow.last_name,
      email:     staffRow.email,
      role:      state.role,
      roleId:    staffRow.role_id ?? null,
      hasPin:    !!staffRow.pin,
    };
  } else {
    // An existing account (signed up on their own, or joined a business then left) can't
    // be re-invited by email - Supabase won't send an invite to an email that already has
    // an account. So on a normal login, if a business has a pending invite for this email,
    // offer it behind a consent step instead of dead-ending. We never silently enroll:
    // the user must accept. Owners creating their own business (role 'owner') skip this.
    if (!isInviteAcceptance && user.user_metadata?.role !== 'owner') {
      const { data: invites } = await supabase
        .from('staff')
        .select('id, business_id, first_name, last_name, roles(name)')
        .eq('email', user.email)
        .is('user_id', null)
        .eq('is_active', true)
        .order('invited_at', { ascending: true });

      if (invites && invites.length) {
        // Business name is best-effort: RLS may hide the businesses row from a non-member,
        // in which case the consent prompt falls back to a generic label.
        const ids = [...new Set(invites.map((i) => i.business_id))];
        const { data: bizRows } = await supabase
          .from('businesses')
          .select('id, name')
          .in('id', ids);
        const nameById = Object.fromEntries((bizRows ?? []).map((b) => [b.id, b.name]));
        state.pendingInvites = invites.map((i) => ({
          staffId:      i.id,
          businessId:   i.business_id,
          businessName: nameById[i.business_id] ?? null,
          firstName:    i.first_name,
          role:         i.roles?.name ?? 'Staff',
        }));
        const err = new Error('PENDING_INVITE_CONSENT');
        err.code = 'PENDING_INVITE_CONSENT';
        throw err;
      }
    }

    // No active membership and no pending invite to accept. If the account previously
    // belonged to a business and was removed/deactivated, that is a genuine lockout - do
    // not fall through to _initBusiness (which would spawn a ghost business for them).
    if (removedFromAll) {
      const err = new Error('Your account has been deactivated. Please contact your business owner.');
      err.code = 'STAFF_DEACTIVATED';
      throw err;
    }

    if (user.user_metadata?.role === 'staff') {
      throw new Error('Staff account setup incomplete. Please contact your manager or try accepting your invite link again.');
    }
    // Admin panel accounts must never spawn a POS business via _initBusiness
    const { data: adminRow } = await supabase
      .from('admins')
      .select('email')
      .eq('email', user.email)
      .maybeSingle();
    if (adminRow) {
      const err = new Error('This is an admin account. Please sign in at the admin panel instead.');
      err.code = 'ADMIN_ACCOUNT';
      throw err;
    }
    state.businessId   = user.id;
    state.role         = 'Admin';
    state.permissions  = new Set(ALL_PERMISSIONS);
    state.currentStaff = null;
    await _initBusiness(user);
  }

  state.currentCashier = state.currentStaff;

  const { data: bizData, error: bizDataError } = await supabase
    .from('businesses')
    .select('name, timezone, phone, address_street, address_city, address_province, address_zip, address_country, business_type, business_industry, settings, subscription_status, ls_customer_portal_url, ls_renews_at')
    .eq('id', state.businessId)
    .single();
  // A failed/unexpected-empty query here must never fall through silently - the
  // caller previously ignored `error`, so a transient failure left every business
  // field at its null default, which made owner logins misread as "onboarding
  // never completed" and show the setup flow to real, fully-onboarded accounts
  // (incident 2026-09-03). Surface it instead of guessing.
  if (bizDataError) throw new Error(`Failed to load business data: ${bizDataError.message}`);
  if (bizData) {
    // `?? {}`: a brand-new business has settings = NULL; passing the empty
    // object makes applyBusinessSettings reset every key to its default
    // instead of leaving this device's cache (possibly another account's
    // values) in place.
    applyBusinessSettings(bizData.settings ?? {});
    state.businessName            = bizData.name              ?? null;
    state.businessTimezone        = bizData.timezone          ?? null;
    state.businessPhone           = bizData.phone             ?? null;
    state.businessAddressStreet   = bizData.address_street    ?? null;
    state.businessAddressCity     = bizData.address_city      ?? null;
    state.businessAddressProvince = bizData.address_province  ?? null;
    state.businessAddressZip      = bizData.address_zip       ?? null;
    state.businessAddressCountry  = bizData.address_country   ?? null;
    state.businessType            = bizData.business_type     ?? null;
    state.businessIndustry        = bizData.business_industry ?? null;
    // Fail safe: unknown/NULL subscription_status (pre-Stripe businesses, or an
    // unreadable row) always reads as 'free', never silently unlocks paid features.
    state.subscriptionStatus      = bizData.subscription_status === 'paid' ? 'paid' : 'free';
    state.subscriptionPortalUrl   = bizData.ls_customer_portal_url ?? null;
    state.subscriptionRenewsAt    = bizData.ls_renews_at ?? null;
  }

  // Trial fields fetched as a SEPARATE, best-effort query so a schema hiccup on
  // these two columns specifically can never again take the whole login down
  // with it (see the incident note above) - worst case, the trial silently
  // doesn't show for a login or two instead of blocking access entirely.
  state.trialEndsAt    = null;
  state.trialGrantedBy = null;
  try {
    const { data: trialData, error: trialError } = await supabase
      .from('businesses')
      .select('trial_ends_at, trial_granted_by')
      .eq('id', state.businessId)
      .single();
    if (trialError) throw trialError;
    state.trialEndsAt    = trialData?.trial_ends_at    ?? null;
    state.trialGrantedBy = trialData?.trial_granted_by ?? null;
  } catch (trialFetchError) {
    console.warn('Trial fields unavailable this login, defaulting to none:', trialFetchError);
  }

  // Recompute from scratch each login - never inherit a stale `true` from a prior
  // in-page session (e.g. an owner/admin login whose error path signed out without a
  // page reload), which would otherwise flash onboarding at the next staff login.
  state.needsOnboarding = false;
  if (user.user_metadata?.role === 'owner' || (!user.user_metadata?.role && state.userId === state.businessId)) {
    const onboardingComplete =
      state.businessName         &&
      state.businessType         &&
      state.businessIndustry     &&
      state.businessPhone        &&
      state.businessAddressStreet  &&
      state.businessAddressCity    &&
      state.businessAddressProvince &&
      state.businessAddressZip     &&
      state.businessAddressCountry;
    if (!onboardingComplete) state.needsOnboarding = true;
  }

  // Set the timezone if the owner hasn't (or if a device in UTC set it):
  // from the US address first, else this device. Only the owner writes it -
  // staff logins leave it untouched. See usTimezones.js.
  _fillBusinessTimezone();
};

// The zone every "today" and clock time uses. A saved UTC (from a UTC device)
// gives way to the address's zone, even for staff, who can't fix the saved one.
// A saved zone that doesn't fit the US address (more than an hour off, e.g. Asia/Manila saved
// from the owner's laptop for a Los Angeles cafe) also gives way - see zoneFitsAddress.
export const businessTz = function () {
  const saved = state.businessTimezone;
  const fromAddress = timezoneForAddress(state.businessAddressCountry, state.businessAddressProvince);
  if (saved && !isUtcZone(saved) && zoneFitsAddress(saved, fromAddress)) return saved;
  return fromAddress || saved || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const _fillBusinessTimezone = function () {
  if (state.userId !== state.businessId) return;
  const current = state.businessTimezone;
  const fromAddress = timezoneForAddress(state.businessAddressCountry, state.businessAddressProvince);
  if (current && !isUtcZone(current) && zoneFitsAddress(current, fromAddress)) return;
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tz = fromAddress || (detected && !isUtcZone(detected) ? detected : null);
  if (!tz || tz === current) return;
  state.businessTimezone = tz;
  supabase
    .from('businesses')
    .update({ timezone: tz })
    .eq('id', state.businessId)
    .then(() => {}); // fire-and-forget, non-blocking
};

export const loadBusinessProfile = async function () {
  const { data } = await supabase
    .from('businesses')
    .select('name, email, phone, timezone, address_street, address_city, address_province, address_zip')
    .eq('id', state.businessId)
    .single();
  if (data) {
    state.businessName            = data.name             ?? null;
    state.businessEmail           = data.email            ?? null;
    state.businessPhone           = data.phone            ?? null;
    state.businessTimezone        = data.timezone         ?? null;
    state.businessAddressStreet   = data.address_street   ?? null;
    state.businessAddressCity     = data.address_city     ?? null;
    state.businessAddressProvince = data.address_province ?? null;
    state.businessAddressZip      = data.address_zip      ?? null;
  }
};

export const saveBusinessInfo = async function ({ name, email, phone, timezone, addressStreet, addressCity, addressProvince, addressZip }) {
  const { error } = await supabase
    .from('businesses')
    .update({
      name,
      email:            email            || null,
      phone:            phone            || null,
      timezone:         timezone         || null,
      address_street:   addressStreet    || null,
      address_city:     addressCity      || null,
      address_province: addressProvince  || null,
      address_zip:      addressZip       || null,
    })
    .eq('id', state.businessId);
  if (error) throw error;
  state.businessName            = name;
  state.businessEmail           = email            || null;
  state.businessPhone           = phone            || null;
  state.businessTimezone        = timezone         || null;
  state.businessAddressStreet   = addressStreet    || null;
  state.businessAddressCity     = addressCity      || null;
  state.businessAddressProvince = addressProvince  || null;
  state.businessAddressZip      = addressZip       || null;
};

// Settings > Home screen "Type of place" (owner only: RLS lets only the owner
// update the businesses row). Keeps business_type / business_industry, which
// the admin panel reads, in step with settings.placeType.
export const saveBusinessKind = async function ({ businessType, industry }) {
  const { error } = await supabase
    .from('businesses')
    .update({ business_type: businessType || null, business_industry: industry || null })
    .eq('id', state.businessId);
  if (error) throw error;
  state.businessType     = businessType || null;
  state.businessIndustry = industry     || null;
};

export const saveOnboardingInfo = async function ({ businessName, businessType, industry, phone, street, city, province, zip, country }) {
  const { data: updated, error } = await supabase
    .from('businesses')
    .update({
      name:              businessName,
      business_type:     businessType     || null,
      business_industry: industry         || null,
      phone:             phone            || null,
      address_street:    street           || null,
      address_city:      city             || null,
      address_province:  province         || null,
      address_zip:       zip              || null,
      address_country:   country          || null,
    })
    .eq('id', state.businessId)
    .select('id');
  if (error) throw new Error(error.message);
  if (!updated?.length) throw new Error(`Business record not found (id: ${state.businessId}). Please refresh and try again.`);
  state.businessName            = businessName;
  state.businessType            = businessType     || null;
  state.businessIndustry        = industry         || null;
  state.businessPhone           = phone            || null;
  state.businessAddressStreet   = street           || null;
  state.businessAddressCity     = city             || null;
  state.businessAddressProvince = province         || null;
  state.businessAddressZip      = zip              || null;
  state.businessAddressCountry  = country          || null;
  state.needsOnboarding         = false;
  // The address is known now: a timezone guessed from the device (or UTC)
  // gives way to the one for the business's state.
  _fillBusinessTimezone();
};

// ── DB ↔ app shape mapping ────────────────────────────────────────────────────

const dbToItem = (row) => ({
  _id: row.id,
  itemName: row.item_name,
  price: Number(row.price),
  category: row.category,
  imageURL: isMissingImage(row.image_url) ? null : row.image_url,
  iconEmoji: row.icon_emoji || null,
  _stock: row.stock ?? "0",
  hasVariants: row.has_variants,
  variants: row.variants ?? [],
  description: row.description ?? "",
  status: row.status ?? "active",
  taxClass: row.tax_class ?? "standard",
});

// ── DB ↔ app shape mapping (employees) ───────────────────────────────────────

const dbToEmployee = (row) => ({
  id: row.id,
  name: row.name,
  role: row.role ?? "",
  systemRole: row.system_role ?? "",
});

// ── DB ↔ app shape mapping (expenses) ────────────────────────────────────────

const dbToExpense = (row) => ({
  id: row.id,
  amount: Number(row.amount),
  description: row.description,
  category: row.category ?? "",
  expenseDate: row.expense_date,
  createdAt: row.created_at,
  createdBy: row.created_by,
});

// ── Loaders (called once at app init) ────────────────────────────────────────

export const loadEmployees = async function () {
  const { data, error } = await supabase
    .from("employees")
    .select("*")
    .eq("user_id", state.businessId)
    .order("created_at");
  if (error) throw error;
  state.employees = data.map(dbToEmployee);
};

export const loadMenuItems = async function () {
  const { data, error } = await supabase
    .from("menu_items")
    .select("*")
    .eq("user_id", state.businessId)
    .order("created_at");
  if (error) throw error;
  // Soft delete reuses the existing free-text status column (status = 'deleted')
  // - no schema change. Deleted items are kept out of the sellable catalogue but
  // remembered so Reports can tag them "(deleted)".
  state.menuItems = data.filter((r) => r.status !== "deleted").map(dbToItem);
  state.deletedItems = data
    .filter((r) => r.status === "deleted")
    .map((r) => ({ id: r.id, name: r.item_name }));
};

export const loadMenuCategories = async function () {
  const { data, error } = await supabase
    .from("menu_categories")
    .select("name")
    .eq("user_id", state.businessId)
    .order("created_at");
  if (error) throw error;
  state.menuCategories = data.map((r) => r.name);
};

// ── Menu item CRUD ────────────────────────────────────────────────────────────

const uploadImage = async function (file, existingUrl = null) {
  // No new file: keep whatever was there (a real URL preserves; "no image"
  // stays null so the DB never stores a default/placeholder string).
  if (!file || file.size === 0) return isMissingImage(existingUrl) ? null : existingUrl;
  // Validate + downscale/compress before upload (throws a friendly error on a
  // non-image or an image too large to process).
  file = await prepareImageForUpload(file);
  const ext = file.name.split(".").pop();
  const path = `${state.businessId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from("item-images").upload(path, file);
  if (error) throw error;
  const { data } = supabase.storage.from("item-images").getPublicUrl(path);
  return data.publicUrl;
};

export const uploadNewMenuItem = async function (newItem) {
  const variants = newItem.variants ?? [];
  // Photo and emoji are mutually exclusive - whichever mode was active on
  // submit wins, so switching tabs never leaves stale data from the other.
  const useEmoji = newItem.imageMode === "emoji" && !!newItem.iconEmoji;
  const imageURL = useEmoji ? null : await uploadImage(newItem.image);

  const { data, error } = await supabase
    .from("menu_items")
    .insert({
      user_id: state.businessId,
      item_name: newItem.name,
      // No price entered -> default to 0 (free / price-on-variant items).
      price: Number(newItem.price) || 0,
      category: newItem.category,
      image_url: imageURL,
      icon_emoji: useEmoji ? newItem.iconEmoji : null,
      stock: "0",
      has_variants: variants.length > 0,
      variants,
      description:
        "This is a sample description of the menu item. You can add more details here.",
      status: "active",
      tax_class: "standard", // tax exemption is edit-only - new items always start Taxable
    })
    .select()
    .single();
  if (error) throw error;

  const created = dbToItem(data);
  state.menuItems.push(created);
  // Returned so the add form can attach inventory tracking to the new item's id.
  return created;
};

// Soft delete: set status = 'deleted' so the row (and its name) survives for
// history. No schema change - reuses the free-text status column. The item
// leaves the sellable catalogue but is remembered in state.deletedItems so
// Reports can render it as "Name (deleted)".
export const deleteMenuItem = async function (id) {
  return softDeleteItems([id]);
};

// A bulk write can fail on the first hit after the access token has gone stale
// (the request races the auto-refresh); retrying once after a beat sails through.
// These writes are idempotent, so a retry can't double-apply.
const _retryWrite = async function (fn, retries = 1) {
  try {
    const res = await fn();
    if (res?.error) throw res.error;
    return res;
  } catch (e) {
    if (retries > 0) {
      await new Promise((r) => setTimeout(r, 250));
      return _retryWrite(fn, retries - 1);
    }
    throw e;
  }
};

export const softDeleteItems = async function (ids) {
  if (!ids?.length) return;
  await _retryWrite(() =>
    supabase
      .from("menu_items")
      .update({ status: "deleted" })
      .in("id", ids)
      .eq("user_id", state.businessId),
  );
  const removed = state.menuItems.filter((i) => ids.includes(i._id));
  state.deletedItems.push(...removed.map((i) => ({ id: i._id, name: i.itemName })));
  state.menuItems = state.menuItems.filter((i) => !ids.includes(i._id));
  // A soft-deleted product must stop consuming stock and its auto "simple count"
  // item must leave the Stock list. Best effort - a failure here never blocks the delete.
  deleteInventoryForProducts(ids).catch(() => {});
};

export const bulkUpdateItemStatus = async function (ids, status) {
  if (!ids?.length) return;
  await _retryWrite(() =>
    supabase
      .from("menu_items")
      .update({ status })
      .in("id", ids)
      .eq("user_id", state.businessId),
  );
  state.menuItems.forEach((i) => {
    if (ids.includes(i._id)) i.status = status;
  });
};

// Bulk CSV menu import. `items` are already assembled + de-duplicated by the
// controller (shape: { itemName, price, category, variants, hasVariants }).
// `newCategories` are normalized (lowercase) category names not yet in the
// catalogue. Both writes go through a single batched insert each, so 50 items
// is one round-trip, not 50. No schema change - reuses menu_items/menu_categories.
// Centralized exemption picker (Receipt Adjustments panel) - the Edit Item modal no
// longer writes tax_class itself, so this is the only write path now.
export const bulkUpdateItemTaxClass = async function (ids, taxClass) {
  if (!ids?.length) return;
  await _retryWrite(() =>
    supabase
      .from("menu_items")
      .update({ tax_class: taxClass })
      .in("id", ids)
      .eq("user_id", state.businessId),
  );
  state.menuItems.forEach((i) => {
    if (ids.includes(i._id)) i.taxClass = taxClass;
  });
};

export const bulkImportMenuItems = async function (items, newCategories = []) {
  if (!items?.length) return { added: 0 };

  // 1. Create any missing categories first (deduped against current state).
  const toCreate = [...new Set(newCategories.map((c) => c.trim().toLowerCase()).filter(Boolean))]
    .filter((c) => !state.menuCategories.includes(c));
  if (toCreate.length) {
    await _retryWrite(() =>
      supabase
        .from("menu_categories")
        .insert(toCreate.map((name) => ({ user_id: state.businessId, name }))),
    );
    state.menuCategories.push(...toCreate);
  }

  // 2. Batched insert of the items. Category is stored lowercase to match how
  // the add form / category list store it; a blank category lands in
  // "uncategorized" (loadMenuItems groups it there).
  const rows = items.map((it) => ({
    user_id: state.businessId,
    item_name: it.itemName,
    price: Number(it.price) || 0,
    category: it.category ? it.category.trim().toLowerCase() : "uncategorized",
    image_url: null,
    stock: "0",
    has_variants: Boolean(it.hasVariants),
    variants: it.variants ?? [],
    description: "",
    status: "active",
    tax_class: "standard", // no tax-class column in the CSV format - same precedent as status
  }));

  const { data } = await _retryWrite(() =>
    supabase.from("menu_items").insert(rows).select(),
  );
  const inserted = (data ?? []).map(dbToItem);
  state.menuItems.push(...inserted);
  return { added: inserted.length };
};

export const deleteCartItem = function (index) {
  state.cart.splice(index, 1);
};

export const updateMenuItem = async function (id, rawData) {
  const item = state.menuItems.find((item) => item._id === id);
  if (!item) throw new Error("Item not found");
  const prevName = item.itemName;

  const hasVariants = rawData.hasVariants === "on";
  const variants = hasVariants ? parseVariants(rawData) : [];

  // Photo and emoji are mutually exclusive - whichever mode was active on
  // submit wins, clearing the other so a switch never leaves stale data.
  const useEmoji = rawData.imageMode === "emoji" && !!rawData.iconEmoji;
  const newImageURL = useEmoji ? null : await uploadImage(rawData.image, item.imageURL);
  const newIconEmoji = useEmoji ? rawData.iconEmoji : null;

  const { error } = await supabase
    .from("menu_items")
    .update({
      item_name: rawData.itemName || "",
      price: Number(rawData.price) || 0,
      category: rawData.category || "",
      stock: rawData.stock || "0",
      description: rawData.description || "",
      has_variants: hasVariants,
      variants,
      status: rawData.status?.toLowerCase() || "active",
      // The dropdown only renders when tax-inclusive is on, so rawData.taxClass
      // is undefined whenever it's off - keep the item's existing class rather
      // than silently reverting an already-exempt item to standard.
      tax_class: rawData.taxClass === undefined ? item.taxClass : (rawData.taxClass === "exempt" ? "exempt" : "standard"),
      image_url: newImageURL,
      icon_emoji: newIconEmoji,
    })
    .eq("id", id)
    .eq("user_id", state.businessId);
  if (error) throw error;

  item.itemName = rawData.itemName || "";
  item.price = Number(rawData.price) || 0;
  item.category = rawData.category || "";
  item._stock = rawData.stock || "0";
  item.description = rawData.description || "";
  item.hasVariants = hasVariants;
  item.variants = variants;
  item.status = rawData.status?.toLowerCase() || "active";
  item.taxClass = rawData.taxClass === undefined ? item.taxClass : (rawData.taxClass === "exempt" ? "exempt" : "standard");
  item.imageURL = newImageURL;
  item.iconEmoji = newIconEmoji;

  // Keep the auto-created "simple count" inventory item's name in sync so the
  // Stock list stays readable. Best effort - never blocks the item save.
  if (item.itemName && item.itemName !== prevName) {
    syncSimpleItemName(id, item.itemName).catch(() => {});
  }
};

// Current tax class of a menu item by id, for stamping onto a cart line at the
// moment a sale is recorded (see controller.js _buildSale) - so a later edit to
// the item's tax class never retroactively changes a past receipt.
export const resolveItemTaxClass = function (itemId) {
  return state.menuItems.find((i) => i._id === itemId)?.taxClass ?? "standard";
};

// ── Inventory ─────────────────────────────────────────────────────────────────
// Stock tracking connected to products. One ledger (inventory_items); products
// consume from it via inventory_links (base row per inventory item + optional
// per-variant override rows). "Simple count" mode is sugar: an auto-created
// inventory item (linked_menu_item_id set) with a single base link of qty 1.
// ALL quantity changes go through SECURITY DEFINER RPCs (atomic + logged in
// inventory_adjustments); direct table writes are for non-quantity fields only.

const dbToInventoryItem = (row) => ({
  id: row.id,
  name: row.name,
  unit: row.unit ?? "unit",
  quantity: Number(row.quantity ?? 0),
  lowStockThreshold: row.low_stock_threshold == null ? null : Number(row.low_stock_threshold),
  linkedMenuItemId: row.linked_menu_item_id ?? null,
  sku: row.sku ?? null,
  category: row.category ?? null,
  unitCost: row.unit_cost == null ? null : Number(row.unit_cost),
  supplierId: row.supplier_id ?? null,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const dbToInventoryLink = (row) => ({
  id: row.id,
  menuItemId: row.menu_item_id,
  inventoryItemId: row.inventory_item_id,
  qty: Number(row.qty ?? 0),
  variantLabel: row.variant_label ?? null,
  variantOption: row.variant_option ?? null,
});

// The acting person's display name for the adjustment log - the switched-in
// cashier if one is active, else the logged-in staff member.
const _inventoryActorName = function () {
  const who = state.currentCashier ?? state.currentStaff;
  return [who?.firstName, who?.lastName].filter(Boolean).join(" ") || null;
};

const dbToItemSupplier = (row) => ({
  id: row.id,
  inventoryItemId: row.inventory_item_id,
  supplierId: row.supplier_id,
  unitCost: row.unit_cost == null ? null : Number(row.unit_cost),
  preferred: !!row.preferred,
});

export const loadInventory = async function () {
  const [itemsRes, linksRes, itemSupRes] = await Promise.all([
    supabase
      .from("inventory_items")
      .select("*")
      .eq("business_id", state.businessId)
      .order("name"),
    supabase
      .from("inventory_links")
      .select("*")
      .eq("business_id", state.businessId)
      .order("created_at"),
    // Tolerant of a not-yet-migrated DB: the price list just stays empty.
    supabase
      .from("inventory_item_suppliers")
      .select("*")
      .eq("business_id", state.businessId)
      .order("created_at")
      .then((res) => res, () => ({ data: [] })),
  ]);
  if (itemsRes.error) throw itemsRes.error;
  if (linksRes.error) throw linksRes.error;
  state.inventoryItems = (itemsRes.data ?? []).map(dbToInventoryItem);
  state.inventoryLinks = (linksRes.data ?? []).map(dbToInventoryLink);
  state.itemSuppliers = (itemSupRes.error ? [] : itemSupRes.data ?? []).map(dbToItemSupplier);
};

// ── Item <-> supplier price list ─────────────────────────────────────────────
// One item can be bought from several suppliers, each with their own price;
// exactly one is PREFERRED (DB-enforced partial unique index). The item's
// inventory_items.supplier_id mirrors the preferred row so every existing
// display/reorder site keeps working.

const _syncPreferredSupplier = async function (itemId) {
  const pref = state.itemSuppliers.find((r) => r.inventoryItemId === itemId && r.preferred) ?? null;
  const supplierId = pref?.supplierId ?? null;
  const { error } = await supabase
    .from("inventory_items")
    .update({ supplier_id: supplierId })
    .eq("id", itemId)
    .eq("business_id", state.businessId);
  if (error) throw error;
  const item = state.inventoryItems.find((i) => i.id === itemId);
  if (item) item.supplierId = supplierId;
};

export const addItemSupplier = async function (itemId, supplierId, unitCost) {
  const first = !state.itemSuppliers.some((r) => r.inventoryItemId === itemId);
  const { data, error } = await supabase
    .from("inventory_item_suppliers")
    .insert({
      business_id: state.businessId,
      inventory_item_id: itemId,
      supplier_id: supplierId,
      unit_cost: unitCost == null || unitCost === "" || isNaN(Number(unitCost)) ? null : Number(unitCost),
      preferred: first, // the first linked supplier is automatically preferred
    })
    .select()
    .single();
  if (error) {
    if (error.code === "23505") throw new Error("That supplier is already linked to this item");
    throw error;
  }
  state.itemSuppliers.push(dbToItemSupplier(data));
  if (first) await _syncPreferredSupplier(itemId);
  return data;
};

export const setPreferredItemSupplier = async function (rowId) {
  const row = state.itemSuppliers.find((r) => r.id === rowId);
  if (!row || row.preferred) return;
  // Clear the current preferred first (the partial unique index allows one).
  const { error: clearError } = await supabase
    .from("inventory_item_suppliers")
    .update({ preferred: false })
    .eq("inventory_item_id", row.inventoryItemId)
    .eq("business_id", state.businessId)
    .eq("preferred", true);
  if (clearError) throw clearError;
  const { error } = await supabase
    .from("inventory_item_suppliers")
    .update({ preferred: true })
    .eq("id", rowId)
    .eq("business_id", state.businessId);
  if (error) throw error;
  state.itemSuppliers.forEach((r) => {
    if (r.inventoryItemId === row.inventoryItemId) r.preferred = r.id === rowId;
  });
  await _syncPreferredSupplier(row.inventoryItemId);
};

export const updateItemSupplierCost = async function (rowId, unitCost) {
  const cost = unitCost == null || unitCost === "" || isNaN(Number(unitCost)) ? null : Number(unitCost);
  const { error } = await supabase
    .from("inventory_item_suppliers")
    .update({ unit_cost: cost })
    .eq("id", rowId)
    .eq("business_id", state.businessId);
  if (error) throw error;
  const row = state.itemSuppliers.find((r) => r.id === rowId);
  if (row) row.unitCost = cost;
};

export const removeItemSupplier = async function (rowId) {
  const row = state.itemSuppliers.find((r) => r.id === rowId);
  if (!row) return;
  const { error } = await supabase
    .from("inventory_item_suppliers")
    .delete()
    .eq("id", rowId)
    .eq("business_id", state.businessId);
  if (error) throw error;
  state.itemSuppliers = state.itemSuppliers.filter((r) => r.id !== rowId);
  if (row.preferred) {
    // Promote the next remaining supplier (if any) so the item keeps a default.
    const next = state.itemSuppliers.find((r) => r.inventoryItemId === row.inventoryItemId);
    if (next) {
      const { error: promoteError } = await supabase
        .from("inventory_item_suppliers")
        .update({ preferred: true })
        .eq("id", next.id)
        .eq("business_id", state.businessId);
      if (!promoteError) next.preferred = true;
    }
    await _syncPreferredSupplier(row.inventoryItemId);
  }
};

export const loadInventoryAdjustments = async function (itemId = null, limit = 500) {
  // The embedded sales row lets History label sale/void rows with the invoice
  // number via formatInvoiceNumber(receipt_number, sale_date).
  let query = supabase
    .from("inventory_adjustments")
    .select("*, sales(receipt_number, sale_date)")
    .eq("business_id", state.businessId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (itemId) query = query.eq("inventory_item_id", itemId);
  const { data, error } = await query;
  if (error) throw error;
  state.inventoryAdjustments = (data ?? []).map((row) => ({
    id: row.id,
    inventoryItemId: row.inventory_item_id,
    delta: Number(row.delta),
    kind: row.kind,
    note: row.note ?? null,
    saleId: row.sale_id ?? null,
    receiptNumber: row.sales?.receipt_number ?? null,
    saleDate: row.sales?.sale_date ?? null,
    staffName: row.staff_name ?? null,
    createdAt: row.created_at,
  }));
  return state.inventoryAdjustments;
};

// Optional profile fields shared by create + update. Empty strings become NULL.
const _inventoryItemExtras = function ({ sku, category, unitCost, supplierId } = {}) {
  const out = {};
  if (sku !== undefined) out.sku = (sku ?? "").trim() || null;
  if (category !== undefined) out.category = (category ?? "").trim() || null;
  if (unitCost !== undefined)
    out.unit_cost = unitCost == null || unitCost === "" || isNaN(Number(unitCost)) ? null : Number(unitCost);
  if (supplierId !== undefined) out.supplier_id = supplierId || null;
  return out;
};

export const createInventoryItem = async function ({ name, unit, quantity = 0, lowStockThreshold = null, ...extras }) {
  const { data, error } = await supabase
    .from("inventory_items")
    .insert({
      business_id: state.businessId,
      name: name.trim(),
      unit: (unit || "unit").trim(),
      quantity: Number(quantity) || 0,
      low_stock_threshold: lowStockThreshold == null || lowStockThreshold === "" ? null : Number(lowStockThreshold),
      ..._inventoryItemExtras(extras),
    })
    .select()
    .single();
  if (error) throw error;
  const item = dbToInventoryItem(data);
  state.inventoryItems.push(item);
  state.inventoryItems.sort((a, b) => a.name.localeCompare(b.name));
  return item;
};

// Non-quantity fields only - quantity always moves via adjustInventory so every
// change leaves a history row.
export const updateInventoryItem = async function (id, { name, unit, lowStockThreshold, ...extras }) {
  const { data, error } = await supabase
    .from("inventory_items")
    .update({
      name: name.trim(),
      unit: (unit || "unit").trim(),
      low_stock_threshold: lowStockThreshold == null || lowStockThreshold === "" ? null : Number(lowStockThreshold),
      ..._inventoryItemExtras(extras),
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .eq("business_id", state.businessId)
    .select()
    .single();
  if (error) throw error;
  const idx = state.inventoryItems.findIndex((i) => i.id === id);
  if (idx !== -1) state.inventoryItems[idx] = dbToInventoryItem(data);
  state.inventoryItems.sort((a, b) => a.name.localeCompare(b.name));
  return state.inventoryItems[idx];
};

// FK cascades remove the item's links and its adjustment history. Linked
// products silently revert to untracked for this ingredient - the caller
// confirms with the user first (the view's warning names the linked count).
export const deleteInventoryItem = async function (id) {
  const { error } = await supabase
    .from("inventory_items")
    .delete()
    .eq("id", id)
    .eq("business_id", state.businessId);
  if (error) throw error;
  state.inventoryItems = state.inventoryItems.filter((i) => i.id !== id);
  state.inventoryLinks = state.inventoryLinks.filter((l) => l.inventoryItemId !== id);
};

// Manual +/- via the adjust_inventory RPC (atomic increment + 'manual' log row).
export const adjustInventory = async function (itemId, delta, note) {
  const { data, error } = await supabase.rpc("adjust_inventory", {
    p_item_id: itemId,
    p_delta: delta,
    p_note: note || null,
    p_staff_name: _inventoryActorName(),
  });
  if (error) throw error;
  const item = state.inventoryItems.find((i) => i.id === itemId);
  if (item && data != null) item.quantity = Number(data);
  return item;
};

// Persists a product's tracking config from the item editor.
// cfg = { mode: 'none'|'simple'|'ingredients',
//         onHand,                                            // simple mode
//         links: [{ inventoryItemId, qty, variantLabel, variantOption }] } // ingredients
export const saveProductInventory = async function (menuItemId, cfg) {
  const autoItem = state.inventoryItems.find((i) => i.linkedMenuItemId === menuItemId);
  const product = state.menuItems.find((i) => i._id === menuItemId);

  const deleteAllLinks = async () => {
    const { error } = await supabase
      .from("inventory_links")
      .delete()
      .eq("menu_item_id", menuItemId)
      .eq("business_id", state.businessId);
    if (error) throw error;
    state.inventoryLinks = state.inventoryLinks.filter((l) => l.menuItemId !== menuItemId);
  };
  const deleteAutoItem = async () => {
    if (!autoItem) return;
    await deleteInventoryItem(autoItem.id);
  };

  if (cfg.mode === "none") {
    await deleteAllLinks();
    await deleteAutoItem();
    return;
  }

  if (cfg.mode === "simple") {
    const onHand = Number(cfg.onHand) || 0;
    let item = autoItem;
    if (!item) {
      // Fresh simple tracking: create the backing item at the given count.
      // History starts at the first adjustment (initial qty is not logged).
      const { data, error } = await supabase
        .from("inventory_items")
        .insert({
          business_id: state.businessId,
          name: product?.itemName || "Untitled item",
          unit: "unit",
          quantity: onHand,
          linked_menu_item_id: menuItemId,
        })
        .select()
        .single();
      if (error) throw error;
      item = dbToInventoryItem(data);
      state.inventoryItems.push(item);
      state.inventoryItems.sort((a, b) => a.name.localeCompare(b.name));
    } else if (onHand !== item.quantity) {
      // Count edited in the product editor - go through the RPC so it's logged.
      await adjustInventory(item.id, onHand - item.quantity, "Set from product editor");
    }
    // Exactly one base link (qty 1) and nothing else.
    await deleteAllLinks();
    const { data: linkData, error: linkError } = await supabase
      .from("inventory_links")
      .insert({
        business_id: state.businessId,
        menu_item_id: menuItemId,
        inventory_item_id: item.id,
        qty: 1,
      })
      .select()
      .single();
    if (linkError) throw linkError;
    state.inventoryLinks.push(dbToInventoryLink(linkData));
    return;
  }

  // mode === 'ingredients': switching away from simple removes the auto item;
  // links are replace-all (delete + batch insert).
  await deleteAllLinks();
  await deleteAutoItem();
  // qty 0 is valid ONLY on a variant row: it means "None" - that option uses
  // none of this item (overrides the base down to zero). A base row needs a
  // positive qty to exist at all.
  const rows = (cfg.links ?? [])
    .filter((l) => l.inventoryItemId &&
      (Number(l.qty) > 0 || (l.variantLabel != null && Number(l.qty) === 0)))
    .map((l) => ({
      business_id: state.businessId,
      menu_item_id: menuItemId,
      inventory_item_id: l.inventoryItemId,
      qty: Number(l.qty),
      variant_label: l.variantLabel ?? null,
      variant_option: l.variantOption ?? null,
    }));
  if (rows.length) {
    const { data, error } = await supabase.from("inventory_links").insert(rows).select();
    if (error) throw error;
    state.inventoryLinks.push(...(data ?? []).map(dbToInventoryLink));
  }
};

// Renaming a simple-tracked product renames its auto inventory item.
export const syncSimpleItemName = async function (menuItemId, newName) {
  const { error } = await supabase
    .from("inventory_items")
    .update({ name: newName, updated_at: new Date().toISOString() })
    .eq("linked_menu_item_id", menuItemId)
    .eq("business_id", state.businessId);
  if (error) throw error;
  const item = state.inventoryItems.find((i) => i.linkedMenuItemId === menuItemId);
  if (item) item.name = newName;
};

// Cleanup when products are (soft-)deleted: their links go, and their auto
// simple items leave the Stock list. Ingredient items shared with other
// products are untouched.
export const deleteInventoryForProducts = async function (menuItemIds) {
  if (!menuItemIds?.length) return;
  await supabase
    .from("inventory_links")
    .delete()
    .in("menu_item_id", menuItemIds)
    .eq("business_id", state.businessId);
  await supabase
    .from("inventory_items")
    .delete()
    .in("linked_menu_item_id", menuItemIds)
    .eq("business_id", state.businessId);
  state.inventoryLinks = state.inventoryLinks.filter((l) => !menuItemIds.includes(l.menuItemId));
  state.inventoryItems = state.inventoryItems.filter(
    (i) => !i.linkedMenuItemId || !menuItemIds.includes(i.linkedMenuItemId),
  );
};

// ── Consumption resolution (pure - no network) ────────────────────────────────
// For one cart line, what does each linked inventory item lose? Per inventory
// item: the base row's qty, unless one or more variant override rows MATCH the
// line's selected options - then the SUM of matching overrides replaces base.
// A matching qty-0 override ("None" in the editor) therefore zeroes out the
// base for that option: it counts as matched, contributes 0 to the sum, and
// the perUnit > 0 guard below drops the item entirely when the sum is 0.
// A row only matches if its (variantLabel, variantOption) still exists in the
// product's CURRENT variants JSONB - renamed/removed options never match, so
// orphaned overrides silently fall back to base (surfaced for cleanup in the
// item editor, not here).
const _resolveLineConsumption = function (line) {
  const links = state.inventoryLinks.filter((l) => l.menuItemId === line.id);
  if (!links.length) return [];
  const menuItem = state.menuItems.find((i) => i._id === line.id);
  const validPairs = new Set();
  (menuItem?.variants ?? []).forEach((group) => {
    (group.options ?? []).forEach((opt) => {
      validPairs.add(`${group.optionLabel}∥${opt.optionName}`);
    });
  });
  const selectedNames = new Set((line.selectedVariants ?? []).map((v) => v.variantName));

  const byItem = new Map();
  links.forEach((l) => {
    if (!byItem.has(l.inventoryItemId)) byItem.set(l.inventoryItemId, []);
    byItem.get(l.inventoryItemId).push(l);
  });

  const out = [];
  const lineQty = Number(line.quantity) || 1;
  byItem.forEach((rows, inventoryItemId) => {
    const base = rows.find((r) => r.variantLabel == null);
    const matched = rows.filter(
      (r) =>
        r.variantLabel != null &&
        selectedNames.has(r.variantOption) &&
        validPairs.has(`${r.variantLabel}∥${r.variantOption}`),
    );
    const perUnit = matched.length
      ? matched.reduce((sum, r) => sum + r.qty, 0)
      : (base?.qty ?? 0);
    if (perUnit > 0) out.push({ inventory_item_id: inventoryItemId, qty: perUnit * lineQty });
  });
  return out;
};

// Aggregated consumption for a whole cart: [{ inventory_item_id, qty }].
export const computeConsumption = function (cartLines) {
  const totals = new Map();
  (cartLines ?? []).forEach((line) => {
    _resolveLineConsumption(line).forEach(({ inventory_item_id, qty }) => {
      totals.set(inventory_item_id, (totals.get(inventory_item_id) ?? 0) + qty);
    });
  });
  return [...totals.entries()].map(([inventory_item_id, qty]) => ({ inventory_item_id, qty }));
};

// Stock status of one product: which linked inventory items are out (at or
// below zero) and which are low (at/below their alert threshold). `state` is
// the worst of the two for quick checks. A "Simple count" product's auto item
// appears under the product's own name - natural in an alert list.
export const productStockStatus = function (menuItemId) {
  const out = [];
  const low = [];
  // Inventory tracking OFF = no alerts anywhere (order-screen chips, the
  // out-of-stock heads-up, KDS). Data stays intact; this only mutes signals.
  if (state.settings.inventoryEnabled === false)
    return { state: 'ok', out, low };
  state.inventoryLinks.forEach((l) => {
    if (l.menuItemId !== menuItemId) return;
    if (!(Number(l.qty) > 0)) return; // a "None" (qty 0) override never drives alerts
    const inv = state.inventoryItems.find((i) => i.id === l.inventoryItemId);
    if (!inv) return;
    if (inv.quantity <= 0) {
      if (!out.includes(inv.name)) out.push(inv.name);
    } else if (inv.lowStockThreshold != null && inv.quantity <= inv.lowStockThreshold) {
      if (!low.includes(inv.name)) low.push(inv.name);
    }
  });
  return { state: out.length ? 'out' : low.length ? 'low' : 'ok', out, low };
};

export const productOutOfStock = function (menuItemId) {
  return productStockStatus(menuItemId).state === 'out';
};

// Map<menuItemId, { state, out, low }> for every non-ok product - what the
// order screen renders its alert chips from.
export const productStockStatuses = function () {
  const map = new Map();
  if (state.settings.inventoryEnabled === false) return map;
  state.menuItems.forEach((i) => {
    const st = productStockStatus(i._id);
    if (st.state !== 'ok') map.set(i._id, st);
  });
  return map;
};

// Optimistic local bump so the selling terminal's badges update instantly
// without waiting for the RPC round-trip. sign = -1 consumes, +1 restores.
// Clamped at zero like the RPCs (stock never goes negative); the next
// loadInventory re-syncs to server truth either way.
export const applyConsumptionLocally = function (lines, sign = -1) {
  (lines ?? []).forEach(({ inventory_item_id, qty }) => {
    const item = state.inventoryItems.find((i) => i.id === inventory_item_id);
    if (item) item.quantity = Math.max(0, item.quantity + sign * qty);
  });
};

export const applyInventoryConsumption = async function (saleId, lines, staffName) {
  const { error } = await supabase.rpc("apply_inventory_consumption", {
    p_sale_id: saleId,
    p_lines: lines,
    p_staff_name: staffName || _inventoryActorName(),
  });
  if (error) throw error;
};

export const reverseInventoryConsumption = async function (saleId, staffName) {
  const { error } = await supabase.rpc("reverse_inventory_consumption", {
    p_sale_id: saleId,
    p_staff_name: staffName || _inventoryActorName(),
  });
  if (error) throw error;
};

// ── Inventory: product links from the inventory side ─────────────────────────
// The item drawer's "Used by" block edits BASE link rows only (variant_label
// null); variant overrides stay in the product editor. These keep
// state.inventoryLinks in sync so checkout consumption reflects edits live.

// overrides (optional): [{ variantLabel, variantOption, qty }] - per-option
// amounts created alongside the base row (same rows the product editor
// writes; matching overrides SUM-replace the base at consumption time).
export const addInventoryLink = async function (menuItemId, inventoryItemId, qty, overrides = []) {
  const rows = [
    {
      business_id: state.businessId,
      menu_item_id: menuItemId,
      inventory_item_id: inventoryItemId,
      qty: Number(qty),
      variant_label: null,
      variant_option: null,
    },
    ...overrides
      .filter((o) => o.variantLabel && o.variantOption && Number.isFinite(Number(o.qty)) && Number(o.qty) >= 0)
      .map((o) => ({
        business_id: state.businessId,
        menu_item_id: menuItemId,
        inventory_item_id: inventoryItemId,
        qty: Number(o.qty),
        variant_label: o.variantLabel,
        variant_option: o.variantOption,
      })),
  ];
  const { data, error } = await supabase
    .from("inventory_links")
    .insert(rows)
    .select();
  if (error) {
    if (error.code === "23505")
      throw new Error("That product is already linked to this item");
    throw error;
  }
  const links = (data ?? []).map(dbToInventoryLink);
  state.inventoryLinks.push(...links);
  return links[0];
};

export const updateInventoryLinkQty = async function (linkId, qty) {
  const { error } = await supabase
    .from("inventory_links")
    .update({ qty: Number(qty) })
    .eq("id", linkId)
    .eq("business_id", state.businessId);
  if (error) throw error;
  const link = state.inventoryLinks.find((l) => l.id === linkId);
  if (link) link.qty = Number(qty);
  return link;
};

export const removeInventoryLink = async function (linkId) {
  const { error } = await supabase
    .from("inventory_links")
    .delete()
    .eq("id", linkId)
    .eq("business_id", state.businessId);
  if (error) throw error;
  state.inventoryLinks = state.inventoryLinks.filter((l) => l.id !== linkId);
};

// ── Suppliers (STRICTLY optional - nothing anywhere requires one) ────────────

const dbToSupplier = (row) => ({
  id: row.id,
  name: row.name,
  category: row.category ?? null,
  contactName: row.contact_name ?? null,
  phone: row.phone ?? null,
  email: row.email ?? null,
  address: row.address ?? null,
  leadTimeDays: row.lead_time_days == null ? null : Number(row.lead_time_days),
  paymentTerms: row.payment_terms ?? null,
  minOrder: row.min_order ?? null,
  status: row.status ?? "active",
  notes: row.notes ?? null,
  createdAt: row.created_at,
});

const _supplierFields = (data) => ({
  name: data.name.trim(),
  category: (data.category ?? "").trim() || null,
  contact_name: (data.contactName ?? "").trim() || null,
  phone: (data.phone ?? "").trim() || null,
  email: (data.email ?? "").trim() || null,
  address: (data.address ?? "").trim() || null,
  lead_time_days:
    data.leadTimeDays == null || data.leadTimeDays === "" || isNaN(Number(data.leadTimeDays))
      ? null
      : Math.max(0, Math.round(Number(data.leadTimeDays))),
  payment_terms: (data.paymentTerms ?? "").trim() || null,
  min_order: (data.minOrder ?? "").trim() || null,
  status: ["active", "preferred", "on_hold"].includes(data.status) ? data.status : "active",
  notes: (data.notes ?? "").trim() || null,
});

export const loadSuppliers = async function () {
  const { data, error } = await supabase
    .from("suppliers")
    .select("*")
    .eq("business_id", state.businessId)
    .order("name");
  if (error) throw error;
  state.suppliers = (data ?? []).map(dbToSupplier);
};

export const createSupplier = async function (data) {
  const { data: row, error } = await supabase
    .from("suppliers")
    .insert({ business_id: state.businessId, ..._supplierFields(data) })
    .select()
    .single();
  if (error) throw error;
  const supplier = dbToSupplier(row);
  state.suppliers.push(supplier);
  state.suppliers.sort((a, b) => a.name.localeCompare(b.name));
  return supplier;
};

export const updateSupplier = async function (id, data) {
  const { data: row, error } = await supabase
    .from("suppliers")
    .update({ ..._supplierFields(data), updated_at: new Date().toISOString() })
    .eq("id", id)
    .eq("business_id", state.businessId)
    .select()
    .single();
  if (error) throw error;
  const idx = state.suppliers.findIndex((s) => s.id === id);
  if (idx !== -1) state.suppliers[idx] = dbToSupplier(row);
  state.suppliers.sort((a, b) => a.name.localeCompare(b.name));
  return state.suppliers[idx];
};

// Items keep their history; the FK nulls their supplier server-side, mirrored
// locally. POs keep their supplier_name snapshot.
export const deleteSupplier = async function (id) {
  const { error } = await supabase
    .from("suppliers")
    .delete()
    .eq("id", id)
    .eq("business_id", state.businessId);
  if (error) throw error;
  state.suppliers = state.suppliers.filter((s) => s.id !== id);
  state.inventoryItems.forEach((i) => { if (i.supplierId === id) i.supplierId = null; });
  state.purchaseOrders.forEach((po) => { if (po.supplierId === id) po.supplierId = null; });
  state.itemSuppliers = state.itemSuppliers.filter((r) => r.supplierId !== id); // FK cascades server-side
};

// ── Purchase orders ──────────────────────────────────────────────────────────
// Line totals are always COMPUTED (qty x unitCost); only flat shipping + tax
// are stored. po_number is assigned by a DB trigger (gapless per business).
// "Overdue" is derived display state (sent + expected_date past), never stored.

const dbToPoLine = (row) => ({
  id: row.id,
  inventoryItemId: row.inventory_item_id ?? null,
  itemName: row.item_name,
  unit: row.unit ?? "unit",
  qty: Number(row.qty ?? 0),
  unitCost: row.unit_cost == null ? null : Number(row.unit_cost),
  createdAt: row.created_at,
});

const dbToPurchaseOrder = (row) => ({
  id: row.id,
  poNumber: row.po_number,
  supplierId: row.supplier_id ?? null,
  supplierName: row.supplier_name ?? null,
  status: row.status,
  expectedDate: row.expected_date ?? null,
  notes: row.notes ?? null,
  shipping: row.shipping == null ? null : Number(row.shipping),
  tax: row.tax == null ? null : Number(row.tax),
  createdByName: row.created_by_name ?? null,
  receiptUrl: row.receipt_url ?? null,
  createdAt: row.created_at,
  sentAt: row.sent_at ?? null,
  receivedAt: row.received_at ?? null,
  cancelledAt: row.cancelled_at ?? null,
  lines: (row.purchase_order_items ?? [])
    .map(dbToPoLine)
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
});

export const loadPurchaseOrders = async function () {
  const { data, error } = await supabase
    .from("purchase_orders")
    .select("*, purchase_order_items(*)")
    .eq("business_id", state.businessId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  state.purchaseOrders = (data ?? []).map(dbToPurchaseOrder);
};

const _poHeaderFields = (data) => ({
  supplier_id: data.supplierId || null,
  supplier_name: data.supplierId
    ? state.suppliers.find((s) => s.id === data.supplierId)?.name ?? null
    : null,
  expected_date: data.expectedDate || null,
  notes: (data.notes ?? "").trim() || null,
  shipping: data.shipping == null || data.shipping === "" ? null : Number(data.shipping) || 0,
  tax: data.tax == null || data.tax === "" ? null : Number(data.tax) || 0,
  // Only written when the caller resolved an upload (undefined = leave as is).
  ...(data.receiptUrl !== undefined ? { receipt_url: data.receiptUrl || null } : {}),
});

// Receipt photo for a purchase order - same guarded pipeline as every other
// image (type check, 5 MB ceiling with client-side compression, item-images
// bucket). Returns the public URL.
export const uploadPoReceipt = async function (file) {
  return uploadImage(file);
};

const _poLineRows = (poId, lines) =>
  (lines ?? [])
    .filter((l) => l.inventoryItemId && Number(l.qty) > 0)
    .map((l) => {
      const item = state.inventoryItems.find((i) => i.id === l.inventoryItemId);
      return {
        business_id: state.businessId,
        po_id: poId,
        inventory_item_id: l.inventoryItemId,
        item_name: item?.name ?? l.itemName ?? "Item",
        unit: item?.unit ?? l.unit ?? "unit",
        qty: Number(l.qty),
        unit_cost: l.unitCost == null || l.unitCost === "" ? null : Number(l.unitCost),
      };
    });

export const createPurchaseOrder = async function (data) {
  const { data: row, error } = await supabase
    .from("purchase_orders")
    .insert({
      business_id: state.businessId,
      ..._poHeaderFields(data),
      created_by_name: _inventoryActorName(),
    })
    .select()
    .single();
  if (error) throw error;
  const lineRows = _poLineRows(row.id, data.lines);
  let lineData = [];
  if (lineRows.length) {
    const { data: inserted, error: lineError } = await supabase
      .from("purchase_order_items")
      .insert(lineRows)
      .select();
    if (lineError) throw lineError;
    lineData = inserted ?? [];
  }
  const po = dbToPurchaseOrder({ ...row, purchase_order_items: lineData });
  state.purchaseOrders.unshift(po);
  return po;
};

// Draft-only edit: header update + replace-all lines (same reconcile pattern
// as saveProductInventory's links).
export const updatePurchaseOrder = async function (id, data) {
  const existing = state.purchaseOrders.find((p) => p.id === id);
  if (!existing || existing.status !== "draft")
    throw new Error("Only draft purchase orders can be edited");
  const { data: row, error } = await supabase
    .from("purchase_orders")
    .update({ ..._poHeaderFields(data), updated_at: new Date().toISOString() })
    .eq("id", id)
    .eq("business_id", state.businessId)
    .select()
    .single();
  if (error) throw error;
  const { error: delError } = await supabase
    .from("purchase_order_items")
    .delete()
    .eq("po_id", id)
    .eq("business_id", state.businessId);
  if (delError) throw delError;
  const lineRows = _poLineRows(id, data.lines);
  let lineData = [];
  if (lineRows.length) {
    const { data: inserted, error: lineError } = await supabase
      .from("purchase_order_items")
      .insert(lineRows)
      .select();
    if (lineError) throw lineError;
    lineData = inserted ?? [];
  }
  const idx = state.purchaseOrders.findIndex((p) => p.id === id);
  if (idx !== -1)
    state.purchaseOrders[idx] = dbToPurchaseOrder({ ...row, purchase_order_items: lineData });
  return state.purchaseOrders[idx];
};

// 'sent' (Approve & send - status only, no email) or 'cancelled'.
export const setPurchaseOrderStatus = async function (id, status) {
  const stamp =
    status === "sent" ? { sent_at: new Date().toISOString() }
    : status === "cancelled" ? { cancelled_at: new Date().toISOString() }
    : {};
  const { data: row, error } = await supabase
    .from("purchase_orders")
    .update({ status, ...stamp, updated_at: new Date().toISOString() })
    .eq("id", id)
    .eq("business_id", state.businessId)
    .select("*, purchase_order_items(*)")
    .single();
  if (error) throw error;
  const idx = state.purchaseOrders.findIndex((p) => p.id === id);
  if (idx !== -1) state.purchaseOrders[idx] = dbToPurchaseOrder(row);
  return state.purchaseOrders[idx];
};

// Whole-PO receive via the SECURITY DEFINER RPC (atomic + idempotent;
// increments stock, logs kind='po' rows, last-cost-wins unit_cost).
export const receivePurchaseOrder = async function (id) {
  const { error } = await supabase.rpc("receive_purchase_order", {
    p_po_id: id,
    p_staff_name: _inventoryActorName(),
  });
  if (error) throw error;
};

// Stock count apply via RPC: lines = [{ inventory_item_id, counted }].
export const applyStockCount = async function (lines) {
  const { error } = await supabase.rpc("apply_stock_count", {
    p_lines: lines,
    p_staff_name: _inventoryActorName(),
  });
  if (error) throw error;
};

// ── Category CRUD ─────────────────────────────────────────────────────────────

export const addCategory = async function (name) {
  const normalized = name.trim().toLowerCase();
  if (!normalized) throw new Error("Category name cannot be empty");
  if (state.menuCategories.includes(normalized))
    throw new Error("Category already exists");

  const { error } = await supabase
    .from("menu_categories")
    .insert({ user_id: state.businessId, name: normalized });
  if (error) throw error;
  state.menuCategories.push(normalized);
};

export const deleteCategory = async function (name) {
  const affected = state.menuItems.filter((i) => i.category === name);
  if (affected.length) {
    const { error: updateError } = await supabase
      .from("menu_items")
      .update({ category: "uncategorized" })
      .eq("user_id", state.businessId)
      .eq("category", name);
    if (updateError) throw updateError;
    affected.forEach((i) => { i.category = "uncategorized"; });
  }

  const { error } = await supabase
    .from("menu_categories")
    .delete()
    .eq("user_id", state.businessId)
    .eq("name", name);
  if (error) throw error;
  const index = state.menuCategories.indexOf(name);
  if (index !== -1) state.menuCategories.splice(index, 1);
};

// ── Settings: adjustment CRUD ─────────────────────────────────────────────────

const dbToAdjustment = (row) => ({
  id: row.id,
  name: row.name,
  type: row.type,
  calculation: row.calculation,
  value: Number(row.value),
  enabled: row.enabled,
});

export const loadAdjustments = async function () {
  const { data, error } = await supabase
    .from("adjustments")
    .select("*")
    .eq("user_id", state.businessId)
    .order("created_at");
  if (error) throw error;
  state.settings.adjustments = data.map(dbToAdjustment);
};

export const addAdjustment = async function (data) {
  const { data: row, error } = await supabase
    .from("adjustments")
    .insert({
      user_id: state.businessId,
      name: data.name,
      type: data.type,
      calculation: data.calculation,
      value: Number(data.value) || 0,
      enabled: true,
    })
    .select()
    .single();
  if (error) throw error;
  const adjustment = dbToAdjustment(row);
  state.settings.adjustments.push(adjustment);
  return adjustment;
};

export const updateAdjustment = async function (id, data) {
  const { error } = await supabase
    .from("adjustments")
    .update({
      name: data.name,
      type: data.type,
      calculation: data.calculation,
      value: Number(data.value) || 0,
    })
    .eq("id", id)
    .eq("user_id", state.businessId);
  if (error) throw error;
  const adj = state.settings.adjustments.find((a) => a.id === id);
  if (adj) {
    adj.name = data.name;
    adj.type = data.type;
    adj.calculation = data.calculation;
    adj.value = Number(data.value) || 0;
  }
};

export const deleteAdjustment = async function (id) {
  const { error } = await supabase
    .from("adjustments")
    .delete()
    .eq("id", id)
    .eq("user_id", state.businessId);
  if (error) throw error;
  const index = state.settings.adjustments.findIndex((a) => a.id === id);
  if (index !== -1) state.settings.adjustments.splice(index, 1);
};

export const toggleAdjustment = async function (id) {
  const adj = state.settings.adjustments.find((a) => a.id === id);
  if (!adj) throw new Error("Adjustment not found");
  const newEnabled = !adj.enabled;
  const { error } = await supabase
    .from("adjustments")
    .update({ enabled: newEnabled })
    .eq("id", id)
    .eq("user_id", state.businessId);
  if (error) throw error;
  adj.enabled = newEnabled;
};

// ── Per-receipt adjustments ───────────────────────────────────────────────────

export const initReceiptAdjustments = function () {
  state.currentPromoCode = null;
  state.currentLoyaltyCustomer = null;
  state.currentLoyaltyRedemption = null;
  state.currentReceiptAdjustments = state.settings.adjustments
    .filter((a) => a.enabled)
    .map((a) => ({
      id: a.id,
      name: a.name,
      type: a.type,
      calculation: a.calculation,
      value: a.value,
      appliedValue: a.value,
      removed: false,
      source: "auto",
    }));
};

export const overrideReceiptAdjustment = function (id, newValue) {
  const adj = state.currentReceiptAdjustments.find((a) => a.id === id);
  if (!adj) throw new Error("Receipt adjustment not found");
  adj.appliedValue = Number(newValue) || 0;
};

export const removeReceiptAdjustment = function (id) {
  const adj = state.currentReceiptAdjustments.find((a) => a.id === id);
  if (!adj) throw new Error("Receipt adjustment not found");
  adj.removed = true;
};

export const addManualReceiptAdjustment = function (data) {
  const adjustment = {
    id: generateAdjustmentId(),
    name: data.name,
    type: data.type,
    calculation: data.calculation,
    value: Number(data.value) || 0,
    appliedValue: Number(data.value) || 0,
    removed: false,
    source: "manual",
    // When a cashier without apply_receipt_adjustment adds this via a manager
    // override, record who authorized it (persists into the sale's adjustments).
    approvedBy: data.approvedBy ?? null,
  };
  state.currentReceiptAdjustments.push(adjustment);
  return adjustment;
};

export const clearReceiptAdjustments = function () {
  state.currentReceiptAdjustments = [];
  state.currentPromoCode = null;
  state.currentLoyaltyCustomer = null;
  state.currentLoyaltyRedemption = null;
};

// ── Calculation ───────────────────────────────────────────────────────────────

// Sales tax added at checkout (Tax Settings "Tax added at checkout" mode):
// { name, rate } or null. Rate + name share the taxInclusiveRate /
// taxInclusiveLabel settings with the "already included" mode.
export const addedTax = function () {
  const s = state.settings;
  const rate = Number(s.taxInclusiveRate);
  if (s.taxMode !== "exclusive" || !(rate > 0)) return null;
  return { name: taxLabel(), rate };
};

// Order (2026-10-10, US sales tax): discounts off the subtotal, then fees
// (service charge...) off the running total, then the added sales tax LAST,
// charged only on the TAXABLE base - taxable items (after their share of the
// discounts) plus fees, which are taxable by default. `items` are the
// cart/sale lines ({ id, lineId, totalPrice, taxClass? }); a line without a
// stamped taxClass resolves from the current menu. MUST stay in lockstep with
// step 9 of public.record_sale (migration-2026-10-10-sales-tax.sql).
export const calculateAdjustments = function (subtotal, adjustments, items = [], tax = addedTax()) {
  const active = adjustments.filter((a) => !a.removed);
  const discounts = active.filter((a) => a.type === "discount");
  const fees = active.filter((a) => a.type === "fee");

  const isExempt = (it) => (it.taxClass ?? resolveItemTaxClass(it.id)) === "exempt";
  const lineExempt = new Map(items.map((it) => [it.lineId, isExempt(it)]));
  const taxableGross = items.reduce((acc, it) => acc + (isExempt(it) ? 0 : Number(it.totalPrice) || 0), 0);
  const taxableShare = subtotal > 0 ? taxableGross / subtotal : 0;

  let runningTotal = subtotal;
  let taxableBase = taxableGross;
  const lineItems = [];

  discounts.forEach((adj) => {
    const amount =
      adj.calculation === "percentage"
        ? subtotal * (adj.appliedValue / 100)
        : adj.appliedValue;
    lineItems.push({ ...adj, computedAmount: -amount });
    runningTotal -= amount;
    // A free-item reward comps one specific line, so it only lowers the
    // taxable base if that line is taxable; every other discount is spread
    // across the cart in proportion.
    taxableBase -= adj.source === "loyalty_reward"
      ? (lineExempt.get(adj.cartItemLineId) ? 0 : amount)
      : amount * taxableShare;
  });

  fees.forEach((adj) => {
    const amount =
      adj.calculation === "percentage"
        ? runningTotal * (adj.appliedValue / 100)
        : adj.appliedValue;
    lineItems.push({ ...adj, computedAmount: amount });
    runningTotal += amount;
    taxableBase += amount;
  });

  let taxAmount = 0;
  if (tax) {
    taxAmount = Math.max(0, taxableBase) * (tax.rate / 100);
    lineItems.push({
      id: "sales-tax", name: tax.name, type: "fee", calculation: "percentage",
      value: tax.rate, appliedValue: tax.rate, removed: false, source: "tax",
      isTax: true, computedAmount: taxAmount,
    });
  }

  return {
    subtotal,
    lineItems,
    finalTotal: Math.max(0, runningTotal + taxAmount),
  };
};

// ── Today's sales total ───────────────────────────────────────────────────────

// The business's calendar day (its timezone, not the device's) as [start, end].
// A register whose clock is set to another zone must still count the cafe's
// "today" (team test 2026-10-24: a device ahead of Portland showed tomorrow).
const _bizDay = (offset = 0) => {
  const tz = businessTz();
  const day = addDays(todayIn(tz), offset);
  return [zonedToDate(day, '00:00', tz), new Date(zonedToDate(addDays(day, 1), '00:00', tz).getTime() - 1)];
};

export const loadTodaySalesTotal = async function () {
  const [start, end] = _bizDay(0);
  const { data, error } = await supabase
    .from("sales")
    .select("total_price")
    .eq("user_id", state.businessId)
    .gte("sale_date", start.toISOString())
    .lte("sale_date", end.toISOString())
    .is("voided_at", null);
  if (error) throw error;
  return data.reduce((sum, r) => sum + Number(r.total_price), 0);
};

// Today's expenses total for the home Sales & Expenses card (light query -
// the full cashflow fetch is too heavy for the home page load).
export const loadTodayExpensesTotal = async function () {
  const [start, end] = _bizDay(0);
  const { data, error } = await supabase
    .from("expenses")
    .select("amount")
    .gte("expense_date", start.toISOString())
    .lte("expense_date", end.toISOString());
  if (error) throw error;
  return (data ?? []).reduce((sum, r) => sum + Number(r.amount ?? 0), 0);
};

// Home cards and tiles (homeCards.js): today's tips, items sold and best
// sellers from one light query. Only runs while one of those is showing.
export const fetchTodaySnapshot = async function (startISO, endISO) {
  const { data, error } = await supabase
    .from("sales")
    .select("items, tip_amount")
    .eq("user_id", state.businessId)
    .gte("sale_date", startISO)
    .lte("sale_date", endISO)
    .is("voided_at", null);
  if (error) throw error;
  let tips = 0, tipped = 0, itemsSold = 0;
  const counts = new Map();
  for (const r of data ?? []) {
    const tip = Number(r.tip_amount ?? 0);
    tips += tip;
    if (tip > 0) tipped++;
    for (const it of r.items ?? []) {
      const q = Number(it.quantity ?? 1);
      itemsSold += q;
      counts.set(it.itemName, (counts.get(it.itemName) ?? 0) + q);
    }
  }
  const top = [...counts]
    .map(([name, quantity]) => ({ name, quantity }))
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 5);
  return { tips, tipped, itemsSold, top };
};

// Home "Sales today" / "Last 7 days" cards: just the time and total of every
// sale in the range (the controller buckets them by hour and by day).
export const fetchSalesTimes = async function (startISO, endISO) {
  const { data, error } = await supabase
    .from("sales")
    .select("sale_date, total_price")
    .eq("user_id", state.businessId)
    .gte("sale_date", startISO)
    .lte("sale_date", endISO)
    .is("voided_at", null);
  if (error) throw error;
  return (data ?? []).map((r) => ({ at: new Date(r.sale_date).getTime(), total: Number(r.total_price) || 0 }));
};

export const loadTransactionCounts = async function () {
  const [todayStart, todayEnd] = _bizDay(0);
  const [yesterdayStart, yesterdayEnd] = _bizDay(-1);

  const [todayRes, yestRes] = await Promise.all([
    supabase.from('sales').select('id', { count: 'exact', head: true })
      .eq('user_id', state.businessId)
      .gte('sale_date', todayStart.toISOString())
      .lte('sale_date', todayEnd.toISOString())
      .is('voided_at', null),
    supabase.from('sales').select('id', { count: 'exact', head: true })
      .eq('user_id', state.businessId)
      .gte('sale_date', yesterdayStart.toISOString())
      .lte('sale_date', yesterdayEnd.toISOString())
      .is('voided_at', null),
  ]);

  return { today: todayRes.count ?? 0, yesterday: yestRes.count ?? 0 };
};

// One-time "has this business ever recorded a sale?" check for the Getting
// Started checklist. Head-only count - no rows transferred. Errors report
// false so the step just stays unchecked and retries next load.
export const hasAnySale = async function () {
  const { count, error } = await supabase
    .from('sales')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', state.businessId);
  if (error) return false;
  return (count ?? 0) > 0;
};

// Records a completed sale via the server-side validating RPC (record_sale,
// migration-2026-08-14-server-side-sale-totals.sql). Item/variant prices,
// promo code validation + redemption, and loyalty point affordability are all
// recomputed/enforced INSIDE the RPC from real DB rows - the client only
// sends its selections (item ids, quantities, variant names, promo code text,
// redemption requests), never trusted dollar amounts. The RPC also performs
// the sales insert itself (the existing assign_receipt_number() trigger still
// fires normally) and, best-effort, writes the loyalty ledger. Returns the
// server-corrected sale fields so the caller can reconcile its local `sale`
// object before printing - what's stored is always what gets shown.
export const recordSale = async function (req) {
  const { data, error } = await supabase.rpc("record_sale", {
    p_items: req.items,
    p_adjustment_templates: req.adjustmentTemplates,
    p_manual_adjustments: req.manualAdjustments,
    p_promo_code: req.promoCode,
    p_loyalty_redeem_increments: req.loyaltyRedeemIncrements ?? 0,
    p_loyalty_rewards: req.loyaltyRewards,
    p_customer_id: req.customerId,
    p_order_type: req.orderType,
    p_payment_method: req.paymentMethod,
    p_ticket_number: req.ticketNumber,
    p_customer_payment: req.customerPayment,
    p_customer_change: req.customerChange,
    p_cashier_name: req.cashierName,
    p_logged_in_cashier: req.loggedInCashier,
    p_sale_date: new Date(req.date).toISOString(),
    p_tip_amount: req.tipAmount ?? 0,
    // Table fields (migration-2026-10-22-tables.sql) only travel when used, so
    // a sale without a table matches the older record_sale too.
    ...(req.tableId ? { p_table_id: req.tableId } : {}),
    ...(req.tableName ? { p_table_name: req.tableName } : {}),
    ...(req.tabId ? { p_tab_id: req.tabId } : {}),
    ...(req.tabLineIds ? { p_tab_line_ids: req.tabLineIds } : {}),
  });
  if (error) throw error;
  return {
    id: data.id,
    receiptNumber: data.receipt_number,
    subtotal: Number(data.subtotal),
    totalPrice: Number(data.total_price),
    taxInclusiveRate: data.tax_inclusive_rate,
    items: data.items,
    adjustments: data.adjustments,
    customerId: data.customer_id,
    loyaltyEarnedPoints: data.loyalty_earned_points,
    loyaltyRedeemedPoints: data.loyalty_redeemed_points,
    tipAmount: Number(data.tip_amount ?? 0),
    tableName: data.table_name ?? null,
  };
};

export const loadYesterdaySalesTotal = async function () {
  const [start, end] = _bizDay(-1);
  const { data, error } = await supabase
    .from("sales")
    .select("total_price")
    .eq("user_id", state.businessId)
    .gte("sale_date", start.toISOString())
    .lte("sale_date", end.toISOString())
    .is("voided_at", null);
  if (error) throw error;
  return data.reduce((sum, r) => sum + Number(r.total_price), 0);
};

// ── Cashflow ──────────────────────────────────────────────────────────────────

export const fetchCashflowData = async function (startISO, endISO) {
  const [salesResult, expensesResult, voidedResult] = await Promise.all([
    supabase
      .from("sales")
      .select("id, total_price, subtotal, customer_payment, customer_change, adjustments, sale_date, items, is_manual, added_by, order_type, payment_method, ticket_number, receipt_number, tax_inclusive_rate, prepared_at, timed_out, tip_amount")
      .eq("user_id", state.businessId)
      .gte("sale_date", startISO)
      .lte("sale_date", endISO)
      .is("voided_at", null)
      .order("sale_date", { ascending: false }),
    supabase
      .from("expenses")
      .select("*")
      .eq("user_id", state.businessId)
      .gte("expense_date", startISO)
      .lte("expense_date", endISO)
      .order("expense_date", { ascending: false }),
    supabase
      .from("sales")
      .select("id, total_price, subtotal, customer_payment, customer_change, adjustments, sale_date, items, added_by, order_type, payment_method, ticket_number, receipt_number, tax_inclusive_rate, voided_at, voided_by, tip_amount")
      .eq("user_id", state.businessId)
      .gte("sale_date", startISO)
      .lte("sale_date", endISO)
      .not("voided_at", "is", null)
      .order("voided_at", { ascending: false }),
  ]);
  if (salesResult.error) throw salesResult.error;
  if (expensesResult.error) throw expensesResult.error;
  if (voidedResult.error) throw voidedResult.error;
  state.cashflowSales = salesResult.data;
  state.expenses = expensesResult.data.map(dbToExpense);
  state.voidedSales = voidedResult.data;
};

// ── Home activity feed ────────────────────────────────────────────────────────
// Derives the "last N updates" from the tables that already record them - no
// activity_log table exists. Six small LIMIT queries run in parallel; each
// source degrades to [] on error (e.g. shifts RLS for a non-owner admin,
// un-migrated inventory tables) so one failure never blanks the feed.
// Low/out-of-stock entries need no query: they come from the already-loaded
// state.inventoryItems, timestamped with the item's updated_at (every stock
// RPC stamps it, so it approximates when the item hit its current level).
// Sale payloads carry the full row (same columns as fetchCashflowData) so a
// feed click can open the sale-receipt modal and reprint with no extra fetch.
// Writes NO state - the caller (controller) caches the returned events.

const ACTIVITY_SALE_COLUMNS = "id, total_price, subtotal, customer_payment, customer_change, adjustments, sale_date, items, is_manual, added_by, order_type, payment_method, ticket_number, receipt_number, tax_inclusive_rate, prepared_at, timed_out, voided_at, voided_by, tip_amount";

export const fetchActivityFeed = async function (limit = 15) {
  const safe = (query) => query.then((res) => (res.error ? [] : res.data ?? []), () => []);
  const [sales, voids, expenses, clockIns, clockOuts, adjustments, newCustomers, receivedPos] = await Promise.all([
    safe(supabase
      .from("sales")
      .select(ACTIVITY_SALE_COLUMNS)
      .eq("user_id", state.businessId)
      .order("sale_date", { ascending: false })
      .limit(limit)),
    safe(supabase
      .from("sales")
      .select(ACTIVITY_SALE_COLUMNS)
      .eq("user_id", state.businessId)
      .not("voided_at", "is", null)
      .order("voided_at", { ascending: false })
      .limit(limit)),
    safe(supabase
      .from("expenses")
      .select("*")
      .eq("user_id", state.businessId)
      .order("created_at", { ascending: false })
      .limit(limit)),
    // source: real time-clock punches only (NULL predates the column = clock).
    // 'manual' (admin-typed, can carry any time incl. future) and 'request'
    // (approved corrections/backfills) are bookkeeping, not "X timed in" events.
    safe(supabase
      .from("shifts")
      .select("id, clocked_in_at, clocked_out_at, staff:staff!shifts_staff_id_fkey(first_name, last_name)")
      .eq("business_id", state.businessId)
      .is("deleted_at", null)
      .or("source.is.null,source.eq.clock")
      .order("clocked_in_at", { ascending: false })
      .limit(limit)),
    safe(supabase
      .from("shifts")
      .select("id, clocked_out_at, staff:staff!shifts_staff_id_fkey(first_name, last_name)")
      .eq("business_id", state.businessId)
      .is("deleted_at", null)
      .or("source.is.null,source.eq.clock")
      .not("clocked_out_at", "is", null)
      .order("clocked_out_at", { ascending: false })
      .limit(limit)),
    // kind='manual' only - 'sale'/'void' rows would duplicate the sale/void events
    safe(supabase
      .from("inventory_adjustments")
      .select("id, delta, note, staff_name, created_at, inventory_item_id, inventory_items(name, unit)")
      .eq("business_id", state.businessId)
      .eq("kind", "manual")
      .order("created_at", { ascending: false })
      .limit(limit)),
    // New loyalty members. RLS note: a manage_business holder without a
    // loyalty capability just gets 0 rows here - the feed degrades gracefully.
    safe(supabase
      .from("customers")
      .select("id, name, phone, created_at")
      .eq("business_id", state.businessId)
      .order("created_at", { ascending: false })
      .limit(limit)),
    // Received purchase orders - ONE event per PO (the per-line kind='po'
    // adjustment rows deliberately don't feed here; a 10-line PO is one update).
    safe(supabase
      .from("purchase_orders")
      .select("id, po_number, supplier_name, received_at")
      .eq("business_id", state.businessId)
      .not("received_at", "is", null)
      .order("received_at", { ascending: false })
      .limit(limit)),
  ]);

  const shiftName = (r) =>
    `${r.staff?.first_name ?? ""} ${r.staff?.last_name ?? ""}`.trim() || "Staff";

  const events = [
    // A voided sale keeps its "recorded" event (query 1 has no void filter)
    // AND gains a "voided" event at voided_at - both are real updates.
    ...sales.map((row) => ({ id: `sale:${row.id}`, type: "sale", ts: row.sale_date, payload: row })),
    ...voids.map((row) => ({ id: `void:${row.id}`, type: "void", ts: row.voided_at, payload: row })),
    // created_at, not expense_date: a backdated expense entered just now IS a
    // recent update, and that's what a feed of "what happened" should show.
    ...expenses.map(dbToExpense).map((e) => ({ id: `exp:${e.id}`, type: "expense", ts: e.createdAt, payload: e })),
    ...clockIns.map((r) => ({ id: `in:${r.id}`, type: "clock_in", ts: r.clocked_in_at, payload: { staffName: shiftName(r) } })),
    ...clockOuts.map((r) => ({ id: `out:${r.id}`, type: "clock_out", ts: r.clocked_out_at, payload: { staffName: shiftName(r) } })),
    ...adjustments.map((row) => ({
      id: `adj:${row.id}`,
      type: "stock_adjust",
      ts: row.created_at,
      payload: {
        itemName: row.inventory_items?.name ?? "Inventory item",
        unit: row.inventory_items?.unit ?? "unit",
        delta: Number(row.delta),
        note: row.note ?? null,
        staffName: row.staff_name ?? null,
      },
    })),
    ...newCustomers.map((row) => ({
      id: `cust:${row.id}`,
      type: "customer_enrolled",
      ts: row.created_at,
      payload: { name: row.name ?? null, phone: row.phone },
    })),
    ...receivedPos.map((row) => ({
      id: `po:${row.id}`,
      type: "po_received",
      ts: row.received_at,
      payload: { poNumber: row.po_number, supplierName: row.supplier_name ?? null },
    })),
    // Same rules as productStockStatus: at/below zero = out; else at/below the
    // item's own alert threshold (null threshold = never alerts) = low.
    // Muted entirely while inventory tracking is off.
    ...(state.settings.inventoryEnabled === false ? [] : state.inventoryItems).flatMap((item) => {
      const base = { payload: { itemName: item.name, unit: item.unit, quantity: item.quantity }, ts: item.updatedAt ?? item.createdAt };
      if (item.quantity <= 0) return [{ id: `oos:${item.id}`, type: "out_of_stock", ...base }];
      if (item.lowStockThreshold != null && item.quantity <= item.lowStockThreshold)
        return [{ id: `low:${item.id}`, type: "low_stock", ...base }];
      return [];
    }),
  ];

  return events
    .filter((e) => e.ts)
    .sort((a, b) => new Date(b.ts) - new Date(a.ts))
    .slice(0, limit);
};

// `client` is the approving manager's temporary session when a void is authorized
// by override: the database only accepts a void from an account holding void_sale
// (sales_update_guard, 2026-10-08), so it must be written under the approver's login.
export const voidSale = async function (id, voidedBy, client = supabase) {
  const now = new Date().toISOString();
  const { error } = await client
    .from('sales')
    .update({ voided_at: now, voided_by: voidedBy })
    .eq('id', id)
    .eq('user_id', state.businessId);
  if (error) throw error;
  const wasInQueue = state.orderQueue.some(o => o.id === id);
  state.orderQueue = state.orderQueue.filter(o => o.id !== id);
  const voidedSale = state.cashflowSales.find(s => s.id === id);
  state.cashflowSales = state.cashflowSales.filter(s => s.id !== id);
  if (voidedSale) {
    state.voidedSales = [{ ...voidedSale, voided_at: now, voided_by: voidedBy }, ...state.voidedSales];
  }
  return { wasInQueue };
};



// A password re-auth on an isolated client lands at aal1. The database refuses aal1
// sessions for accounts with a verified authenticator (migration-2026-10-08-require-mfa),
// so those accounts must also pass their 6-digit code before the temp client can query.
// No code yet -> sign the temp client out and throw MFA_REQUIRED so the UI can ask for it.
const _elevateTempClient = async function (tempClient, mfaCode) {
  const { data: aal } = await tempClient.auth.mfa.getAuthenticatorAssuranceLevel();
  if (!(aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2')) return;
  const { data: factors } = await tempClient.auth.mfa.listFactors();
  const factorId = (factors?.all ?? []).find((f) => f.factor_type === 'totp' && f.status === 'verified')?.id;
  if (!factorId) return;
  if (!mfaCode) {
    await tempClient.auth.signOut({ scope: 'local' });
    const err = new Error('Enter the 6-digit code from the authenticator app for this account.');
    err.code = 'MFA_REQUIRED';
    throw err;
  }
  const { error } = await tempClient.auth.mfa.challengeAndVerify({ factorId, code: mfaCode });
  if (error) {
    await tempClient.auth.signOut({ scope: 'local' });
    const err = new Error('Incorrect or expired code. Try again.');
    err.code = 'MFA_REQUIRED';
    throw err;
  }
};

// `whileSignedIn(tempClient, approverName)`, if given, runs with the approver's
// session before it is signed out - for writes the database only accepts from an
// account holding the capability (e.g. a void authorized by override).
export const verifyOverrideCredentials = async function (email, password, requiredCap = 'void_sale', captchaToken, mfaCode, whileSignedIn) {
  const tempClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
  // CAPTCHA enforcement in Supabase is project-wide, so this re-auth signInWithPassword
  // needs a token too. Without it the call fails - which we must NOT mislabel as a bad
  // password, or a manager will retype correct credentials forever.
  const { data: authData, error: authError } = await tempClient.auth.signInWithPassword({
    email,
    password,
    options: { captchaToken },
  });
  if (authError) {
    if (/captcha/i.test(authError.message || ''))
      throw new Error('Verification check failed. Please try again.');
    throw new Error('Invalid email or password.');
  }
  await _elevateTempClient(tempClient, mfaCode);

  try {
    const { data: staffData } = await tempClient
      .from('staff')
      .select('first_name, last_name, roles(name, permissions)')
      .eq('user_id', authData.user.id)
      .eq('business_id', state.businessId)
      .eq('is_active', true)
      .maybeSingle();

    if (!staffData) throw new Error('This account is not a staff member at this location.');
    // The overriding account must itself be allowed to void (the owner always can).
    const isOwnerOverride = authData.user.id === state.businessId;
    const caps = capabilitySetFromRole(staffData.roles?.name, staffData.roles?.permissions);
    if (!isOwnerOverride && !caps.has(requiredCap)) throw new Error('This account does not have permission to authorize this action.');

    const approver = [staffData.first_name, staffData.last_name].filter(Boolean).join(' ') || email;
    if (whileSignedIn) await whileSignedIn(tempClient, approver);
    return approver;
  } finally {
    await tempClient.auth.signOut({ scope: 'local' });
  }
};

export const fetchReportsSalesRaw = async function (startISO, endISO) {
  const { data, error } = await supabase
    .from("sales")
    .select("id, total_price, sale_date, items, added_by, order_type, prepared_at, ready_at, payment_method, tip_amount")
    .eq("user_id", state.businessId)
    .gte("sale_date", startISO)
    .lte("sale_date", endISO)
    .is("voided_at", null)
    .order("sale_date", { ascending: true });
  if (error) throw error;
  return data ?? [];
};

export const fetchReportsSales = async function (startISO, endISO) {
  state.reportsSales = await fetchReportsSalesRaw(startISO, endISO);
};

export const fetchPeriodTotals = async function (startISO, endISO) {
  const { data, error } = await supabase
    .from("sales")
    .select("total_price, sale_date, prepared_at, ticket_number")
    .eq("user_id", state.businessId)
    .gte("sale_date", startISO)
    .lte("sale_date", endISO)
    .is("voided_at", null);
  if (error) throw error;
  const revenue = data.reduce((s, r) => s + Number(r.total_price), 0);
  const transactions = data.length;
  let servTotal = 0, servCount = 0;
  // Extra queue stats for the Active Queue's empty state ("Today so far").
  let completed = 0, longest = null, lastDoneAt = null;
  const servingMinutes = [];
  const perHour = new Map();
  for (const r of data) {
    const hr = new Date(r.sale_date).getHours();
    perHour.set(hr, (perHour.get(hr) ?? 0) + 1);
    if (!r.prepared_at) continue;
    completed++;
    const doneAt = new Date(r.prepared_at).getTime();
    if (lastDoneAt === null || doneAt > lastDoneAt) lastDoneAt = doneAt;
    const mins = (doneAt - new Date(r.sale_date)) / 60000;
    if (mins < 0) continue;
    // Every done order counts toward the on time / slow / delayed split;
    // only the averages and longest wait drop the >2h outliers.
    servingMinutes.push(mins);
    if (mins > 120) continue;
    servTotal += mins; servCount++;
    if (!longest || mins > longest.minutes) longest = { minutes: mins, ticketNumber: r.ticket_number ?? null };
  }
  let busiestHour = null;
  perHour.forEach((n, hr) => { if (n > 1 && (!busiestHour || n > busiestHour.count)) busiestHour = { hour: hr, count: n }; });
  return {
    revenue,
    transactions,
    avgOrder: transactions > 0 ? revenue / transactions : 0,
    avgServingMinutes: servCount > 0 ? servTotal / servCount : null,
    completed,
    longest,
    lastDoneAt,
    busiestHour,
    servingMinutes,
  };
};

// Lightweight expenses-only total for a period - used alongside
// fetchPeriodTotals to compute the PREVIOUS period's Net Income for the
// Reports Overview "vs last period" delta, without pulling the full sales/
// voided rows fetchCashflowData does (and without touching state.expenses,
// which fetchCashflowData overwrites for the CURRENT period only).
export const fetchExpensesTotal = async function (startISO, endISO) {
  const { data, error } = await supabase
    .from("expenses")
    .select("amount")
    .eq("user_id", state.businessId)
    .gte("expense_date", startISO)
    .lte("expense_date", endISO);
  if (error) throw error;
  return data.reduce((s, e) => s + Number(e.amount), 0);
};

export const addExpense = async function ({ amount, description, category, expense_date }) {
  const { data, error } = await supabase
    .from("expenses")
    .insert({
      user_id: state.businessId,
      store_name: state.username,
      amount: Number(amount),
      description,
      category: category || null,
      expense_date,
      created_by: state.username,
    })
    .select()
    .single();
  if (error) throw error;
  const expense = dbToExpense(data);
  state.expenses.unshift(expense);
  return expense;
};

export const deleteExpense = async function (id) {
  const { error } = await supabase
    .from("expenses")
    .delete()
    .eq("id", id)
    .eq("user_id", state.businessId);
  if (error) throw error;
  const index = state.expenses.findIndex((e) => e.id === id);
  if (index !== -1) state.expenses.splice(index, 1);
};

// ── Discount Codes ────────────────────────────────────────────────────────────

const dbToDiscountCode = (row) => ({
  id: row.id,
  title: row.title,
  code: row.code,
  description: row.description ?? "",
  type: row.type,
  value: Number(row.value),
  usageLimit: row.usage_limit,
  usageCount: row.usage_count,
  status: row.status,
  createdAt: row.created_at,
});

export const loadDiscountCodes = async function () {
  const { data, error } = await supabase
    .from("discount_codes")
    .select("*")
    .eq("user_id", state.businessId)
    .order("created_at");
  if (error) {
    state.discountCodes = [];
    return;
  }
  state.discountCodes = data.map(dbToDiscountCode);
};

export const createDiscountCode = async function (data) {
  const code = data.code.toUpperCase().trim();
  const { data: row, error } = await supabase
    .from("discount_codes")
    .insert({
      user_id: state.businessId,
      title: data.title,
      code,
      description: data.description || null,
      type: data.type,
      value: Number(data.value),
      usage_limit: data.usageLimit ?? null,
      usage_count: 0,
      status: "active",
    })
    .select()
    .single();
  if (error) throw error;
  const dc = dbToDiscountCode(row);
  state.discountCodes.push(dc);
  return dc;
};

export const updateDiscountCode = async function (id, data) {
  const code = data.code.toUpperCase().trim();
  const { error } = await supabase
    .from("discount_codes")
    .update({
      title: data.title,
      code,
      description: data.description || null,
      type: data.type,
      value: Number(data.value),
      usage_limit: data.usageLimit ?? null,
    })
    .eq("id", id)
    .eq("user_id", state.businessId);
  if (error) throw error;
  const dc = state.discountCodes.find((d) => d.id === id);
  if (dc) {
    dc.title = data.title;
    dc.code = code;
    dc.description = data.description || "";
    dc.type = data.type;
    dc.value = Number(data.value);
    dc.usageLimit = data.usageLimit ?? null;
  }
};

export const deleteDiscountCode = async function (id) {
  const { error } = await supabase
    .from("discount_codes")
    .delete()
    .eq("id", id)
    .eq("user_id", state.businessId);
  if (error) throw error;
  const index = state.discountCodes.findIndex((d) => d.id === id);
  if (index !== -1) state.discountCodes.splice(index, 1);
};

export const toggleDiscountCodeStatus = async function (id) {
  const dc = state.discountCodes.find((d) => d.id === id);
  if (!dc) throw new Error("Code not found");
  const newStatus = dc.status === "active" ? "paused" : "active";
  const { error } = await supabase
    .from("discount_codes")
    .update({ status: newStatus })
    .eq("id", id)
    .eq("user_id", state.businessId);
  if (error) throw error;
  dc.status = newStatus;
};

export const validateDiscountCode = function (code) {
  const normalized = code.toUpperCase().trim();
  const dc = state.discountCodes.find((d) => d.code === normalized);
  if (!dc) throw new Error("Invalid promo code");
  if (dc.status !== "active") throw new Error("This code is currently inactive");
  if (dc.usageLimit !== null && dc.usageCount >= dc.usageLimit)
    throw new Error("This code has reached its usage limit");
  return dc;
};

export const applyPromoCodeToReceipt = function (dc) {
  const adj = {
    id: generateAdjustmentId(),
    name: `Promo (${dc.code})`,
    type: "discount",
    calculation: dc.type === "percentage" ? "percentage" : "fixed",
    value: dc.value,
    appliedValue: dc.value,
    removed: false,
    source: "promo-code",
  };
  state.currentReceiptAdjustments.push(adj);
  state.currentPromoCode = {
    discountCodeId: dc.id,
    adjId: adj.id,
    code: dc.code,
    title: dc.title,
    type: dc.type,
    value: dc.value,
  };
  return adj;
};

export const removePromoCodeFromReceipt = function () {
  if (!state.currentPromoCode) return;
  const { adjId } = state.currentPromoCode;
  const index = state.currentReceiptAdjustments.findIndex((a) => a.id === adjId);
  if (index !== -1) state.currentReceiptAdjustments.splice(index, 1);
  state.currentPromoCode = null;
};

// ── Loyalty program ───────────────────────────────────────────────────────────
// Customers are identified by phone (digits-only, unique per business). Points
// balance is denormalized on customers; the loyalty_transactions ledger (RPC-
// written only) is the audit truth. A redemption is just a fixed discount line
// in currentReceiptAdjustments (source: "loyalty"), so calculateAdjustments,
// the receipt, and the tax-inclusive split all handle it with zero changes.

const dbToCustomer = (row) => ({
  id: row.id,
  phone: row.phone,
  name: row.name ?? null,
  email: row.email ?? null,
  birthdayMonth: row.birthday_month ?? null,
  birthdayDay: row.birthday_day ?? null,
  note: row.note ?? null,
  pointsBalance: row.points_balance ?? 0,
  visitCount: row.visit_count ?? 0,
  lastVisitAt: row.last_visit_at ?? null,
  createdAt: row.created_at ?? null,
});

// Normalize the optional profile fields shared by enroll + update. Empty
// strings become NULL so the DB stays clean.
const _customerExtras = function (extras = {}) {
  const out = {};
  if ('email' in extras) out.email = (extras.email ?? '').trim() || null;
  if ('note' in extras) out.note = (extras.note ?? '').trim() || null;
  if ('birthdayMonth' in extras || 'birthdayDay' in extras) {
    const m = parseInt(extras.birthdayMonth);
    const d = parseInt(extras.birthdayDay);
    // Birthday is stored only when BOTH parts are valid (a month without a
    // day, or vice versa, is meaningless).
    const valid = m >= 1 && m <= 12 && d >= 1 && d <= 31;
    out.birthday_month = valid ? m : null;
    out.birthday_day = valid ? d : null;
  }
  return out;
};

// Look up a customer by phone for the checkout flow. Returns the customer (and
// stamps state.currentLoyaltyCustomer, including their non-voided sale dates
// for the streak badge) or null when no customer with that phone exists.
export const lookupCustomer = async function (rawPhone) {
  const phone = normalizePhone(rawPhone);
  if (!phone) throw new Error("Please enter a valid phone number");
  const { data, error } = await supabase
    .from("customers")
    .select("*")
    .eq("business_id", state.businessId)
    .eq("phone", phone)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const customer = dbToCustomer(data);
  const { data: saleRows, error: salesError } = await supabase
    .from("sales")
    .select("sale_date")
    .eq("customer_id", customer.id)
    .is("voided_at", null);
  if (salesError) throw salesError;
  customer.saleDates = (saleRows ?? []).map((r) => r.sale_date);
  state.currentLoyaltyCustomer = customer;
  return customer;
};

// Enroll a new customer (checkout or the Loyalty panel). If another terminal
// enrolled the same phone first (23505 unique violation), fall back to lookup
// - EXCEPT when strict is true (the panel's dedicated Add Customer form),
// where a duplicate phone must surface as an error instead of silently
// resolving to the existing member (that used to show a false "success").
// extras (panel only): { email, birthdayMonth, birthdayDay, note } - all optional.
export const enrollCustomer = async function (rawPhone, name, extras = {}, { strict = false } = {}) {
  const phone = normalizePhone(rawPhone);
  if (!phone) throw new Error("Please enter a valid phone number");
  const cleanName = (name ?? "").trim() || null;
  const { data, error } = await supabase
    .from("customers")
    .insert({ business_id: state.businessId, phone, name: cleanName, ..._customerExtras(extras) })
    .select("*")
    .single();
  if (error) {
    if (error.code === "23505") {
      if (strict) throw new Error("A customer with this phone number already exists.");
      return await lookupCustomer(phone);
    }
    throw error;
  }
  const customer = dbToCustomer(data);
  customer.saleDates = [];
  state.currentLoyaltyCustomer = customer;
  return customer;
};

// Update a customer's profile fields (Loyalty panel edit; RLS requires
// manage_loyalty). Phone is deliberately NOT editable here - changing the
// identity key is a v2 concern (merge/dedupe questions).
export const updateCustomer = async function (id, { name, ...extras }) {
  const fields = { name: (name ?? "").trim() || null, ..._customerExtras(extras) };
  const { data, error } = await supabase
    .from("customers")
    .update(fields)
    .eq("id", id)
    .eq("business_id", state.businessId)
    .select("*")
    .single();
  if (error) throw error;
  return dbToCustomer(data);
};

export const clearLoyaltyCustomer = function () {
  removeLoyaltyRedemptionFromReceipt();
  // Strip any free-item reward redemptions too - they're only meaningful
  // while a customer is linked (unlike removeLoyaltyRedemptionFromReceipt,
  // there can be several, one per redemption). Each one minted its own cart
  // line (2026-07-27) - those come out too, otherwise the "free" item
  // reverts to full price the instant its comping discount disappears.
  const rewardLineIds = new Set(
    state.currentReceiptAdjustments
      .filter((a) => a.source === "loyalty_reward")
      .map((a) => a.cartItemLineId),
  );
  state.currentReceiptAdjustments = state.currentReceiptAdjustments.filter(
    (a) => a.source !== "loyalty_reward",
  );
  state.cart = state.cart.filter((it) => !rewardLineIds.has(it.lineId));
  state.currentLoyaltyCustomer = null;
};

// Apply a points redemption as a fixed discount line. increments = how many
// blocks of loyaltyRedeemPoints to spend. Validated against the linked
// customer's balance; the per-order cap is enforced by the caller (controller)
// because it depends on the current running total.
export const applyLoyaltyRedemption = function (increments) {
  const customer = state.currentLoyaltyCustomer;
  if (!customer) throw new Error("No loyalty customer linked");
  const n = Math.floor(Number(increments) || 0);
  if (n < 1) throw new Error("Nothing to redeem");
  const points = n * state.settings.loyaltyRedeemPoints;
  // Subtract only reward-reserved points here - removeLoyaltyRedemptionFromReceipt()
  // runs next, so any EXISTING $-off redemption is about to be replaced, not
  // double-counted against itself.
  const rewardReserved = state.currentReceiptAdjustments
    .filter((a) => !a.removed && a.source === "loyalty_reward")
    .reduce((sum, a) => sum + (a.points || 0), 0);
  if (points > customer.pointsBalance - rewardReserved)
    throw new Error("Not enough points for that redemption");
  removeLoyaltyRedemptionFromReceipt();
  const value = n * state.settings.loyaltyRedeemValue;
  const adj = {
    id: generateAdjustmentId(),
    name: `Loyalty reward (${points} pts)`,
    type: "discount",
    calculation: "fixed",
    value,
    appliedValue: value,
    removed: false,
    source: "loyalty",
  };
  state.currentReceiptAdjustments.push(adj);
  // `increments` rides along so the checkout commit can hand the RPC a plain
  // count (validated/recomputed server-side from real settings) instead of
  // back-deriving it from `points`/`value`, which would be fragile if
  // loyaltyRedeemPoints/Value changed mid-checkout.
  state.currentLoyaltyRedemption = { adjId: adj.id, points, value, increments: n };
  return adj;
};

export const removeLoyaltyRedemptionFromReceipt = function () {
  if (!state.currentLoyaltyRedemption) return;
  const { adjId } = state.currentLoyaltyRedemption;
  const index = state.currentReceiptAdjustments.findIndex((a) => a.id === adjId);
  if (index !== -1) state.currentReceiptAdjustments.splice(index, 1);
  state.currentLoyaltyRedemption = null;
};

// Record a completed sale's earn + redeem via the SECURITY DEFINER RPC.
// Atomic + idempotent server-side; callers fire-and-forget (never fail a
// committed sale over a loyalty write).
export const recordLoyaltyForSale = async function (saleId, customerId, earnPoints, redeemPoints) {
  const { error } = await supabase.rpc("record_loyalty_for_sale", {
    p_sale_id: saleId,
    p_customer_id: customerId,
    p_earn_points: earnPoints,
    p_redeem_points: redeemPoints,
  });
  if (error) throw error;
};

// Reverse a voided sale's points. Server-side no-op when the sale had no
// loyalty rows, so the void path calls it unconditionally.
export const reverseLoyaltyForSale = async function (saleId) {
  const { error } = await supabase.rpc("record_loyalty_void", { p_sale_id: saleId });
  if (error) throw error;
};

// Full customer list for the Loyalty panel, newest first.
export const fetchLoyaltyCustomers = async function () {
  const { data, error } = await supabase
    .from("customers")
    .select("*")
    .eq("business_id", state.businessId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(dbToCustomer);
};

// All non-voided sale dates that carry a customer_id, grouped per customer -
// one query for the whole panel list's streak math. Revisit with a SQL
// aggregate if a business accumulates tens of thousands of loyalty sales (v2).
export const fetchLoyaltySaleDates = async function () {
  const { data, error } = await supabase
    .from("sales")
    .select("customer_id, sale_date")
    .eq("user_id", state.businessId)
    .not("customer_id", "is", null)
    .is("voided_at", null);
  if (error) throw error;
  const byCustomer = new Map();
  for (const row of data ?? []) {
    if (!byCustomer.has(row.customer_id)) byCustomer.set(row.customer_id, []);
    byCustomer.get(row.customer_id).push(row.sale_date);
  }
  return byCustomer;
};

// Points ledger for the customer detail view, newest first. 500 covers the
// all-time-points sum for any realistic v1 customer; a SQL aggregate is the
// v2 answer if a member ever exceeds it.
export const fetchLoyaltyLedger = async function (customerId) {
  const { data, error } = await supabase
    .from("loyalty_transactions")
    .select("id, sale_id, kind, points, created_at")
    .eq("customer_id", customerId)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  return data ?? [];
};

// ── Free-item reward catalog ─────────────────────────────────────────────────
// Owner-configured "spend N points, get this menu item free." Loaded eagerly
// at bootstrap (alongside inventory) since checkout needs it regardless of
// whether the current user can ever open the Loyalty panel (manage_loyalty-
// gated) - checkout only needs use_loyalty, which the RLS select policy allows.

const dbToReward = (row) => ({
  id: row.id,
  itemId: row.item_id,
  pointsCost: row.points_cost,
  active: row.active,
});

export const fetchLoyaltyRewards = async function () {
  const { data, error } = await supabase
    .from("loyalty_rewards")
    .select("*")
    .eq("business_id", state.businessId)
    .order("created_at");
  if (error) throw error;
  state.loyaltyRewards = data.map(dbToReward);
};

export const addLoyaltyReward = async function (itemId, pointsCost) {
  const { data: row, error } = await supabase
    .from("loyalty_rewards")
    .insert({
      business_id: state.businessId,
      item_id: itemId,
      points_cost: Math.max(1, Math.floor(Number(pointsCost) || 0)),
      active: true,
    })
    .select()
    .single();
  if (error) throw error;
  const reward = dbToReward(row);
  state.loyaltyRewards.push(reward);
  return reward;
};

export const toggleLoyaltyReward = async function (id) {
  const reward = state.loyaltyRewards.find((r) => r.id === id);
  if (!reward) throw new Error("Reward not found");
  const newActive = !reward.active;
  const { error } = await supabase
    .from("loyalty_rewards")
    .update({ active: newActive })
    .eq("id", id)
    .eq("business_id", state.businessId);
  if (error) throw error;
  reward.active = newActive;
};

export const deleteLoyaltyReward = async function (id) {
  const { error } = await supabase
    .from("loyalty_rewards")
    .delete()
    .eq("id", id)
    .eq("business_id", state.businessId);
  if (error) throw error;
  const index = state.loyaltyRewards.findIndex((r) => r.id === id);
  if (index !== -1) state.loyaltyRewards.splice(index, 1);
};

// Total points already spoken for in the CURRENT checkout - the $-off
// redemption plus every active free-item reward redemption. Derived from
// currentReceiptAdjustments (the single source of truth for what's actually
// applied) rather than tracked in a second array, so there's no way for a
// reward's "redeemed" bookkeeping to drift from its actual discount line.
export const loyaltyReservedPoints = function () {
  const dollarOff = state.currentLoyaltyRedemption?.points ?? 0;
  const rewardPts = state.currentReceiptAdjustments
    .filter((a) => !a.removed && a.source === "loyalty_reward")
    .reduce((sum, a) => sum + (a.points || 0), 0);
  return dollarOff + rewardPts;
};

// Redeem points for a free unit of a specific reward (keyed by rewardId, not
// a pre-existing cart line - 2026-07-27 rework). Mints a NEW cart line (qty 1,
// no variants) regardless of whether the customer is already buying that item
// elsewhere in the cart, so "redeem a free item" always works from the
// catalog rather than requiring the cashier to ring it up first. Comps the
// item's CURRENT price as a fixed discount line - same mechanism as the
// $-off redemption, so calculateAdjustments/receipt/tax split need no
// changes. The new line is a real cart item, so it deducts stock and shows
// on the kitchen ticket like anything else. One redemption per reward per sale.
export const applyLoyaltyRewardRedemption = function (rewardId) {
  const customer = state.currentLoyaltyCustomer;
  if (!customer) throw new Error("No loyalty customer linked");
  const reward = state.loyaltyRewards.find((r) => r.id === rewardId && r.active);
  if (!reward) throw new Error("This reward is no longer available");
  if (state.currentReceiptAdjustments.some(
    (a) => a.source === "loyalty_reward" && a.rewardId === rewardId && !a.removed,
  )) throw new Error("Already redeemed this reward");
  if (reward.pointsCost > customer.pointsBalance - loyaltyReservedPoints())
    throw new Error("Not enough points for that redemption");
  const item = state.menuItems.find((i) => i._id === reward.itemId);
  if (!item) throw new Error("This item is no longer available");

  const cartItemLineId = crypto.randomUUID();
  const cartItem = {
    itemName: item.itemName,
    price: item.price,
    imageURL: item.imageURL,
    selectedVariants: [],
    id: item._id,
    lineId: cartItemLineId,
    date: Date.now(),
    quantity: 1,
    totalPrice: item.price,
  };
  state.cart.push(cartItem);

  const adj = {
    id: generateAdjustmentId(),
    name: `Free ${item.itemName} (Reward)`,
    type: "discount",
    calculation: "fixed",
    value: item.price,
    appliedValue: item.price,
    removed: false,
    source: "loyalty_reward",
    points: reward.pointsCost,
    cartItemLineId,
    rewardId: reward.id,
  };
  state.currentReceiptAdjustments.push(adj);
  return { cartItem, adj };
};

// cartItemLineId (not rewardId) since a cart-delete can also trigger this -
// see controlDeleteCartItemInCheckout, which already has the line id handy.
export const removeLoyaltyRewardRedemption = function (cartItemLineId) {
  const index = state.currentReceiptAdjustments.findIndex(
    (a) => a.source === "loyalty_reward" && a.cartItemLineId === cartItemLineId,
  );
  if (index !== -1) state.currentReceiptAdjustments.splice(index, 1);
  // The redemption minted its own cart line - remove it too so a removed
  // redemption doesn't leave a stray line behind at full price. No-op if the
  // cart line is already gone (e.g. the caller deleted it before this ran).
  const cartIndex = state.cart.findIndex((it) => it.lineId === cartItemLineId);
  if (cartIndex !== -1) state.cart.splice(cartIndex, 1);
};

// ── Business-level settings (shared across all of a business's devices) ─────────
// These live in businesses.settings (JSONB) so a laptop and an iPad see the same
// config. localStorage is kept as a per-device cache/fallback (and for the popups
// + offline mode). Device-specific settings (terminal number, theme, window sizes)
// deliberately stay in localStorage only and are NOT listed here.
//
// Each entry maps a state.settings key → its localStorage cache key, how to parse
// the cached string back when hydrating from localStorage, and the DEFAULT the
// key takes when the business's server settings object doesn't contain it.
const BUSINESS_SETTINGS = {
  printingEnabled:          { ls: 'pointbunny_printing_enabled',  parse: (v) => v !== 'false', def: true },
  confirmPrint:             { ls: 'pointbunny_confirm_print',      parse: (v) => v !== 'false', def: true },
  showCashierName:          { ls: 'pointbunny_show_cashier',       parse: (v) => v !== 'false', def: true },
  showInvoiceNumber:        { ls: 'pointbunny_show_invoice_number',parse: (v) => v !== 'false', def: true },
  showTicketNumber:         { ls: 'pointbunny_show_ticket_number', parse: (v) => v !== 'false', def: true },
  showBusinessPhone:        { ls: 'pointbunny_show_business_phone', parse: (v) => v !== 'false', def: true },
  // Custom line at the bottom of every receipt (manage_business only, max 120
  // chars - set_business_settings enforces both). Empty = "Thank you...".
  receiptFooter:            { ls: 'pointbunny_receipt_footer',    parse: (v) => v || '',      def: '' },
  // Feedback/review link printed as a QR under the footer (https, max 300).
  // Footer + QR only print on paid plans (receiptView / escpos check isFreePlan).
  receiptQrUrl:             { ls: 'pointbunny_receipt_qr_url',    parse: (v) => v || '',      def: '' },
  taxMode:                  { ls: 'pointbunny_tax_mode',           parse: (v) => (v === 'exclusive' || v === 'inclusive' ? v : 'none'), def: 'none' },
  taxInclusive:             { ls: 'pointbunny_tax_inclusive',      parse: (v) => v === 'true',  def: false },
  taxInclusiveRate:         { ls: 'pointbunny_tax_inclusive_rate', parse: (v) => parseFloat(v) || 12, def: 12 },
  taxInclusiveLabel:        { ls: 'pointbunny_tax_label',          parse: (v) => v || null,     def: null },
  taxExemptLabel:           { ls: 'pointbunny_tax_exempt_label',   parse: (v) => v || null,     def: null },
  orderTypeEnabled:         { ls: 'pointbunny_order_type_enabled', parse: (v) => v !== 'false', def: true },
  kdsYellowThreshold:       { ls: 'pointbunny_kds_yellow',         parse: (v) => parseInt(v), def: 600 },
  kdsRedThreshold:          { ls: 'pointbunny_kds_red',            parse: (v) => parseInt(v), def: 900 },
  kdsAutoCompleteThreshold: { ls: 'pointbunny_kds_auto',           parse: (v) => parseInt(v), def: 3600 },
  cfdAdUrl:                 { ls: 'pointbunny_cfd_ad',             parse: (v) => v || null,     def: null },
  cfdQrUrl:                 { ls: 'pointbunny_cfd_qr',             parse: (v) => v || null,     def: null },
  pickupAdUrl:              { ls: 'pointbunny_pickup_ad',          parse: (v) => v || null,     def: null },
  // Tips (2026-10-10, owner / manage_business only - set_business_settings).
  // tipPresets = 1-3 whole percentages; tipQrUrl = optional Venmo/Cash App QR
  // image shown next to the tip buttons on the customer display (those tips
  // are never recorded in Pointbunny).
  tipsEnabled:              { ls: 'pointbunny_tips_enabled',       parse: (v) => v === 'true',  def: false },
  tipPresets:               { ls: 'pointbunny_tip_presets',        parse: (v) => parseTipPresets(v), def: DEFAULT_TIP_PRESETS },
  tipQrUrl:                 { ls: 'pointbunny_tip_qr',             parse: (v) => v || null,     def: null },
  // Roles that share the tip pool (manage_pay). null = every role except
  // Admin; [] = nobody. Cached as "id,id" ("" for an empty list).
  tipPoolRoleIds:           { ls: 'pointbunny_tip_pool_roles',     parse: (v) => (v == null ? null : v === '' ? [] : v.split(',')), def: null },
  hideStatsFromStaff:       { ls: 'pointbunny_hide_stats_staff',   parse: (v) => v === 'true',  def: false },
  gettingStartedDismissed:  { ls: 'pointbunny_checklist_dismissed', parse: (v) => v === 'true', def: false },
  gettingStartedCompleted:  { ls: 'pointbunny_checklist_completed', parse: (v) => v === 'true', def: false },
  receiptSetupSeen:         { ls: 'pointbunny_receipt_setup_seen',  parse: (v) => v === 'true', def: false },
  taxSetupSeen:             { ls: 'pointbunny_tax_setup_seen',      parse: (v) => v === 'true', def: false },
  teamSetupSeen:            { ls: 'pointbunny_team_setup_seen',     parse: (v) => v === 'true', def: false },
  inventorySetupSeen:       { ls: 'pointbunny_inventory_setup_seen', parse: (v) => v === 'true', def: false },
  loyaltyEnabled:           { ls: 'pointbunny_loyalty_enabled',      parse: (v) => v === 'true', def: false },
  loyaltyEarnRate:          { ls: 'pointbunny_loyalty_earn_rate',    parse: (v) => parseFloat(v) || 0.1, def: 0.1 },
  loyaltyEarnAmount:        { ls: 'pointbunny_loyalty_earn_amount',  parse: (v) => parseFloat(v) || 1, def: 1 },
  loyaltyRedeemPoints:      { ls: 'pointbunny_loyalty_redeem_points', parse: (v) => parseInt(v) || 100, def: 100 },
  loyaltyRedeemValue:       { ls: 'pointbunny_loyalty_redeem_value', parse: (v) => parseFloat(v) || 5, def: 5 },
  inventoryEnabled:         { ls: 'pointbunny_inventory_enabled',    parse: (v) => v !== 'false', def: true },
  // Home screen (2026-10-22, homeLayout.js): the type of place, the shortcuts
  // left off the Manage / Screens menus, and table service. Every feature stays
  // available; these only pick what shows. homeHidden is cached as "a,b".
  placeType:                { ls: 'pointbunny_place_type',           parse: (v) => v || null, def: null },
  homeHidden:               { ls: 'pointbunny_home_hidden',          parse: (v) => parseHomeHidden(v), clean: parseHomeHidden, def: [] },
  tableService:             { ls: 'pointbunny_table_service',        parse: (v) => parseTableService(v), clean: parseTableService, def: 'off' },
  waitlistEnabled:          { ls: 'pointbunny_waitlist_enabled',     parse: (v) => v === 'true', def: false },
  reservationsEnabled:      { ls: 'pointbunny_reservations_enabled', parse: (v) => v === 'true', def: false },
  // Booking hours / slot length / table hold, and the waitlist quote buttons
  // (2026-10-24, bookingRules.js). bookingRules is cached as JSON.
  bookingRules:             { ls: 'pointbunny_booking_rules',        parse: (v) => parseBookingRules(v), clean: parseBookingRules, ser: (v) => JSON.stringify(v), def: DEFAULT_BOOKING_RULES },
  waitlistQuotes:           { ls: 'pointbunny_waitlist_quotes',      parse: (v) => parseWaitlistQuotes(v), clean: parseWaitlistQuotes, def: DEFAULT_WAITLIST_QUOTES },
  // loyaltyStreakUnit was removed 2026-07-17: streaks now show daily, weekly,
  // AND monthly side by side, so there is nothing to configure. A leftover key
  // in businesses.settings is harmless (unknown keys are ignored).
};

// Write one business setting to its localStorage cache (null/undefined → remove).
const _cacheBusinessSetting = function (key, value) {
  const ls = BUSINESS_SETTINGS[key]?.ls;
  if (!ls) return;
  if (value === null || value === undefined) localStorage.removeItem(ls);
  else localStorage.setItem(ls, BUSINESS_SETTINGS[key].ser ? BUSINESS_SETTINGS[key].ser(value) : String(value));
};

// Merge a settings object (from businesses.settings) onto state.settings, applying
// only known business keys and caching each to localStorage. Used at login and by
// the popups. Unknown/absent keys are left at their current (default) value.
// SERVER-AUTHORITATIVE (2026-07-06): with the business's settings object in
// hand, a key absent from it means "this business never set it" - apply the
// key's DEFAULT, not whatever this device's cache holds. The old merge left
// absent keys at their cached value, which leaked one account's settings into
// another account on the same browser (a brand-new US business inheriting
// taxInclusive=true and a dismissed Getting Started card from a test account),
// and the next settings save then PERSISTED the leak onto the new business.
// The localStorage cache is only the pre-login/offline fallback now, and gets
// re-stamped from server truth here on every login. Callers must pass `{}`
// (not skip the call) for a business whose settings column is still NULL.
export const applyBusinessSettings = function (obj) {
  if (!obj || typeof obj !== 'object') return;
  for (const key of Object.keys(BUSINESS_SETTINGS)) {
    const raw = obj[key] !== undefined ? obj[key] : BUSINESS_SETTINGS[key].def;
    const val = BUSINESS_SETTINGS[key].clean ? BUSINESS_SETTINGS[key].clean(raw) : raw;
    state.settings[key] = val;
    _cacheBusinessSetting(key, val);
  }
};

// Snapshot the business-level settings from state into a plain object.
const _collectBusinessSettings = function () {
  const obj = {};
  for (const key of Object.keys(BUSINESS_SETTINGS)) obj[key] = state.settings[key];
  return obj;
};

// Real values for settings a tutorial is temporarily faking in state.settings
// (the Inventory practice turns inventoryEnabled on in memory). Every save
// sends the WHOLE settings object, so without this any unrelated save during a
// practice run - a "setup seen" flag, a Settings toggle - would persist the
// pretend value for the real business. Set/cleared by the controller.
let _persistOverrides = null;
export const setSettingsPersistOverrides = function (overrides) {
  _persistOverrides = overrides;
};

// Persist the whole business-settings object to businesses.settings. Goes through a
// SECURITY DEFINER RPC (set_business_settings) rather than a direct UPDATE: business
// settings can be changed by any role that can open Settings, but RLS only lets the
// OWNER update the businesses row directly - the RPC checks membership and writes the
// settings column for owner OR active staff. Fire-and-forget; logs (never throws) so a
// settings save can't block the UI.
export const persistBusinessSettings = async function () {
  if (!state.businessId) return;
  const { error } = await supabase.rpc('set_business_settings', {
    p_settings: { ..._collectBusinessSettings(), ...(_persistOverrides ?? {}) },
  });
  if (error) console.error('[pointbunny] failed to persist settings:', error.message);
};

// Set one business setting: update state + localStorage cache + persist to Supabase.
export const setBusinessSetting = function (key, value) {
  state.settings[key] = value;
  _cacheBusinessSetting(key, value);
  persistBusinessSettings();
};

// Stage one business setting: update state + localStorage cache WITHOUT persisting
// to Supabase. Used by toggles/thresholds in the Settings modal, which defer the
// actual write until the shared footer's "Save Changes" is clicked (so an
// in-progress round of changes can still be discarded).
export const stageBusinessSetting = function (key, value) {
  state.settings[key] = value;
  _cacheBusinessSetting(key, value);
};

// A plain-object snapshot of the current business settings, for the Settings
// modal to restore from if the user discards an unsaved round of changes.
export const snapshotBusinessSettings = function () {
  return _collectBusinessSettings();
};

// Restore state + localStorage cache from a snapshot taken by snapshotBusinessSettings.
// Does not touch Supabase - the prior (already-saved) value is still what's there.
export const restoreBusinessSettings = function (snapshot) {
  for (const key of Object.keys(snapshot)) {
    state.settings[key] = snapshot[key];
    _cacheBusinessSetting(key, snapshot[key]);
  }
};

// These three just upload the file and hand back its URL - they deliberately
// do NOT call setBusinessSetting themselves. The Settings modal stages the
// resulting URL via stageBusinessSetting (same deferred-save flow as every
// other POS/Displays setting) so a photo change isn't live until Save Changes.
export const uploadCFDAdImage = async function (file) {
  return await uploadImage(file);
};

export const uploadPickupAdImage = async function (file) {
  return await uploadImage(file);
};

export const uploadCFDQRImage = async function (file) {
  return await uploadImage(file);
};

export const uploadTipQRImage = async function (file) {
  return await uploadImage(file);
};

// Today's active queue: unprepared sales plus kitchen tickets sent from open
// tabs (kitchenQueue.js, shared with the Queue Screen popup).
export const loadOrderQueue = async function () {
  state.orderQueue = await loadKitchenQueue(supabase, state.businessId, businessTz());
};

// Done / Ready / back to Preparing. Tab tickets (ids "kt-<uuid>") live in
// kitchen_tickets, everything else in sales.
export const recordServeTime = async function (id, timedOut) {
  await setQueueItemState(supabase, state.businessId, id,
    { prepared_at: new Date().toISOString(), timed_out: timedOut });
};

// Marks an order "Ready for Pickup" - the order stays in the active queue
// (prepared_at is untouched); only Mark Done removes it from the queue.
export const markOrderReady = async function (id) {
  await setQueueItemState(supabase, state.businessId, id, { ready_at: new Date().toISOString() });
};

// Reverts a mistaken "Ready" back to Preparing (cashier-side "Back to Kitchen").
export const unmarkOrderReady = async function (id) {
  await setQueueItemState(supabase, state.businessId, id, { ready_at: null });
};

function parseVariants(raw) {
  const variantMap = {};

  Object.keys(raw).forEach((key) => {
    const match = key.match(
      /variants\[(\d+)\]\[(optionLabel|options)\](?:\[(\d+)\]\[(optionName|optionPrice)\])?/,
    );
    if (!match) return;

    const variantIndex = match[1];
    const field = match[2];
    const optionIndex = match[3];
    const optionField = match[4];

    if (!variantMap[variantIndex]) {
      variantMap[variantIndex] = { optionLabel: "", options: [] };
    }

    if (field === "optionLabel") {
      variantMap[variantIndex].optionLabel = raw[key];
    }

    if (field === "options") {
      if (!variantMap[variantIndex].options[optionIndex]) {
        variantMap[variantIndex].options[optionIndex] = {
          optionName: "",
          optionPrice: "0",
        };
      }
      variantMap[variantIndex].options[optionIndex][optionField] =
        optionField === "optionPrice" ? Number(raw[key]) || 0 : raw[key];
    }
  });

  return Object.values(variantMap).map((variant) => ({
    ...variant,
    options: variant.options.filter(
      (option) => option.optionName.trim() !== "",
    ),
  }));
}

// ── Staff management ──────────────────────────────────────────────────────────

// Live deactivation kick-out: fires the callback the moment this user's staff row
// gets is_active = false (in-app removal or admin panel deactivation). Requires the
// staff table to be in the supabase_realtime publication.
let _staffWatchChannel = null;

export const watchStaffDeactivation = function (onDeactivated, onRoleChanged) {
  if (!state.currentStaff?.id || _staffWatchChannel) return;
  const channel = supabase
    .channel(`staff-active-${state.currentStaff.id}`)
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'staff',
      filter: `id=eq.${state.currentStaff.id}`,
    }, (payload) => {
      // Deactivation takes precedence - a kicked-out user is signed out, not reloaded.
      if (payload.new?.is_active === false) { onDeactivated(); return; }
      // An owner changing this user's role pushes the new role_id here. Compare against
      // the role_id this session loaded with; if it differs, the session is stale.
      if (onRoleChanged
          && payload.new?.role_id
          && state.currentStaff?.roleId
          && payload.new.role_id !== state.currentStaff.roleId) {
        state.currentStaff.roleId = payload.new.role_id; // guard against a double-fire
        onRoleChanged();
      }
    });

  // Also reload when the user's OWN role definition is edited (permissions changed by
  // the owner). Without this, an open session keeps its stale capabilities until the
  // next login. Requires `roles` in the supabase_realtime publication.
  if (onRoleChanged && state.currentStaff?.roleId) {
    channel.on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'roles',
      filter: `id=eq.${state.currentStaff.roleId}`,
    }, () => onRoleChanged());
  }

  _staffWatchChannel = channel.subscribe();
};

const dbToStaff = (row) => ({
  id:         row.id,
  firstName:  row.first_name,
  lastName:   row.last_name,
  email:      row.email,
  hourlyRate: row.hourly_rate ?? null,
  isActive:  row.is_active,
  joinedAt:  row.joined_at,
  isPending: !row.user_id,
  role:      row.roles?.name ?? 'Staff',
  roleId:    row.roles?.id ?? null,
  isSelf:    row.user_id === state.userId,
  isOwner:   !!row.user_id && row.user_id === state.businessId,
  hasPin:    !!row.pin,
  pin:       row.pin ?? null,
});

export const loadStaff = async function () {
  const { data, error } = await supabase
    .from('staff')
    .select('id, first_name, last_name, email, is_active, joined_at, user_id, pin, hourly_rate, roles(id, name)')
    .eq('business_id', state.businessId)
    .eq('is_active', true)
    .order('invited_at', { ascending: true });
  if (error) throw error;
  state.staff = data.map(dbToStaff);
};

// Count of people in the business (owner + active staff + pending invites) - the
// owner always has an active staff row, so this is everyone "in" the business.
// Used to size the terminal-number selector (one terminal per person).
export const getActiveHeadcount = async function () {
  const { count, error } = await supabase
    .from('staff')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', state.businessId)
    .eq('is_active', true);
  if (error || count == null) return 1;
  return Math.max(1, count);
};

export const loadRoles = async function () {
  let { data, error } = await supabase
    .from('roles')
    .select('id, name, permissions, description, icon')
    .eq('business_id', state.businessId)
    .order('created_at');
  if (error) {
    // description/icon columns not migrated yet - fall back so Staff still loads.
    ({ data, error } = await supabase
      .from('roles')
      .select('id, name, permissions')
      .eq('business_id', state.businessId)
      .order('created_at'));
  }
  if (error) throw error;
  // Expose each role's granted-capability list (normalised from the stored JSONB,
  // falling back to built-in defaults for legacy/empty shapes) for the role editor.
  state.roles = data.map((r) => ({
    ...r,
    caps: [...capabilitySetFromRole(r.name, r.permissions)],
    locked: r.name === LOCKED_ROLE,
  }));
};

export const inviteStaff = async function ({ firstName, lastName, email, roleId }) {
  const normalizedEmail = email.toLowerCase().trim();
  const role = state.roles.find((r) => r.id === roleId);
  if (role && !canAssignRole(role)) {
    throw new Error(role.locked
      ? 'Only the owner or an Admin can invite someone as Admin.'
      : "You can't invite someone with access you don't have yourself.");
  }

  // The invite-staff Edge Function does the whole invite server-side (2026-10-08):
  // checks the caller's manage_staff + that they can hand out this role, enforces the
  // single-membership rule, writes (or reactivates) the staff row, then sends the
  // email. Writing the row here in the browser used to fail for non-owners (staff
  // RLS only lets the owner insert) AFTER the email had already gone out.
  const { data, error } = await supabase.functions.invoke('invite-staff', {
    body: { email: normalizedEmail, firstName, lastName, businessId: state.businessId, roleId },
  });
  if (error) {
    let detail = error.message;
    try {
      if (error.context instanceof Response) {
        const b = await error.context.json();
        if (b?.error) detail = b.error;
      }
    } catch {}
    throw new Error(detail);
  }
  if (!data?.staff) throw new Error('Could not send the invite. Please try again.');

  state.staff = state.staff.filter((s) => s.id !== data.staff.id);
  state.staff.push(dbToStaff(data.staff));
  return { reactivated: !!data.reactivated, existingAccount: !!data.existingAccount };
};

// Whether the current user may give this role to someone (invite form). Mirrors the
// invite-staff Edge Function, which enforces it: the owner can give any role; only an
// Admin can give Admin; anyone else only a role whose access is within their own.
export const canAssignRole = function (role) {
  if (state.userId && state.userId === state.businessId) return true;
  if (role.locked || role.name === LOCKED_ROLE) return state.role === LOCKED_ROLE;
  return (role.caps ?? []).every((k) => hasPermission(k));
};

// Accepts one of the pending invites surfaced by loadBusinessContext (PENDING_INVITE_CONSENT).
// Claims the chosen row by linking the caller's user_id, then discards the rest so the
// single-membership rule holds. Returns the auth user so the caller can re-run initApp.
export const claimPendingInvite = async function (staffId) {
  const { data: { user } } = await supabase.auth.getUser();

  const { error: claimError } = await supabase
    .from('staff')
    .update({ user_id: user.id, joined_at: new Date().toISOString() })
    .eq('id', staffId)
    .is('user_id', null);
  if (claimError) throw claimError;

  const others = (state.pendingInvites ?? [])
    .filter((p) => p.staffId !== staffId)
    .map((p) => p.staffId);
  if (others.length) {
    await supabase.from('staff').delete().in('id', others);
  }

  state.pendingInvites = null;
  return user;
};

export const dismissPendingInvites = function () {
  state.pendingInvites = null;
};

export const updateStaffRole = async function (staffId, roleId) {
  // The business owner can never be demoted out of full access - no role change is
  // allowed on their row, by anyone. Mirrors the owner-removal guard in removeStaff.
  const target = state.staff.find((s) => s.id === staffId);
  if (target?.isOwner) throw new Error("The business owner's role cannot be changed.");
  const { error, count } = await supabase
    .from('staff')
    .update({ role_id: roleId }, { count: 'exact' })
    .eq('id', staffId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  if (count === 0) throw new Error('Permission denied. Check staff RLS update policy.');
  const role = state.roles.find(r => r.id === roleId);
  const s = state.staff.find(s => s.id === staffId);
  if (s && role) { s.roleId = roleId; s.role = role.name; }
};

export const setStaffPin = async function (staffId, pin) {
  const { error, count } = await supabase
    .from('staff')
    .update({ pin: pin || null }, { count: 'exact' })
    .eq('id', staffId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  if (count === 0) throw new Error('Permission denied. Check staff RLS update policy.');
  const s = state.staff.find(s => s.id === staffId);
  if (s) { s.pin = pin || null; s.hasPin = !!pin; }
};

export const removeStaff = async function (id) {
  const member = state.staff.find((s) => s.id === id);

  // The business owner can never be removed - not by an Admin-role staff member, not by
  // anyone. Their row anchors the business (user_id === business_id). This guard is the
  // authoritative backstop even though the view hides the Remove button on the owner row.
  if (member?.isOwner) {
    throw new Error('The business owner cannot be removed.');
  }

  if (member?.isPending) {
    // Pending invites have no history - hard delete so the email can be re-invited cleanly.
    const { error, count } = await supabase
      .from('staff')
      .delete({ count: 'exact' })
      .eq('id', id)
      .eq('business_id', state.businessId);
    if (error) throw error;
    if (count === 0) throw new Error('Could not delete staff member. Please try again.');
  } else {
    // Joined staff are soft-deleted so shifts, sales attribution, and payroll history survive.
    const { error, count } = await supabase
      .from('staff')
      .update({ is_active: false }, { count: 'exact' })
      .eq('id', id)
      .eq('business_id', state.businessId);
    if (error) throw error;
    if (count === 0) throw new Error('Could not remove staff member. Please try again.');
  }

  const idx = state.staff.findIndex((s) => s.id === id);
  if (idx !== -1) state.staff.splice(idx, 1);
};

// Admin-assisted 2FA removal for a staff member who lost both their authenticator
// and their recovery codes (self-service recovery via model.redeemRecoveryCode no
// longer works for them). Runs the admin-remove-2fa Edge Function (service role -
// the client SDK's auth.mfa.unenroll only ever operates on the CALLER's own factor,
// never another user's). The owner's own factor and the caller's own factor are
// rejected server-side too; these client-side checks just fail fast with a clear
// message instead of a round-trip.
export const adminRemoveStaffMFA = async function (staffId) {
  const target = state.staff.find((s) => s.id === staffId);
  if (target?.isOwner) throw new Error("The business owner's two-factor authentication can only be removed by support.");
  if (target?.isSelf) throw new Error('Use Settings to manage your own two-factor authentication.');

  const { data, error } = await supabase.functions.invoke('admin-remove-2fa', { body: { staffId } });
  if (error) {
    let detail = error.message;
    try {
      if (error.context instanceof Response) {
        const b = await error.context.json();
        if (b?.error) detail = b.error;
      }
    } catch {}
    throw new Error(detail);
  }
  return data;
};

// Keeps only valid capability keys, so a malformed list can never widen access.
const _sanitizeCaps = (caps) => (caps ?? []).filter((k) => ALL_PERMISSIONS.includes(k));

export const addRole = async function (name, caps = [], description = null, icon = null) {
  const clean = _sanitizeCaps(caps);
  const { data, error } = await supabase
    .from('roles')
    .insert({
      business_id: state.businessId,
      name: name.trim(),
      permissions: capsToPermissions(clean),
      description: description?.trim() || null,
      icon: icon || null,
    })
    .select('id, name, permissions, description, icon')
    .single();
  if (error) throw error;
  const role = { ...data, caps: clean, locked: data.name === LOCKED_ROLE };
  state.roles.push(role);
  return role;
};

export const renameRole = async function (roleId, name) {
  const role = state.roles.find((r) => r.id === roleId);
  if (role?.locked) throw new Error('The Admin role cannot be renamed.');
  const { error } = await supabase
    .from('roles')
    .update({ name: name.trim() })
    .eq('id', roleId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  if (role) role.name = name.trim();
};

// Description + icon are cosmetic and editable on any non-locked role.
export const updateRoleMeta = async function (roleId, { description, icon } = {}) {
  const role = state.roles.find((r) => r.id === roleId);
  if (role?.locked) throw new Error('The Admin role cannot be edited.');
  const patch = { description: description?.trim() || null, icon: icon || null };
  const { error } = await supabase
    .from('roles')
    .update(patch)
    .eq('id', roleId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  if (role) Object.assign(role, patch);
};

export const updateRolePermissions = async function (roleId, caps) {
  const role = state.roles.find((r) => r.id === roleId);
  if (role?.locked) throw new Error('The Admin role always has full access and cannot be edited.');
  const clean = _sanitizeCaps(caps);
  const { error } = await supabase
    .from('roles')
    .update({ permissions: capsToPermissions(clean) })
    .eq('id', roleId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  if (role) { role.permissions = capsToPermissions(clean); role.caps = clean; }
};

export const deleteRole = async function (roleId) {
  const role = state.roles.find((r) => r.id === roleId);
  if (role?.locked) throw new Error('The Admin role cannot be deleted.');
  // Block if any staff member still holds this role - the owner must reassign first.
  const inUse = state.staff.filter((s) => s.roleId === roleId).length;
  if (inUse > 0) {
    throw new Error(`This role is assigned to ${inUse} staff member${inUse === 1 ? '' : 's'}. Reassign them to another role before deleting it.`);
  }
  const { error } = await supabase
    .from('roles')
    .delete()
    .eq('id', roleId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  const idx = state.roles.findIndex((r) => r.id === roleId);
  if (idx !== -1) state.roles.splice(idx, 1);
};

export const loadTickets = async function () {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  const { data, error } = await supabase
    .from('tickets')
    .select('id, category, subject, message, status, has_unread_reply, created_at, solved_at, attachments, rating')
    .eq('business_id', state.businessId)
    .or(`status.eq.open,and(status.eq.solved,solved_at.gte.${cutoff.toISOString()})`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  state.tickets = data;
};

export const loadTicketReplies = async function (ticketId) {
  const { data, error } = await supabase
    .from('ticket_replies')
    .select('id, sender_type, message, created_at')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
};

export const markTicketSolved = async function (ticketId) {
  const solvedAt = new Date().toISOString();
  const { error } = await supabase
    .from('tickets')
    .update({ status: 'solved', solved_at: solvedAt })
    .eq('id', ticketId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  const t = state.tickets.find(t => t.id === ticketId);
  if (t) { t.status = 'solved'; t.solved_at = solvedAt; }
};

export const markRepliesRead = async function (ticketId) {
  const { error } = await supabase
    .from('tickets')
    .update({ has_unread_reply: false })
    .eq('id', ticketId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  const t = state.tickets.find(t => t.id === ticketId);
  if (t) t.has_unread_reply = false;
};

// ── Shifts & timeclock ────────────────────────────────────────────────────────

export const generateTimeclockToken = async function () {
  const token = Math.random().toString(36).slice(2, 8).toUpperCase();
  const { error } = await supabase
    .from('businesses')
    .update({ timeclock_token: token })
    .eq('id', state.businessId);
  if (error) throw error;
  return token;
};

// Tips for the Timesheets > Tips pool: one row per non-voided sale with a tip.
export const fetchTipsForPeriod = async function (startISO, endISO) {
  const { data, error } = await supabase
    .from('sales')
    .select('sale_date, tip_amount')
    .eq('user_id', state.businessId)
    .gte('sale_date', startISO)
    .lte('sale_date', endISO)
    .gt('tip_amount', 0)
    .is('voided_at', null);
  if (error) throw error;
  return (data ?? []).map((r) => ({ date: r.sale_date, amount: Number(r.tip_amount) }));
};

export const fetchShifts = async function (startISO, endISO) {
  // staff embed is disambiguated by FK name because shifts now has TWO FKs to staff
  // (staff_id + deleted_by). corrections carry who approved a shift's edit request.
  const { data, error } = await supabase
    .from('shifts')
    .select('id, clocked_in_at, clocked_out_at, note, staff_id, source, edited_at, shift_breaks(id, started_at, ended_at), staff:staff!shifts_staff_id_fkey(first_name, last_name, hourly_rate, role_id, user_id), editor:staff!shifts_edited_by_fkey(first_name, last_name), corrections:shift_edit_requests(status, reviewed_at, reviewed_by_name)')
    .eq('business_id', state.businessId)
    .is('deleted_at', null)
    .gte('clocked_in_at', startISO)
    .lte('clocked_in_at', endISO)
    .order('clocked_in_at', { ascending: true });
  if (error) throw error;
  state.shifts = data.map(r => {
    // Latest approved correction linked to this shift. reviewed_by_name is the
    // denormalized reviewer name (stamped by the review RPC), so no staff join
    // is needed - it stays readable regardless of staff-row RLS.
    const approved = (r.corrections ?? [])
      .filter(c => c.status === 'approved')
      .sort((a, b) => new Date(b.reviewed_at) - new Date(a.reviewed_at))[0];
    const editorName = r.editor ? `${r.editor.first_name ?? ''} ${r.editor.last_name ?? ''}`.trim() : null;
    return {
      source:       r.source ?? 'clock',   // 'clock' (tracked) | 'manual' (admin-added) | 'request' (approved correction)
      editedBy:     editorName,
      id:           r.id,
      staffId:      r.staff_id,
      staffName:    `${r.staff?.first_name ?? ''} ${r.staff?.last_name ?? ''}`.trim(),
      hourlyRate:   r.staff?.hourly_rate ?? null,
      // Tip pool eligibility (Timesheets > Tips): the person's role, and whether
      // it's the owner (never in an employee tip pool).
      staffRoleId:  r.staff?.role_id ?? null,
      staffIsOwner: !!r.staff?.user_id && r.staff.user_id === state.businessId,
      clockedInAt:  r.clocked_in_at,
      clockedOutAt: r.clocked_out_at ?? null,
      note:         r.note ?? '',
      // Reviewer name from the request itself when linked; else fall back to the
      // edited_by stamp the review RPC writes on approval (source = 'request').
      approvedBy:   approved?.reviewed_by_name
        || (r.source === 'request' ? editorName : null),
      breaks:       (r.shift_breaks ?? []).map(b => ({
        id:        b.id,
        startedAt: b.started_at,
        endedAt:   b.ended_at ?? null,
      })),
    };
  });
};

// Soft-deleted shifts, for the "Deleted shifts" log at the bottom of the
// Timesheets view. Shows who deleted each one; restorable via setShiftsDeleted.
// Not filtered by the visible period: purgeDeletedShifts caps retention at 7
// days, so the whole restorable set is small and always worth showing.
export const fetchDeletedShifts = async function () {
  const { data, error } = await supabase
    .from('shifts')
    .select('id, clocked_in_at, clocked_out_at, deleted_at, staff:staff!shifts_staff_id_fkey(first_name, last_name), deleter:staff!shifts_deleted_by_fkey(first_name, last_name)')
    .eq('business_id', state.businessId)
    .not('deleted_at', 'is', null)
    .order('deleted_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map(r => ({
    id:           r.id,
    staffName:    `${r.staff?.first_name ?? ''} ${r.staff?.last_name ?? ''}`.trim(),
    clockedInAt:  r.clocked_in_at,
    clockedOutAt: r.clocked_out_at ?? null,
    deletedAt:    r.deleted_at,
    deletedBy:    `${r.deleter?.first_name ?? ''} ${r.deleter?.last_name ?? ''}`.trim() || 'Unknown',
  }));
};

// Hard-delete soft-deleted shifts whose 7-day restore window has passed. Lazy
// sweep - called best-effort whenever the Timesheets view loads (there is no
// server-side cron). The SECURITY DEFINER RPC checks manage_pay and scopes the
// delete to the caller's business.
export const purgeDeletedShifts = async function () {
  const { error } = await supabase.rpc('purge_deleted_shifts');
  if (error) throw error;
};

// Soft delete (or restore) shifts via the set_shifts_deleted SECURITY DEFINER RPC -
// checks manage_pay server-side and stamps deleted_by, keeping shifts RLS tight.
export const setShiftsDeleted = async function (ids, deleted) {
  const { error } = await supabase.rpc('set_shifts_deleted', {
    p_shift_ids: ids,
    p_deleted:   deleted,
  });
  if (error) throw error;
};

// Pending shift-correction requests for the reviewer inbox (Payroll tab). Any active
// member can read these (RLS); the manage_pay gate is applied in the controller.
export const fetchShiftEditRequests = async function () {
  const { data, error } = await supabase
    .from('shift_edit_requests')
    .select('id, staff_id, shift_id, proposed_clock_in, proposed_clock_out, proposed_breaks, reason, status, created_at, staff:staff_id(first_name, last_name), shift:shift_id(clocked_in_at, clocked_out_at)')
    .eq('business_id', state.businessId)
    .eq('status', 'pending')
    .order('created_at', { ascending: true });
  if (error) throw error;
  state.shiftEditRequests = (data ?? []).map(r => ({
    id:            r.id,
    staffId:       r.staff_id,
    staffName:     `${r.staff?.first_name ?? ''} ${r.staff?.last_name ?? ''}`.trim(),
    shiftId:       r.shift_id ?? null,          // null = missed-shift request
    proposedIn:    r.proposed_clock_in ?? null,
    proposedOut:   r.proposed_clock_out ?? null,
    proposedBreaks: Array.isArray(r.proposed_breaks) ? r.proposed_breaks : null,
    reason:        r.reason ?? '',
    createdAt:     r.created_at,
    currentIn:     r.shift?.clocked_in_at ?? null,
    currentOut:    r.shift?.clocked_out_at ?? null,
  }));
  return state.shiftEditRequests;
};

// Approve or reject a correction. Approval applies the change via a SECURITY DEFINER
// RPC (checks manage_pay server-side, writes the shift + breaks). See admin-updates.
export const reviewShiftEditRequest = async function (id, decision, { clockIn, clockOut, breaks, note } = {}) {
  const { error } = await supabase.rpc('review_shift_edit_request', {
    p_request_id: id,
    p_decision:   decision,
    p_clock_in:   clockIn ?? null,
    p_clock_out:  clockOut ?? null,
    p_breaks:     breaks ?? null,
    p_note:       note ?? null,
  });
  if (error) throw error;
  state.shiftEditRequests = state.shiftEditRequests.filter(r => r.id !== id);
};

// All leave requests for the business (RLS-scoped). The controller passes the
// full list; the Timesheets Leave section splits pending/upcoming/history.
export const fetchLeaveRequests = async function () {
  const { data, error } = await supabase
    .from('leave_requests')
    .select('id, staff_id, type, start_date, end_date, reason, status, created_at, reviewed_by_name, reviewed_at, review_note, staff:staff_id(first_name, last_name)')
    .eq('business_id', state.businessId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map(r => ({
    id:             r.id,
    staffId:        r.staff_id,
    staffName:      `${r.staff?.first_name ?? ''} ${r.staff?.last_name ?? ''}`.trim(),
    type:           r.type,
    startDate:      r.start_date,
    endDate:        r.end_date,
    reason:         r.reason ?? '',
    status:         r.status,
    createdAt:      r.created_at,
    reviewedByName: r.reviewed_by_name ?? null,
    reviewedAt:     r.reviewed_at ?? null,
    reviewNote:     r.review_note ?? null,
  }));
};

// Approve or reject a leave request via the review_leave_request SECURITY
// DEFINER RPC (owner/manage_pay verified server-side). See admin-updates.
export const reviewLeaveRequest = async function (id, decision, note) {
  const { error } = await supabase.rpc('review_leave_request', {
    p_request_id: id,
    p_decision:   decision,
    p_note:       note ?? null,
  });
  if (error) throw error;
};

export const addShift = async function ({ staffId, clockedInAt, clockedOutAt, note }) {
  const { data, error } = await supabase
    .from('shifts')
    // source 'manual' = admin-added (vs 'clock' = tracked by the time clock) so
    // timesheets can show shift provenance.
    .insert({ business_id: state.businessId, staff_id: staffId, clocked_in_at: clockedInAt, clocked_out_at: clockedOutAt || null, note: note || null, source: 'manual', edited_by: state.currentStaff?.id ?? null, edited_at: new Date().toISOString() })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
};

export const updateShift = async function ({ id, clockedInAt, clockedOutAt, note }) {
  const { error } = await supabase
    .from('shifts')
    // Stamp who edited, so timesheets can show "Edited by X".
    .update({ clocked_in_at: clockedInAt, clocked_out_at: clockedOutAt || null, note: note || null, edited_by: state.currentStaff?.id ?? null, edited_at: new Date().toISOString() })
    .eq('id', id)
    .eq('business_id', state.businessId);
  if (error) throw error;
};

// The signed-in staff member's own shifts since `sinceISO` (home "My shift" card).
// Kept separate from fetchShifts so it never overwrites state.shifts (Timesheets).
// Staff RLS (shifts_staff_own) already limits a member to their own rows; the
// staff_id filter keeps it to "me" for owners/admins who can read everyone's.
export const fetchMyShifts = async function (sinceISO) {
  const staffId = state.currentStaff?.id;
  if (!staffId) return [];
  const { data, error } = await supabase
    .from('shifts')
    .select('clocked_in_at, clocked_out_at, shift_breaks(started_at, ended_at)')
    .eq('business_id', state.businessId)
    .eq('staff_id', staffId)
    .is('deleted_at', null)
    .or(`clocked_out_at.is.null,clocked_out_at.gte.${sinceISO}`)
    .order('clocked_in_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(r => ({
    clockedInAt:  r.clocked_in_at,
    clockedOutAt: r.clocked_out_at ?? null,
    breaks: (r.shift_breaks ?? []).map(b => ({ startedAt: b.started_at, endedAt: b.ended_at ?? null })),
  }));
};

// Who is clocked in RIGHT NOW (open shift, not deleted) + whether they're on break.
// Via a SECURITY DEFINER RPC because staff RLS only lets a member read their OWN
// shifts - the on-shift indicator needs the whole team. Membership-checked in SQL.
export const getOnShiftStaff = async function () {
  const { data, error } = await supabase.rpc('get_on_shift_staff');
  if (error) throw error;
  // One row per STAFF MEMBER, not per open shift - a double clock-in (two devices,
  // an unclosed earlier shift) must not list the same person twice. Keep the
  // earliest clock-in; on break if ANY of their open shifts is on break.
  const byStaff = new Map();
  for (const r of data ?? []) {
    const prev = byStaff.get(r.staff_id);
    if (!prev) {
      byStaff.set(r.staff_id, {
        staffId:     r.staff_id,
        name:        `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim(),
        clockedInAt: r.clocked_in_at,
        onBreak:     r.on_break === true,
      });
    } else {
      if (new Date(r.clocked_in_at) < new Date(prev.clockedInAt)) prev.clockedInAt = r.clocked_in_at;
      if (r.on_break === true) prev.onBreak = true;
    }
  }
  return [...byStaff.values()];
};

// Registered time-clock devices (device registry - each activation creates a row).
export const fetchTimeclockDevices = async function () {
  const { data, error } = await supabase
    .from('timeclock_devices')
    .select('id, label, activated_at, last_seen_at')
    .eq('business_id', state.businessId)
    .order('activated_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(d => ({
    id:          d.id,
    label:       d.label || 'Time clock device',
    activatedAt: d.activated_at,
    lastSeenAt:  d.last_seen_at,
  }));
};

// Revoking = deleting the row; that device fails its check on next load and shows
// the activation screen again.
export const deleteTimeclockDevice = async function (id) {
  const { error } = await supabase
    .from('timeclock_devices')
    .delete()
    .eq('id', id)
    .eq('business_id', state.businessId);
  if (error) throw error;
};

// Self-service PIN reset from the cashier switcher: the staff member proves
// identity with their OWN account password (isolated client - never touches the
// POS session), then their staff row's pin is updated via their own self-row
// RLS. The credentials must belong to the staff member being switched to.
export const resetOwnPin = async function ({ email, password, staffId, newPin, captchaToken, mfaCode }) {
  const tempClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
  const { data: authData, error: authError } = await tempClient.auth.signInWithPassword({
    email,
    password,
    options: { captchaToken },
  });
  if (authError) {
    if (/captcha/i.test(authError.message || ''))
      throw new Error('Verification check failed. Please try again.');
    throw new Error('Invalid email or password.');
  }
  await _elevateTempClient(tempClient, mfaCode);
  try {
    const { data: row } = await tempClient
      .from('staff')
      .select('id')
      .eq('user_id', authData.user.id)
      .eq('business_id', state.businessId)
      .eq('is_active', true)
      .maybeSingle();
    if (!row || row.id !== staffId) throw new Error('These credentials belong to a different staff member.');
    const { error } = await tempClient.from('staff').update({ pin: newPin }).eq('id', staffId);
    if (error) throw new Error('Could not update the PIN. Please try again.');
  } finally {
    await tempClient.auth.signOut({ scope: 'local' });
  }
  const s = state.staff.find((x) => x.id === staffId);
  if (s) { s.pin = newPin; s.hasPin = true; }
};

export const updateStaffHourlyRate = async function (staffId, rate) {
  const { error } = await supabase
    .from('staff')
    .update({ hourly_rate: rate ?? null })
    .eq('id', staffId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  const s = state.staff.find(s => s.id === staffId);
  if (s) s.hourlyRate = rate ?? null;
};

export const submitTicket = async function ({ category, subject, message, files }) {
  const attachments = [];
  for (const rawFile of files) {
    const file = await prepareImageForUpload(rawFile);
    const ext = file.name.split('.').pop();
    const path = `${state.businessId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('ticket-attachments')
      .upload(path, file);
    if (uploadError) throw uploadError;
    const { data } = supabase.storage.from('ticket-attachments').getPublicUrl(path);
    attachments.push(data.publicUrl);
  }

  const { error } = await supabase.from('tickets').insert({
    business_id: state.businessId,
    category,
    subject,
    message,
    attachments,
  });
  if (error) throw error;
};

export const submitTicketReply = async function (ticketId, message) {
  const { error } = await supabase.from('ticket_replies').insert({
    ticket_id: ticketId,
    sender_type: 'business',
    message,
  });
  if (error) throw error;
  const { error: flagError } = await supabase
    .from('tickets')
    .update({ has_business_reply: true })
    .eq('id', ticketId)
    .eq('business_id', state.businessId);
  if (flagError) throw flagError;
};

export const submitTicketRating = async function (ticketId, rating, comment) {
  const { error } = await supabase
    .from('tickets')
    .update({ rating, rating_comment: comment || null, rated_at: new Date().toISOString() })
    .eq('id', ticketId)
    .eq('business_id', state.businessId);
  if (error) throw error;
  const t = state.tickets.find(t => t.id === ticketId);
  if (t) { t.rating = rating; t.rating_comment = comment || null; }
};

export const sendPasswordResetEmail = async function (email, captchaToken) {
  // Sent via the dedicated PKCE client (resetClient) so the link is single-use and the
  // PKCE code_verifier is written under its own storageKey. The link lands on the
  // standalone reset.html page, which exchanges the ?code= and sets the new password.
  // redirectTo MUST be same-origin as this send (PKCE verifier is origin-scoped in
  // localStorage), so reset.html reads back the verifier it stored here.
  const { error } = await resetClient.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset`,
    captchaToken,
  });
  if (error) throw error;
};

// Guided-tutorial progress, synced across devices in the auth user's metadata
// (`pb_tours`) so finishing or skipping a tour on one device/browser stops it
// auto-starting on the next. Not sensitive and only UX state, so user-editable
// metadata is fine and no table/migration is needed. updateUser merges
// top-level metadata keys, so this never touches role/name/etc.
export const fetchTourProgress = async function () {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data.user?.user_metadata?.pb_tours ?? null;
};

export const saveTourProgress = async function (progress) {
  const { error } = await supabase.auth.updateUser({ data: { pb_tours: progress } });
  if (error) throw error;
};

export const updatePassword = async function (newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
};

export const sendSettingsVerification = async function (captchaToken) {
  const { data: { user } } = await supabase.auth.getUser();
  // CAPTCHA enforcement in Supabase is project-wide, so this OTP-sending call needs a
  // token too, same as signInWithPassword elsewhere (manager override, time clock).
  const { error } = await supabase.auth.signInWithOtp({
    email: user.email,
    options: { shouldCreateUser: false, captchaToken },
  });
  if (error) {
    if (/captcha/i.test(error.message || '')) throw new Error('Verification check failed. Please try again.');
    throw error;
  }
  return user.email;
};

export const confirmSettingsVerification = async function (email, token) {
  const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
  if (error) throw error;
};

// ── Two-Factor Authentication (TOTP via Supabase MFA) ─────────────────────────
// Opt-in per user. A factor only counts (and is enforced at login) once it has
// been verified with a code. Enrollment hands back a QR + secret; the caller
// must then verify a code before the factor becomes active.

// Remove any half-finished (unverified) TOTP factors so a fresh enroll can't
// trip the "factor already exists" error or leave clutter behind.
const _cleanupUnverifiedTotp = async function () {
  const { data } = await supabase.auth.mfa.listFactors();
  const stale = (data?.all ?? []).filter(
    (f) => f.factor_type === 'totp' && f.status !== 'verified'
  );
  for (const f of stale) {
    try { await supabase.auth.mfa.unenroll({ factorId: f.id }); } catch { /* best effort */ }
  }
};

// Returns { enabled, factorId } - whether the user has a VERIFIED TOTP factor.
export const getMFAStatus = async function () {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  const verified = (data?.all ?? []).find(
    (f) => f.factor_type === 'totp' && f.status === 'verified'
  );
  return { enabled: !!verified, factorId: verified?.id ?? null };
};

// Begins enrollment: returns { factorId, qr, secret } for the setup modal.
export const enrollMFA = async function () {
  await _cleanupUnverifiedTotp();
  const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
  if (error) throw error;
  return { factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret };
};

// Confirms enrollment with a code from the authenticator app. On success the
// session is elevated to AAL2 and the factor becomes active.
export const verifyMFAEnrollment = async function (factorId, code) {
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
  if (error) throw error;
};

// Turns 2FA off by removing the verified factor.
export const unenrollMFA = async function (factorId) {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw error;
};

// { currentLevel, nextLevel }. MFA is required when nextLevel is aal2 but the
// current session is still aal1 (password only).
export const getAAL = async function () {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) throw error;
  return data;
};

// Login-time verification: challenge the factor then verify the entered code.
// Throws on a wrong code so the caller can re-prompt.
export const verifyMFALogin = async function (factorId, code) {
  const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
  if (chErr) throw chErr;
  const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code });
  if (error) throw error;
};

// ── 2FA recovery codes ────────────────────────────────────────────────────────
// One-time backup codes shown once at enrollment. Only HASHES are stored (here and
// server-side); the plaintext is shown to the user a single time. Redemption runs
// in the mfa-recovery Edge Function (it needs the service role to remove the factor
// of a locked-out aal1 user).

// Avoids visually ambiguous characters (no I/O/0/1).
const RECOVERY_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const _genRecoveryCode = function () {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  let s = '';
  for (const b of bytes) s += RECOVERY_ALPHABET[b % RECOVERY_ALPHABET.length];
  return `${s.slice(0, 4)}-${s.slice(4, 8)}`;
};

// Match the Edge Function's normalization so a copied "ABCD-EFGH" hashes the same.
const _normalizeCode = (code) => String(code ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const _sha256Hex = async function (str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
};

// Generates a fresh set of 10 codes, stores their hashes (replacing any prior set),
// and returns the plaintext codes for one-time display. Called right after a TOTP
// factor is verified.
export const generateRecoveryCodes = async function () {
  const codes = Array.from({ length: 10 }, _genRecoveryCode);
  const { data: { user } } = await supabase.auth.getUser();
  const rows = [];
  for (const c of codes) {
    rows.push({ user_id: user.id, code_hash: await _sha256Hex(_normalizeCode(c)) });
  }
  await supabase.from('mfa_recovery_codes').delete().eq('user_id', user.id);
  const { error } = await supabase.from('mfa_recovery_codes').insert(rows);
  if (error) throw error;
  return codes;
};

export const deleteRecoveryCodes = async function () {
  const { data: { user } } = await supabase.auth.getUser();
  await supabase.from('mfa_recovery_codes').delete().eq('user_id', user.id);
};

// Redeems a recovery code at login (removes the lost factor server-side). Throws
// with a readable message on an invalid code so the caller can re-prompt.
export const redeemRecoveryCode = async function (code) {
  const { data, error } = await supabase.functions.invoke('mfa-recovery', { body: { code } });
  if (error) {
    let detail = error.message;
    try {
      if (error.context instanceof Response) {
        const b = await error.context.json();
        if (b?.error) detail = b.error;
      }
    } catch {}
    throw new Error(detail);
  }
  if (!data?.success) throw new Error('Invalid recovery code');
  return true;
};

export const saveProfileInfo = async function ({ firstName, lastName }) {
  const { error } = await supabase
    .from('staff')
    .update({ first_name: firstName, last_name: lastName })
    .eq('id', state.currentStaff.id);
  if (error) throw error;
  state.currentStaff.firstName = firstName;
  state.currentStaff.lastName  = lastName;
  if (state.currentCashier?.id === state.currentStaff.id) {
    state.currentCashier.firstName = firstName;
    state.currentCashier.lastName  = lastName;
  }
  state.username = `${firstName} ${lastName}`.trim() || state.username;
};

// ── Shift schedule (2026-10-15, free on every plan) ───────────────────────────
// Planned shifts, separate from `shifts` (what the time clock actually tracked).
// scheduled_shifts.staff_id NULL = an open shift; published_at NULL = a draft
// only `manage_schedule` holders see (RLS). Times are timestamptz; the UI plans
// in the business timezone via scheduleTime.js. Migration:
// migration-2026-10-15-shift-scheduling.sql.

// Seeded on first open by someone who can manage the schedule; owners edit freely.
export const DEFAULT_STATIONS = [
  { name: 'Barista',    color: '#0FA267' },
  { name: 'Shift lead', color: '#7C3AED' },
  { name: 'Kitchen',    color: '#EA580C' },
  { name: 'Cashier',    color: '#2563EB' },
];

const toStation = (r) => ({ id: r.id, name: r.name, color: r.color, sortOrder: r.sort_order, archived: !!r.archived_at });
const toScheduledShift = (r) => ({
  id: r.id, staffId: r.staff_id, stationId: r.station_id, startsAt: r.starts_at, endsAt: r.ends_at,
  note: r.note ?? '', publishedAt: r.published_at,
  seriesId: r.series_id ?? null, seriesDate: r.series_date ?? null, isException: !!r.is_exception,
});
const SHIFT_COLS = 'id, staff_id, station_id, starts_at, ends_at, note, published_at, series_id, series_date, is_exception';

// ── Tables (2026-10-22, migration-2026-10-22-tables.sql) ────────────────────
// Floor plan rows are written directly (manage_business, RLS); everything a
// tab does goes through SECURITY DEFINER RPCs so two terminals adding to the
// same tab never overwrite each other.

const mapDiningTable = (r) => ({
  id: r.id, name: r.name, seats: r.seats, shape: r.shape, area: r.area,
  x: Number(r.x), y: Number(r.y), w: Number(r.w), h: Number(r.h),
  canReserve: r.can_reserve, needsCleaning: r.needs_cleaning, sortOrder: r.sort_order,
});

const mapTab = (r) => ({
  id: r.id,
  tableId: r.table_id,
  guests: r.guests,
  serverName: r.server_name,
  status: r.status,
  billRequestedAt: r.bill_requested_at,
  openedAt: r.opened_at,
  items: (r.items ?? []).map((l) => ({
    lid: l.lid,
    itemId: l.item_id,
    itemName: l.item_name,
    unitPrice: Number(l.unit_price) || 0,
    quantity: Number(l.quantity) || 1,
    selectedVariants: (l.selected_variants ?? []).map((v) => ({ variantName: v.variant_name })),
    note: l.note ?? null,
    sentAt: l.sent_at ?? null,
    paidSaleId: l.paid_sale_id ?? null,
  })),
});

// Keeps state.openTabs in step with one tab row returned by an RPC.
const _storeTab = (row) => {
  if (!row?.id) return null;
  const tab = mapTab(row);
  state.openTabs = state.openTabs.filter((t) => t.id !== tab.id);
  if (tab.status === 'open') state.openTabs.push(tab);
  return tab;
};

export const loadTables = async function () {
  const [tables, tabs] = await Promise.all([
    supabase.from('dining_tables')
      .select('id, name, seats, shape, area, x, y, w, h, can_reserve, needs_cleaning, sort_order')
      .eq('business_id', state.businessId)
      .is('archived_at', null)
      .order('sort_order', { ascending: true }),
    supabase.from('table_tabs')
      .select('id, table_id, guests, server_name, status, bill_requested_at, opened_at, items')
      .eq('business_id', state.businessId)
      .eq('status', 'open'),
  ]);
  if (tables.error) throw tables.error;
  if (tabs.error) throw tabs.error;
  state.diningTables = tables.data.map(mapDiningTable);
  state.openTabs = tabs.data.map(mapTab);
};

// Saves the whole edited layout: rows with an id update, rows without insert,
// removedIds are archived (kept for past sales, never shown again).
export const saveTableLayout = async function (tables, removedIds = []) {
  const toRow = (t, i) => ({
    business_id: state.businessId,
    name: String(t.name).trim().slice(0, 12),
    seats: t.seats, shape: t.shape, area: String(t.area || 'Inside').trim().slice(0, 30),
    x: Math.round(t.x), y: Math.round(t.y), w: Math.round(t.w), h: Math.round(t.h),
    can_reserve: t.canReserve !== false, sort_order: i,
  });
  const existing = tables.map((t, i) => [t, i]).filter(([t]) => t.id);
  const added = tables.map((t, i) => [t, i]).filter(([t]) => !t.id);
  const jobs = existing.map(([t, i]) => supabase.from('dining_tables').update(toRow(t, i)).eq('id', t.id).eq('business_id', state.businessId));
  if (added.length) jobs.push(supabase.from('dining_tables').insert(added.map(([t, i]) => toRow(t, i))));
  if (removedIds.length) {
    jobs.push(supabase.from('dining_tables').update({ archived_at: new Date().toISOString() })
      .in('id', removedIds).eq('business_id', state.businessId));
  }
  const results = await Promise.all(jobs);
  const failed = results.find((r) => r.error);
  if (failed) throw failed.error;
  await loadTables();
};

export const openTab = async function (tableId, guests, serverName) {
  const { data, error } = await supabase.rpc('open_table_tab', { p_table: tableId, p_guests: guests, p_server: serverName ?? null });
  if (error) throw error;
  const t = state.diningTables.find((x) => x.id === tableId);
  if (t) t.needsCleaning = false;
  return _storeTab(data);
};

// cart items -> tab lines (the server sets names and prices).
export const addTabLines = async function (tabId, cartItems) {
  const lines = cartItems.map((it) => ({
    item_id: it.id,
    quantity: it.quantity,
    selected_variants: (it.selectedVariants ?? []).map((v) => ({ variant_name: v.variantName })),
    note: it.note ?? null,
  }));
  const { data, error } = await supabase.rpc('tab_add_lines', { p_tab: tabId, p_lines: lines });
  if (error) throw error;
  return _storeTab(data);
};

export const removeTabLine = async function (tabId, lid) {
  const { data, error } = await supabase.rpc('tab_remove_line', { p_tab: tabId, p_lid: lid });
  if (error) throw error;
  return _storeTab(data);
};

// Sends every unsent line to the kitchen. Returns the new queue item (or null
// when nothing was waiting) and refreshes the tab.
export const sendTab = async function (tabId, sentBy) {
  const { data, error } = await supabase.rpc('tab_send', { p_tab: tabId, p_sent_by: sentBy ?? null });
  if (error) throw error;
  await refreshTab(tabId);
  return data?.id ? ticketToQueueItem(data) : null;
};

export const refreshTab = async function (tabId) {
  const { data, error } = await supabase.from('table_tabs')
    .select('id, table_id, guests, server_name, status, bill_requested_at, opened_at, items')
    .eq('id', tabId).maybeSingle();
  if (error) throw error;
  if (!data) { state.openTabs = state.openTabs.filter((t) => t.id !== tabId); return null; }
  return _storeTab(data);
};

export const updateTab = async function (tabId, { guests = null, serverName = null, billRequested = null } = {}) {
  const { data, error } = await supabase.rpc('tab_update', { p_tab: tabId, p_guests: guests, p_server: serverName, p_bill_requested: billRequested });
  if (error) throw error;
  return _storeTab(data);
};

export const moveTab = async function (tabId, tableId) {
  const { data, error } = await supabase.rpc('tab_move', { p_tab: tabId, p_table: tableId });
  if (error) throw error;
  return _storeTab(data);
};

export const mergeTabs = async function (fromTabId, intoTabId) {
  const { data, error } = await supabase.rpc('tab_merge', { p_from: fromTabId, p_into: intoTabId });
  if (error) throw error;
  state.openTabs = state.openTabs.filter((t) => t.id !== fromTabId);
  return _storeTab(data);
};

export const closeTab = async function (tabId) {
  const { error } = await supabase.rpc('tab_close', { p_tab: tabId });
  if (error) throw error;
  await loadTables();
};

export const setTableClean = async function (tableId, needsCleaning) {
  const { error } = await supabase.rpc('set_table_clean', { p_table: tableId, p_needs_cleaning: needsCleaning });
  if (error) throw error;
  const t = state.diningTables.find((x) => x.id === tableId);
  if (t) t.needsCleaning = needsCleaning;
};

// Reservations for [from, to) (ISO strings), any status.
const mapReservation = (r) => ({
  id: r.id, name: r.name, phone: r.phone, partySize: r.party_size, startsAt: r.starts_at,
  durationMin: r.duration_min, tableId: r.table_id, note: r.note, status: r.status, tabId: r.tab_id,
});

export const loadReservations = async function (fromISO, toISO) {
  const { data, error } = await supabase.from('table_reservations')
    .select('id, name, phone, party_size, starts_at, duration_min, table_id, note, status, tab_id')
    .eq('business_id', state.businessId)
    .gte('starts_at', fromISO).lt('starts_at', toISO)
    .order('starts_at', { ascending: true });
  if (error) throw error;
  state.reservations = data.map(mapReservation);
  return state.reservations;
};

export const saveReservation = async function (r) {
  const row = {
    business_id: state.businessId,
    name: String(r.name).trim().slice(0, 60),
    phone: r.phone ? String(r.phone).trim().slice(0, 30) : null,
    party_size: r.partySize,
    starts_at: r.startsAt,
    duration_min: r.durationMin ?? 90,
    table_id: r.tableId ?? null,
    note: r.note ? String(r.note).trim().slice(0, 200) : null,
  };
  const q = r.id
    ? supabase.from('table_reservations').update(row).eq('id', r.id).eq('business_id', state.businessId)
    : supabase.from('table_reservations').insert(row);
  const { error } = await q;
  if (error) throw error;
};

export const setReservationStatus = async function (id, status, tabId = null) {
  const patch = { status, ...(tabId ? { tab_id: tabId } : {}) };
  const { error } = await supabase.from('table_reservations').update(patch).eq('id', id).eq('business_id', state.businessId);
  if (error) throw error;
};

// Waiting parties, plus anyone seated or removed in the last 12 hours (so a
// mistaken Seat / Remove can still be seen).
const mapWaitlist = (r) => ({
  id: r.id, name: r.name, phone: r.phone, partySize: r.party_size, note: r.note,
  quotedMin: r.quoted_min, status: r.status, tableId: r.table_id, addedAt: r.added_at, seatedAt: r.seated_at,
});

export const loadWaitlist = async function () {
  const since = new Date(Date.now() - 12 * 3600 * 1000).toISOString();
  const { data, error } = await supabase.from('waitlist_entries')
    .select('id, name, phone, party_size, note, quoted_min, status, table_id, added_at, seated_at')
    .eq('business_id', state.businessId)
    .gte('added_at', since)
    .order('added_at', { ascending: true });
  if (error) throw error;
  state.waitlist = data.map(mapWaitlist);
  return state.waitlist;
};

export const addWaitlistEntry = async function (e) {
  const { error } = await supabase.from('waitlist_entries').insert({
    business_id: state.businessId,
    name: String(e.name).trim().slice(0, 60),
    phone: e.phone ? String(e.phone).trim().slice(0, 30) : null,
    party_size: e.partySize,
    note: e.note ? String(e.note).trim().slice(0, 200) : null,
    quoted_min: e.quotedMin ?? null,
  });
  if (error) throw error;
};

export const updateWaitlistEntry = async function (id, { status, tableId } = {}) {
  const patch = {};
  if (status) patch.status = status;
  if (status === 'seated') patch.seated_at = new Date().toISOString();
  if (tableId !== undefined) patch.table_id = tableId;
  const { error } = await supabase.from('waitlist_entries').update(patch).eq('id', id).eq('business_id', state.businessId);
  if (error) throw error;
};

export const loadScheduleStations = async function () {
  const query = () => supabase
    .from('schedule_stations')
    .select('id, name, color, sort_order, archived_at')
    .eq('business_id', state.businessId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  let { data, error } = await query();
  if (error) throw error;
  if (!data.length && hasPermission('manage_schedule')) {
    const rows = DEFAULT_STATIONS.map((s, i) => ({ business_id: state.businessId, name: s.name, color: s.color, sort_order: i }));
    const { error: seedErr } = await supabase.from('schedule_stations').insert(rows);
    if (seedErr) throw seedErr;
    ({ data, error } = await query());
    if (error) throw error;
  }
  state.scheduleStations = data.map(toStation);
  return state.scheduleStations;
};

export const addScheduleStation = async function ({ name, color }) {
  const sortOrder = state.scheduleStations.reduce((m, s) => Math.max(m, s.sortOrder), -1) + 1;
  const { data, error } = await supabase
    .from('schedule_stations')
    .insert({ business_id: state.businessId, name: name.trim(), color, sort_order: sortOrder })
    .select('id, name, color, sort_order, archived_at')
    .single();
  if (error) throw error;
  state.scheduleStations.push(toStation(data));
};

export const updateScheduleStation = async function (id, { name, color }) {
  const patch = {};
  if (name !== undefined) patch.name = name.trim();
  if (color !== undefined) patch.color = color;
  const { error } = await supabase.from('schedule_stations').update(patch).eq('id', id);
  if (error) throw error;
  const s = state.scheduleStations.find((x) => x.id === id);
  if (s) Object.assign(s, patch);
};

// Archived, not deleted: existing shifts keep showing the station's name.
export const archiveScheduleStation = async function (id) {
  const { error } = await supabase.from('schedule_stations').update({ archived_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
  const s = state.scheduleStations.find((x) => x.id === id);
  if (s) s.archived = true;
};

// Every shift starting in [fromISO, toISO).
// Home "Today's schedule" card: published shifts in a range plus the station
// list. Leaves state.scheduledShifts alone (the Schedule panel's week).
export const fetchPublishedShiftsWithStations = async function (fromISO, toISO) {
  const [shifts, stations] = await Promise.all([
    // Names and "uses the Time Clock" come with the shift, so the card never
    // depends on state.staff having loaded (it said "Teammate" before).
    supabase.from('scheduled_shifts')
      .select(`${SHIFT_COLS}, staff(first_name, last_name, roles(permissions))`)
      .eq('business_id', state.businessId)
      .not('published_at', 'is', null)
      .lt('starts_at', toISO)
      .gt('ends_at', fromISO)
      .order('starts_at', { ascending: true }),
    supabase.from('schedule_stations')
      .select('id, name, color, sort_order, archived_at')
      .eq('business_id', state.businessId),
  ]);
  if (shifts.error) throw shifts.error;
  return {
    shifts: shifts.data.map((r) => ({
      ...toScheduledShift(r),
      staffName: r.staff ? `${r.staff.first_name ?? ''} ${r.staff.last_name ?? ''}`.trim() : null,
      usesClock: r.staff?.roles?.permissions?.use_timeclock === true,
    })),
    stations: (stations.data ?? []).map(toStation),
  };
};

export const fetchScheduledShifts = async function (fromISO, toISO) {
  const { data, error } = await supabase
    .from('scheduled_shifts')
    .select(SHIFT_COLS)
    .eq('business_id', state.businessId)
    .gte('starts_at', fromISO)
    .lt('starts_at', toISO)
    .order('starts_at', { ascending: true });
  if (error) throw error;
  state.scheduledShifts = data.map(toScheduledShift);
  return state.scheduledShifts;
};

// Insert (no id) or update. Editing an already-published shift keeps it
// published, so the change reaches staff right away.
// isException: editing ONE shift of a repeat ("this shift") - later edits to the
// whole repeat leave it alone unless the owner asks.
export const saveScheduledShift = async function ({ id, staffId, stationId, startsAt, endsAt, note, isException }) {
  const row = {
    staff_id: staffId || null,
    station_id: stationId || null,
    starts_at: startsAt,
    ends_at: endsAt,
    note: note?.trim() ? note.trim().slice(0, 200) : null,
  };
  if (isException) row.is_exception = true;
  const q = id
    ? supabase.from('scheduled_shifts').update(row).eq('id', id)
    : supabase.from('scheduled_shifts').insert({ ...row, business_id: state.businessId });
  const { data, error } = await q.select(SHIFT_COLS).single();
  if (error) throw error;
  const saved = toScheduledShift(data);
  const i = state.scheduledShifts.findIndex((s) => s.id === saved.id);
  if (i === -1) state.scheduledShifts.push(saved); else state.scheduledShifts[i] = saved;
  return saved;
};

export const deleteScheduledShift = async function (id) {
  const { error } = await supabase.from('scheduled_shifts').delete().eq('id', id);
  if (error) throw error;
  state.scheduledShifts = state.scheduledShifts.filter((s) => s.id !== id);
};

// Inserts new DRAFT shifts in one call ("Copy last week"). The caller works out
// each copy's times from the business-timezone wall clock, so a week that
// crosses a daylight-saving change keeps 6:30a as 6:30a.
export const insertScheduledShifts = async function (shifts) {
  if (!shifts.length) return 0;
  const rows = shifts.map((s) => ({
    business_id: state.businessId,
    staff_id: s.staffId || null,
    station_id: s.stationId || null,
    starts_at: s.startsAt,
    ends_at: s.endsAt,
    note: s.note || null,
  }));
  const { error } = await supabase.from('scheduled_shifts').insert(rows);
  if (error) throw error;
  return rows.length;
};

// Publishes every draft starting in the week of `mondayStr` (business timezone).
export const publishScheduleWeek = async function (mondayStr) {
  const { data, error } = await supabase.rpc('publish_schedule_week', {
    p_business: state.businessId, p_week_start: mondayStr, p_by_name: state.username ?? '',
  });
  if (error) throw error;
  return data ?? 0;
};

// The signed-in staff member's own PUBLISHED shifts from now on (schedulers
// can also read drafts through RLS, so the published filter is explicit).
export const fetchMyUpcomingShifts = async function (days = 14) {
  const staffId = state.currentStaff?.id;
  if (!staffId) return [];
  const now = new Date();
  const { data, error } = await supabase
    .from('scheduled_shifts')
    .select(`${SHIFT_COLS}, schedule_stations(name, color)`)
    .eq('business_id', state.businessId)
    .eq('staff_id', staffId)
    .not('published_at', 'is', null)
    .gt('ends_at', now.toISOString())
    .lt('starts_at', new Date(now.getTime() + days * 86400000).toISOString())
    .order('starts_at', { ascending: true });
  if (error) throw error;
  return data.map((r) => ({ ...toScheduledShift(r), stationName: r.schedule_stations?.name ?? '', stationColor: r.schedule_stations?.color ?? null }));
};

// ── Repeating shifts (2026-10-15) ─────────────────────────────────────────────
// A repeat rule lives in schedule_series; its shifts are real scheduled_shifts
// rows generated a few weeks ahead by extend_schedule_series (and topped up as
// weeks are viewed). Migration: migration-2026-10-15-schedule-repeats.sql.

const toSeries = (r) => ({
  id: r.id, staffId: r.staff_id, stationId: r.station_id, weekdays: r.weekdays ?? [],
  startTime: (r.start_time ?? '').slice(0, 5), endTime: (r.end_time ?? '').slice(0, 5),
  startDate: r.start_date, untilDate: r.until_date, note: r.note ?? '', autoPublish: !!r.auto_publish,
});

export const loadScheduleSeries = async function () {
  const { data, error } = await supabase
    .from('schedule_series')
    .select('id, staff_id, station_id, weekdays, start_time, end_time, start_date, until_date, note, auto_publish')
    .eq('business_id', state.businessId);
  if (error) throw error;
  state.scheduleSeries = data.map(toSeries);
  return state.scheduleSeries;
};

// def: { staffId, stationId, weekdays (0 = Mon), startTime 'HH:MM', endTime, startDate, untilDate|null, note }
export const createScheduleSeries = async function (def) {
  const { data, error } = await supabase
    .from('schedule_series')
    .insert({
      business_id: state.businessId,
      staff_id: def.staffId || null,
      station_id: def.stationId || null,
      weekdays: def.weekdays,
      start_time: def.startTime,
      end_time: def.endTime,
      start_date: def.startDate,
      until_date: def.untilDate || null,
      note: def.note?.trim() ? def.note.trim().slice(0, 200) : null,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
};

// Fills in every repeat's shifts up to `throughDate` ('YYYY-MM-DD', business tz).
export const extendScheduleSeries = async function (throughDate) {
  const { data, error } = await supabase.rpc('extend_schedule_series', { p_business: state.businessId, p_through: throughDate });
  if (error) throw error;
  return data ?? 0;
};

const seriesDefJson = (def) => ({
  staff_id: def.staffId || '',
  station_id: def.stationId || '',
  weekdays: def.weekdays,
  start_time: def.startTime,
  end_time: def.endTime,
  until_date: def.untilDate || '',
  note: def.note?.trim() ? def.note.trim().slice(0, 200) : '',
});

// scope: 'following' | 'all'. Returns the id of the repeat now covering fromDate.
export const editScheduleSeries = async function (seriesId, fromDate, scope, def, includeEdited) {
  const { data, error } = await supabase.rpc('edit_schedule_series', {
    p_series: seriesId, p_from: fromDate, p_scope: scope, p_new: seriesDefJson(def), p_include_edited: !!includeEdited,
  });
  if (error) throw error;
  return data;
};

// scope: 'this' | 'following' | 'all'
export const deleteScheduleSeries = async function (seriesId, date, scope, includeEdited) {
  const { data, error } = await supabase.rpc('delete_schedule_series', {
    p_series: seriesId, p_date: date, p_scope: scope, p_include_edited: !!includeEdited,
  });
  if (error) throw error;
  return data ?? 0;
};

// "Keep future weeks live": the repeats publish their own future shifts from now
// on, and the drafts already generated from `fromISO` on are published too.
export const setSeriesAutoPublish = async function (seriesIds, fromISO) {
  if (!seriesIds.length) return;
  const { error } = await supabase.from('schedule_series').update({ auto_publish: true }).in('id', seriesIds);
  if (error) throw error;
  const { error: pubErr } = await supabase
    .from('scheduled_shifts')
    .update({ published_at: new Date().toISOString() })
    .in('series_id', seriesIds)
    .is('published_at', null)
    .gte('starts_at', fromISO);
  if (pubErr) throw pubErr;
  state.scheduleSeries.forEach((x) => { if (seriesIds.includes(x.id)) x.autoPublish = true; });
};

// ── Availability + leave on the grid (phase 2, 2026-10-17) ────────────────────
// staff_availability: one row per person per weekday they're NOT simply free
// all day ('unavailable', or 'partial' = only between startTime and endTime).
// Writes go through set_staff_availability (staff: their own; schedulers:
// anyone). Leave comes from the schedule_leave RPC, so a scheduler without
// manage_pay still sees who's off. Migration: migration-2026-10-17-schedule-phase2-3.sql.

const toAvailability = (r) => ({
  staffId: r.staff_id, weekday: r.weekday, status: r.status,
  startTime: (r.start_time ?? '').slice(0, 5), endTime: (r.end_time ?? '').slice(0, 5), note: r.note ?? '',
});

export const loadStaffAvailability = async function () {
  const { data, error } = await supabase
    .from('staff_availability')
    .select('staff_id, weekday, status, start_time, end_time, note')
    .eq('business_id', state.businessId);
  if (error) throw error;
  state.staffAvailability = data.map(toAvailability);
  return state.staffAvailability;
};

// days: [{ weekday, status, startTime, endTime, note }] - replaces the whole week.
export const saveStaffAvailability = async function (staffId, days) {
  const { error } = await supabase.rpc('set_staff_availability', {
    p_staff: staffId,
    p_days: days.map((d) => ({ weekday: d.weekday, status: d.status, start_time: d.startTime || null, end_time: d.endTime || null, note: d.note || '' })),
  });
  if (error) throw error;
  state.staffAvailability = state.staffAvailability
    .filter((a) => a.staffId !== staffId)
    .concat(days.filter((d) => d.status !== 'available' || d.note).map((d) => ({ staffId, ...d })));
};

// Approved + pending leave touching [fromDate, toDate] ('YYYY-MM-DD').
export const fetchScheduleLeave = async function (fromDate, toDate) {
  const { data, error } = await supabase.rpc('schedule_leave', { p_business: state.businessId, p_from: fromDate, p_to: toDate });
  if (error) throw error;
  state.scheduleLeave = (data ?? []).map((r) => ({ id: r.id, staffId: r.staff_id, type: r.type, startDate: r.start_date, endDate: r.end_date, status: r.status }));
  return state.scheduleLeave;
};

// ── Shift swaps + open-shift pickup (phase 3, 2026-10-17) ─────────────────────
// shift_requests is read-only to clients; staff file requests from the Time
// Clock, schedulers decide here through review_shift_request.

const REQ_SHIFT_COLS = 'id, staff_id, starts_at, ends_at, station_id, published_at';
const toShiftRequest = (r) => ({
  id: r.id, kind: r.kind, status: r.status, note: r.note ?? '',
  requesterId: r.requester_id, requesterName: r.requester_name ?? '',
  targetId: r.target_id, targetName: r.target_name ?? '',
  shiftId: r.shift_id, targetShiftId: r.target_shift_id,
  shift: r.shift ? { id: r.shift.id, staffId: r.shift.staff_id, startsAt: r.shift.starts_at, endsAt: r.shift.ends_at, stationId: r.shift.station_id } : null,
  targetShift: r.target_shift ? { id: r.target_shift.id, staffId: r.target_shift.staff_id, startsAt: r.target_shift.starts_at, endsAt: r.target_shift.ends_at, stationId: r.target_shift.station_id } : null,
  createdAt: r.created_at, respondedAt: r.responded_at,
  reviewedByName: r.reviewed_by_name ?? null, reviewedAt: r.reviewed_at ?? null, reviewNote: r.review_note ?? null,
});

// Open requests (waiting on a teammate or a manager) plus the last 30 closed ones.
export const fetchShiftRequests = async function () {
  const sel = `id, kind, status, note, requester_id, requester_name, target_id, target_name, shift_id, target_shift_id,
    created_at, responded_at, reviewed_by_name, reviewed_at, review_note,
    shift:shift_id(${REQ_SHIFT_COLS}), target_shift:target_shift_id(${REQ_SHIFT_COLS})`;
  const [open, closed] = await Promise.all([
    supabase.from('shift_requests').select(sel).eq('business_id', state.businessId)
      .in('status', ['waiting_teammate', 'pending']).order('created_at', { ascending: true }),
    supabase.from('shift_requests').select(sel).eq('business_id', state.businessId)
      .not('status', 'in', '(waiting_teammate,pending)').order('created_at', { ascending: false }).limit(30),
  ]);
  if (open.error) throw open.error;
  if (closed.error) throw closed.error;
  state.shiftRequests = [...open.data, ...closed.data].map(toShiftRequest);
  return state.shiftRequests;
};

// decision: 'approve' | 'reject'. Returns 'approved' | 'rejected' | 'stale'
// (the shift changed since the request, so it was closed instead).
export const reviewShiftRequest = async function (id, decision, note) {
  const { data, error } = await supabase.rpc('review_shift_request', {
    p_request: id, p_decision: decision, p_note: note ?? null, p_by_name: state.username ?? '',
  });
  if (error) throw error;
  return data;
};

// Swap requests waiting for the signed-in staff member's answer (home My shift card).
export const fetchMySwapAsksCount = async function () {
  const staffId = state.currentStaff?.id;
  if (!staffId) return 0;
  const { count, error } = await supabase
    .from('shift_requests')
    .select('id', { count: 'exact', head: true })
    .eq('target_id', staffId)
    .eq('status', 'waiting_teammate');
  if (error) throw error;
  return count ?? 0;
};
